import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Cell, ReferenceLine, ComposedChart, Area } from 'recharts';
import { createClient } from '@supabase/supabase-js';
import DashboardPanel from './components/DashboardPanel';
import AccountsManager from './components/AccountsManager';
import TradeReplayPanel from './components/TradeReplayPanel';
import TradeTable from './components/TradeTable';
import LiveTradingPanel from './components/LiveTradingPanel';
import AnalyticsPanel from './components/AnalyticsPanel';
import WatchlistPanel from './components/WatchlistPanel';
import FuturesPanel from './components/FuturesPanel';
import MAlertsWindow from './components/MAlertsWindow';
import DailyBriefing from './components/DailyBriefing';

function TradournalLogo({ size = 36, themeMode = 'night' }) {
  const s = size;
  const id = `tlg${s}`;
  const isDay = themeMode === 'day';
  return (
    <svg width={s} height={s} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={`${id}bg`} x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
          {isDay ? <>
            <stop offset="0%" stopColor="#fff8ee"/>
            <stop offset="100%" stopColor="#f5ead6"/>
          </> : <>
            <stop offset="0%" stopColor="#0a1628"/>
            <stop offset="100%" stopColor="#050d1a"/>
          </>}
        </linearGradient>
        <radialGradient id={`${id}glow`} cx="50%" cy="50%" r="55%" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#1a4a20" stopOpacity={isDay ? "0.15" : "0.4"}/>
          <stop offset="100%" stopColor={isDay ? "#f5ead6" : "#0a1628"} stopOpacity="0"/>
        </radialGradient>
        <linearGradient id={`${id}g`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#26e87a"/>
          <stop offset="100%" stopColor="#0db857"/>
        </linearGradient>
        <linearGradient id={`${id}r`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ff5c5c"/>
          <stop offset="100%" stopColor="#d42020"/>
        </linearGradient>
        {/* Metallic gold gradient for arrow */}
        <linearGradient id={`${id}ag`} x1="3" y1="46" x2="43" y2="5" gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor="#C47D0A"/>
          <stop offset="30%"  stopColor="#E8A800"/>
          <stop offset="55%"  stopColor="#FFD966"/>
          <stop offset="80%"  stopColor="#D4920A"/>
          <stop offset="100%" stopColor="#C07A00"/>
        </linearGradient>
        {/* Border highlight gradient */}
        <linearGradient id={`${id}border`} x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor="#26e87a" stopOpacity="0.9"/>
          <stop offset="50%"  stopColor="#ffffff" stopOpacity="0.3"/>
          <stop offset="100%" stopColor="#ff5c5c" stopOpacity="0.9"/>
        </linearGradient>
        <filter id={`${id}bglow`} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="1.5" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>

      {/* Background */}
      <rect width="48" height="48" rx="12" fill={`url(#${id}bg)`}/>
      <rect width="48" height="48" rx="12" fill={`url(#${id}glow)`}/>

      {/* ── Glowing boundary highlight ── */}
      {/* Outer glow layer */}
      <rect x="-1" y="-1" width="50" height="50" rx="13"
        fill="none" stroke={`url(#${id}border)`} strokeWidth="3" opacity="0.4" filter={`url(#${id}bglow)`}/>
      {/* Sharp inner border */}
      <rect x="0.5" y="0.5" width="47" height="47" rx="11.5"
        fill="none" stroke={`url(#${id}border)`} strokeWidth="1.2"/>

      {/* ── 5 Big Candlesticks ── */}

      {/* Candle 1 — Green */}
      <line x1="7" y1="26" x2="7" y2="46" stroke="#26e87a" strokeWidth="1.5" strokeLinecap="round"/>
      <rect x="3" y="29" width="8" height="13" rx="1.5" fill={`url(#${id}g)`}/>

      {/* Candle 2 — Red */}
      <line x1="16" y1="20" x2="16" y2="42" stroke="#ff5c5c" strokeWidth="1.5" strokeLinecap="round"/>
      <rect x="12" y="23" width="8" height="13" rx="1.5" fill={`url(#${id}r)`}/>

      {/* Candle 3 — Green */}
      <line x1="25" y1="14" x2="25" y2="38" stroke="#26e87a" strokeWidth="1.5" strokeLinecap="round"/>
      <rect x="21" y="17" width="8" height="15" rx="1.5" fill={`url(#${id}g)`}/>

      {/* Candle 4 — Red */}
      <line x1="34" y1="18" x2="34" y2="40" stroke="#ff5c5c" strokeWidth="1.5" strokeLinecap="round"/>
      <rect x="30" y="21" width="8" height="13" rx="1.5" fill={`url(#${id}r)`}/>

      {/* Candle 5 — Green (tallest) */}
      <line x1="43" y1="6" x2="43" y2="30" stroke="#26e87a" strokeWidth="1.5" strokeLinecap="round"/>
      <rect x="39" y="9" width="8" height="17" rx="1.5" fill={`url(#${id}g)`}/>

      {/* ── Rising arrow: shadow layer ── */}
      <path d="M 3,46 C 22,46 38,18 43,5"
        stroke="#000" strokeWidth="5.5" strokeLinecap="round" fill="none" opacity="0.45"/>
      <polygon points="43,5 44,13 37,11"
        fill="#000" opacity="0.45"/>
      {/* Metallic gold arrow — flat start, steep rise (like reference) */}
      <path d="M 3,46 C 22,46 38,18 43,5"
        stroke={`url(#${id}ag)`} strokeWidth="3.5" strokeLinecap="round" fill="none"/>
      {/* Solid filled arrowhead */}
      <polygon points="43,5 44,13 37,11"
        fill={`url(#${id}ag)`}/>
    </svg>
  );
}

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
const LIVE_VIEW_TOKEN = process.env.REACT_APP_LIVE_VIEW_TOKEN || '';
const SUPABASE_ENV_MISSING = !SUPABASE_URL || !SUPABASE_ANON_KEY;
const supabase = createClient(
  SUPABASE_URL || 'https://invalid.supabase.local',
  SUPABASE_ANON_KEY || 'invalid-anon-key',
  { auth: { detectSessionInUrl: false } }
);

const C = {
  bg: '#050912', sidebar: '#08101d', card: '#0f1725', card2: '#131d2d',
  border: '#22314a', green: '#0ecb81', greenBright: '#39e5a5', red: '#ef4444',
  redBright: '#f87171', blue: '#2f8cff', blueBright: '#7cb6ff', purple: '#8b7bff',
  orange: '#f59e0b', text: '#e8eefb', sub: '#95a5c0', input: '#0b1424',
  font: '"Manrope","Sora","Segoe UI",sans-serif',
};
const cardStyle = {
  background: 'linear-gradient(160deg, rgba(20,30,48,0.88), rgba(14,22,36,0.9))',
  border: '1px solid #22314a',
  borderRadius: '14px',
  boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
};
const EMOTIONS = ['😊 Confident','😰 Anxious','😤 Frustrated','😌 Calm','🤑 Greedy','😨 Fearful','🧠 Focused','😴 Tired'];
const RULES = ['Followed my trading plan','Proper position sizing','Set stop loss before entry','Confirmed trend direction','Waited for my setup','No revenge trading','Checked risk/reward ratio','No overtrading'];
const GAP_SCANNER_LARGE_CAPS = ['AAPL','MSFT','NVDA','AMZN','GOOGL','META','TSLA','LLY','V','UNH','JPM','XOM','MA','AVGO','PG','HD','COST','MRK','ABBV','CVX','NFLX','CRM','BAC','AMD','KO','PEP','WMT','TMO','MCD','CSCO','ORCL','ACN','ABT','DIS','ADBE','WFC','VZ','IBM','QCOM','INTU','CAT','GS','MS','AMGN','GE','TXN','HON','SPGI','LOW','RTX','UNP','BMY','AXP','ISRG','MDT','DE','SCHW','LMT','ADP','BKNG','GILD','SYK','BLK','VRTX','REGN','C','ZTS','CI','ETN','MO','BSX','TJX','CME','SHW','MPC','GD','PLD','ITW','F','GM','UBER','COIN','PLTR','SNOW','CRWD','NET','DDOG','MELI','SHOP','SPOT','ABNB','DASH','SOFI','RIVN','ROKU','PINS','RBLX','U','ZM','LYFT'];
const inp = { padding:'10px 13px',borderRadius:'10px',border:'1px solid #22314a',background:'#0b1424',color:'#e8eefb',fontSize:'13px',outline:'none',width:'100%',boxSizing:'border-box' };

const THEMES = {
  night: {
    colors: {
      bg: '#050912', sidebar: '#08101d', card: '#0f1725', card2: '#131d2d',
      border: '#22314a', green: '#0ecb81', greenBright: '#39e5a5', red: '#ef4444',
      redBright: '#f87171', blue: '#2f8cff', blueBright: '#7cb6ff', purple: '#8b7bff',
      orange: '#f59e0b', text: '#e8eefb', sub: '#95a5c0', input: '#0b1424',
      font: '"Manrope","Sora","Segoe UI",sans-serif',
    },
    cardBackground: 'linear-gradient(160deg, rgba(20,30,48,0.88), rgba(14,22,36,0.9))',
    shadow: '0 10px 30px rgba(0,0,0,0.35)',
  },
  day: {
    colors: {
      bg: '#f3f7fc', sidebar: '#eaf0f8', card: '#ffffff', card2: '#f5f8fd',
      border: '#cfd9e8', green: '#0f9f61', greenBright: '#0f9f61', red: '#d94a47',
      redBright: '#d94a47', blue: '#2563eb', blueBright: '#2563eb', purple: '#6d5dd3',
      orange: '#d97706', text: '#10233f', sub: '#5f7492', input: '#ffffff',
      font: '"Manrope","Sora","Segoe UI",sans-serif',
    },
    cardBackground: 'linear-gradient(160deg, rgba(255,255,255,0.98), rgba(247,250,255,0.98))',
    shadow: '0 10px 24px rgba(44,72,112,0.10)',
  },
};

const ACCENT_PRESETS = [
  { name: 'Ocean Blue',  value: '#2f8cff' },
  { name: 'Indigo',      value: '#6366f1' },
  { name: 'Violet',      value: '#8b5cf6' },
  { name: 'Teal',        value: '#14b8a6' },
  { name: 'Emerald',     value: '#10b981' },
  { name: 'Amber',       value: '#f59e0b' },
  { name: 'Rose',        value: '#f43f5e' },
  { name: 'Pink',        value: '#ec4899' },
];

function hexToRgb(hex) {
  const n = parseInt(hex.replace('#',''), 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}
function rgbToHex(r, g, b) {
  return `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;
}
function tintColor(base, accent, amount) {
  const [br, bg2, bb] = hexToRgb(base);
  const [ar, ag, ab] = hexToRgb(accent);
  return rgbToHex(
    Math.round(br * (1 - amount) + ar * amount),
    Math.round(bg2 * (1 - amount) + ag * amount),
    Math.round(bb * (1 - amount) + ab * amount)
  );
}
function lightenHex(hex, amount = 0.25) {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex(
    Math.min(255, r + Math.round((255 - r) * amount)),
    Math.min(255, g + Math.round((255 - g) * amount)),
    Math.min(255, b + Math.round((255 - b) * amount))
  );
}

function applyTheme(mode, accentColor) {
  const theme = THEMES[mode] || THEMES.night;
  Object.assign(C, theme.colors);
  const isDark = mode !== 'day';
  if (accentColor) {
    // Accent color drives buttons/highlights
    C.blue = accentColor;
    C.blueBright = lightenHex(accentColor, 0.28);
    // Tint backgrounds subtly toward the accent color
    const tintAmt = isDark ? 0.06 : 0.04;
    C.bg      = tintColor(theme.colors.bg,      accentColor, tintAmt * 0.7);
    C.sidebar = tintColor(theme.colors.sidebar,  accentColor, tintAmt * 0.8);
    C.card    = tintColor(theme.colors.card,     accentColor, tintAmt * 0.5);
    C.card2   = tintColor(theme.colors.card2,    accentColor, tintAmt * 0.6);
    C.border  = tintColor(theme.colors.border,   accentColor, tintAmt * 1.2);
    C.input   = tintColor(theme.colors.input,    accentColor, tintAmt * 0.4);
  }
  cardStyle.background = theme.cardBackground;
  cardStyle.border = `1px solid ${C.border}`;
  cardStyle.boxShadow = theme.shadow;
  inp.border = `1px solid ${C.border}`;
  inp.background = C.input;
  inp.color = C.text;
}

function parseTradeDate(raw) {
  const value = raw ? `${raw}`.trim() : '';
  if (!value) return new Date(NaN);

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map((part) => Number.parseInt(part, 10));
    return new Date(year, month - 1, day);
  }

  const parseBySeparator = (sep) => {
    const parts = value.split(sep).map((p) => p.trim());
    if (parts.length !== 3) return null;
    const [aRaw, bRaw, cRaw] = parts;
    const a = Number.parseInt(aRaw, 10);
    const b = Number.parseInt(bRaw, 10);
    const c = Number.parseInt(cRaw, 10);
    if (!Number.isFinite(a) || !Number.isFinite(b) || !Number.isFinite(c)) return null;

    if (aRaw.length === 4) return new Date(a, b - 1, c); // YYYY/MM/DD or YYYY-MM-DD
    if (cRaw.length === 4) {
      if (a > 12 && b <= 12) return new Date(c, b - 1, a); // DD/MM/YYYY
      return new Date(c, a - 1, b); // MM/DD/YYYY
    }
    return null;
  };

  if (value.includes('/')) {
    const d = parseBySeparator('/');
    if (d) return d;
  }
  if (value.includes('-')) {
    const d = parseBySeparator('-');
    if (d) return d;
  }
  return new Date(value);
}

function formatTradeDate(date = new Date()) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isTransientNetworkError(err) {
  const message = `${err?.message || err || ''}`.toLowerCase();
  return (
    err instanceof TypeError
    || message.includes('load failed')
    || message.includes('failed to fetch')
    || message.includes('network')
    || message.includes('fetch')
  );
}

async function fetchSupabaseRest(path, { method = 'GET', token = '', body, headers = {} } = {}) {
  const parsePayload = async (res) => {
    const text = await res.text();
    let payload = null;
    try { payload = text ? JSON.parse(text) : null; } catch { payload = text; }
    if (!res.ok) {
      const message = payload?.message || payload?.error_description || payload?.error || `HTTP ${res.status}`;
      throw new Error(message);
    }
    return payload;
  };

  if (typeof window !== 'undefined') {
    try {
      const proxyRes = await fetch('/api/supabase/rest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path, method, token, body, headers }),
        cache: 'no-store',
      });
      return await parsePayload(proxyRes);
    } catch (proxyErr) {
      if (!isTransientNetworkError(proxyErr)) throw proxyErr;
    }
  }

  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) throw new Error('Supabase config missing');
  const directRes = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    method,
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: token ? `Bearer ${token}` : `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
      Prefer: method === 'POST' ? 'return=representation' : undefined,
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
    cache: 'no-store',
    mode: 'cors',
  });
  return parsePayload(directRes);
}

async function signInWithPasswordFallback(email, password) {
  let payload = null;
  let res = null;
  if (typeof window !== 'undefined') {
    try {
      res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      payload = await res.json().catch(() => ({}));
    } catch (proxyErr) {
      if (!isTransientNetworkError(proxyErr)) throw proxyErr;
    }
  }

  if (!res) {
    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) throw new Error('Supabase config missing');
    res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
      method: 'POST',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });
    payload = await res.json().catch(() => ({}));
  }

  if (!res.ok) {
    const message = payload?.msg || payload?.message || payload?.error_description || payload?.error || `HTTP ${res.status}`;
    throw new Error(message);
  }
  if (!payload?.access_token || !payload?.refresh_token) {
    throw new Error('Login succeeded but no session was returned.');
  }
  const { data, error } = await supabase.auth.setSession({
    access_token: payload.access_token,
    refresh_token: payload.refresh_token,
  });
  if (error) throw error;
  return data;
}

async function runAuthActionFallback(action, email, redirectTo) {
  let res = null;
  let payload = null;

  if (typeof window !== 'undefined') {
    try {
      res = await fetch('/api/auth/action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, email, redirectTo }),
      });
      payload = await res.json().catch(() => ({}));
    } catch (proxyErr) {
      if (!isTransientNetworkError(proxyErr)) throw proxyErr;
    }
  }

  if (!res) {
    const endpoint = action === 'reset'
      ? `${SUPABASE_URL}/auth/v1/recover`
      : action === 'magic-link'
        ? `${SUPABASE_URL}/auth/v1/otp`
        : `${SUPABASE_URL}/auth/v1/resend`;
    const body = action === 'reset'
      ? (redirectTo ? { email, redirect_to: redirectTo } : { email })
      : action === 'magic-link'
        ? (redirectTo ? { email, create_user: false, email_redirect_to: redirectTo } : { email, create_user: false })
      : (redirectTo ? { type: 'signup', email, options: { emailRedirectTo: redirectTo } } : { type: 'signup', email });

    res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    payload = await res.json().catch(() => ({}));
  }

  if (!res.ok) {
    const message = payload?.msg || payload?.message || payload?.error_description || payload?.error || `HTTP ${res.status}`;
    throw new Error(message);
  }
  return payload;
}

function toNumber(raw, fallback = 0) {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : fallback;
  if (raw === null || raw === undefined) return fallback;
  const cleaned = `${raw}`.replace(/[$,%\s]/g, '').replace(/,/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : fallback;
}

function resolveTvSymbol(raw, market) {
  const s = (raw || '').toUpperCase().trim();
  const normalizeStrikeToOcc = (strikeRaw) => {
    const strike = `${strikeRaw}`.trim();
    if (!strike) return null;
    if (/^\d{8}$/.test(strike)) return strike;
    const parsed = parseFloat(strike);
    if (Number.isNaN(parsed) || parsed <= 0) return null;
    return `${Math.round(parsed * 1000)}`.padStart(8, '0');
  };
  if (!s) return 'NASDAQ:AAPL';
  if (s.includes(':')) return s;
  const compact = s.replace(/\s+/g, '');
  const occ = compact.match(/^([A-Z]{1,6})(\d{6})([CP])(\d{8})$/);
  if (occ) return `OPRA:${compact}`;
  const compactOption = compact.match(/^([A-Z]{1,6})(\d{6})([CP])(\d+(?:\.\d+)?)$/);
  if (compactOption) {
    const [, under, yymmdd, cp, strikeRaw] = compactOption;
    const strikeOcc = normalizeStrikeToOcc(strikeRaw);
    if (strikeOcc) return `OPRA:${under}${yymmdd}${cp}${strikeOcc}`;
  }
  const splitOption = s.match(/^([A-Z]{1,6})[\s/_-]+(\d{6})[\s/_-]*([CP])[\s/_-]*(\d+(?:\.\d+)?)$/);
  if (splitOption) {
    const [, under, yymmdd, cp, strikeRaw] = splitOption;
    const strikeOcc = normalizeStrikeToOcc(strikeRaw);
    if (strikeOcc) return `OPRA:${under}${yymmdd}${cp}${strikeOcc}`;
  }
  if ((market || '').toLowerCase() === 'options') {
    const under = s.split(/[\s\-_/]/)[0];
    if (under) return `NASDAQ:${under}`;
  }
  if (s === 'BTC' || s === 'BTCUSD') return 'BINANCE:BTCUSDT';
  if (s === 'ETH' || s === 'ETHUSD') return 'BINANCE:ETHUSDT';
  return `NASDAQ:${s}`;
}

function inferTradeMeta(symbolRaw, strategyRaw, notesRaw) {
  const symbol = (symbolRaw || '').toUpperCase().trim();
  const notes = (notesRaw || '').toLowerCase();
  const strategy = strategyRaw || (
    notes.includes('break') ? 'Breakout'
      : notes.includes('scalp') ? 'Scalp'
      : notes.includes('swing') ? 'Swing'
      : notes.includes('reversal') ? 'Reversal'
      : notes.includes('trend') ? 'Trend Following'
      : ''
  );
  const session = notes.includes('premarket') ? 'Premarket'
    : notes.includes('open') ? 'Open'
    : notes.includes('close') ? 'Close'
    : 'Regular';
  const market = /^([A-Z]{1,6})(\d{6})([CP])(\d{8})$/.test(symbol.replace(/\s+/g, '')) ? 'Options'
    : (symbol === 'BTC' || symbol === 'ETH' || symbol.endsWith('USD')) ? 'Crypto'
    : 'Stocks';
  const tags = [
    market === 'Options' ? 'options' : market === 'Crypto' ? 'crypto' : 'stock',
    session.toLowerCase(),
    strategy ? strategy.toLowerCase() : null,
  ].filter(Boolean).join(', ');
  return { strategy, market, tags, session };
}

function calculateTradePnl(type, entryRaw, exitRaw, sizeRaw) {
  const entry = toNumber(entryRaw, NaN);
  const exit = toNumber(exitRaw, NaN);
  const size = toNumber(sizeRaw, NaN);
  if (Number.isNaN(entry) || Number.isNaN(exit) || Number.isNaN(size)) return 0;
  const move = type === 'Sell' ? (entry - exit) : (exit - entry);
  return move * size;
}

function ToastStack({ toasts, removeToast }) {
  return (
    <div style={{ position:'fixed', right:'16px', bottom:'16px', zIndex:9999, display:'flex', flexDirection:'column', gap:'8px' }}>
      {toasts.map((t) => (
        <div key={t.id} style={{ minWidth:'240px', maxWidth:'360px', background:C.card, border:`1px solid ${t.type === 'error' ? C.red : t.type === 'success' ? C.green : C.border}`, color:C.text, borderRadius:'10px', padding:'10px 12px', boxShadow:'0 8px 20px rgba(0,0,0,0.25)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', gap:'8px', alignItems:'start' }}>
            <div style={{ fontSize:'12px' }}>{t.message}</div>
            <button onClick={() => removeToast(t.id)} style={{ background:'none', border:'none', color:C.sub, cursor:'pointer' }}>✕</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function GlobalFilterBar({ filters, setFilters, accounts, savePreset, applyPreset, presets }) {
  return (
    <div style={{ ...cardStyle, padding:'10px 12px', marginBottom:'12px', display:'flex', gap:'8px', alignItems:'center', flexWrap:'wrap' }}>
      <input type="date" value={filters.from} onChange={(e)=>setFilters((f)=>({ ...f, from:e.target.value }))} style={{ ...inp, width:'145px', padding:'7px 8px', fontSize:'12px' }} />
      <input type="date" value={filters.to} onChange={(e)=>setFilters((f)=>({ ...f, to:e.target.value }))} style={{ ...inp, width:'145px', padding:'7px 8px', fontSize:'12px' }} />
      <input placeholder="Asset" value={filters.asset} onChange={(e)=>setFilters((f)=>({ ...f, asset:e.target.value }))} style={{ ...inp, width:'130px', padding:'7px 8px', fontSize:'12px' }} />
      <input placeholder="Strategy" value={filters.strategy} onChange={(e)=>setFilters((f)=>({ ...f, strategy:e.target.value }))} style={{ ...inp, width:'130px', padding:'7px 8px', fontSize:'12px' }} />
      <select value={filters.account} onChange={(e)=>setFilters((f)=>({ ...f, account:e.target.value }))} style={{ ...inp, width:'140px', padding:'7px 8px', fontSize:'12px' }}>
        <option value="Current">Current Account</option>
        <option value="All">All Accounts</option>
        {accounts.map((a)=><option key={a.id} value={a.name}>{a.name}</option>)}
      </select>
      <button onClick={savePreset} style={{ padding:'7px 10px', borderRadius:'6px', border:`1px solid ${C.border}`, background:C.card2, color:C.text, cursor:'pointer', fontSize:'11px', fontWeight:'700' }}>Save Preset</button>
      <select value="" onChange={(e)=>{ if (e.target.value) applyPreset(e.target.value); }} style={{ ...inp, width:'150px', padding:'7px 8px', fontSize:'12px' }}>
        <option value="">Load Preset</option>
        {presets.map((p)=><option key={p.name} value={p.name}>{p.name}</option>)}
      </select>
    </div>
  );
}

function TradeDetailDrawer({ trade, onClose, openReplay }) {
  if (!trade) return null;
  const pnl = toNumber(trade.pnl);
  const isWin = pnl > 0;
  const checks = Array.isArray(trade.rulesChecked)
    ? trade.rulesChecked
    : typeof trade.rulesChecked === 'string'
      ? trade.rulesChecked.split(',').map((x) => x.trim()).filter(Boolean)
      : [];
  const tags = `${trade.tags || ''}`.split(',').map(t => t.trim()).filter(Boolean);
  const rr = (trade.stopLoss && trade.target && trade.entry)
    ? (() => {
        const risk = Math.abs(toNumber(trade.entry) - toNumber(trade.stopLoss));
        const reward = Math.abs(toNumber(trade.target) - toNumber(trade.entry));
        return risk > 0 ? (reward / risk).toFixed(2) : '—';
      })()
    : '—';

  const stat = (label, value, color) => (
    <div style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ color: C.sub, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 5 }}>{label}</div>
      <div style={{ color: color || C.text, fontSize: 15, fontWeight: 800 }}>{value}</div>
    </div>
  );

  return (
    <div style={{ position:'fixed', inset:0, zIndex:9998, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
      onClick={onClose}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, width: '100%', maxWidth: 620, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <div style={{ color: C.text, fontSize: 22, fontWeight: 900, letterSpacing: '-0.5px' }}>{trade.asset}</div>
              <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                background: trade.type === 'Buy' ? 'rgba(14,203,129,0.15)' : 'rgba(239,68,68,0.15)',
                color: trade.type === 'Buy' ? C.greenBright : C.redBright,
                border: `1px solid ${trade.type === 'Buy' ? C.greenBright : C.redBright}44` }}>
                {trade.type}
              </span>
              {trade.strategy && (
                <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: `${C.blue}18`, color: C.blueBright, border: `1px solid ${C.blue}33` }}>
                  {trade.strategy}
                </span>
              )}
            </div>
            <div style={{ color: C.sub, fontSize: 12 }}>{trade.date} · {trade.account} · {trade.market || 'Stocks'}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ color: isWin ? C.greenBright : C.redBright, fontSize: 26, fontWeight: 900, letterSpacing: '-0.5px' }}>
                {pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}
              </div>
              <div style={{ color: C.sub, fontSize: 10, fontWeight: 600 }}>NET P&L</div>
            </div>
            <button onClick={onClose} style={{ background: C.card2, border: `1px solid ${C.border}`, color: C.sub, cursor: 'pointer', borderRadius: 8, width: 32, height: 32, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
          </div>
        </div>

        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Price Stats Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            {stat('Entry', `$${trade.entry}`)}
            {stat('Exit', `$${trade.exit}`)}
            {stat('Size', trade.size)}
            {stat('Stop Loss', trade.stopLoss ? `$${trade.stopLoss}` : '—')}
            {stat('Target', trade.target ? `$${trade.target}` : '—')}
            {stat('R:R', rr, C.orange)}
          </div>
          {trade.commission && toNumber(trade.commission) > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {stat('Commission', `$${toNumber(trade.commission).toFixed(2)}`, C.redBright)}
              {stat('Gross P&L', `${(pnl + toNumber(trade.commission)) >= 0 ? '+' : ''}$${(pnl + toNumber(trade.commission)).toFixed(2)}`)}
            </div>
          )}

          {/* Tags */}
          {tags.length > 0 && (
            <div>
              <div style={{ color: C.sub, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Tags</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {tags.map(tag => (
                  <span key={tag} style={{ padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${C.purple}18`, color: C.purple, border: `1px solid ${C.purple}33` }}>
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Emotion + Psychology */}
          {(trade.emotion || trade.psychScore) && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {trade.emotion && stat('Emotion', trade.emotion, C.orange)}
              {trade.psychScore && stat('Psychology Score', `${trade.psychScore}/10`, toNumber(trade.psychScore) >= 7 ? C.greenBright : toNumber(trade.psychScore) >= 4 ? C.orange : C.redBright)}
            </div>
          )}

          {/* Notes */}
          {trade.notes && (
            <div style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ color: C.sub, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Notes</div>
              <div style={{ color: C.text, fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{trade.notes}</div>
            </div>
          )}

          {/* Rules Checklist */}
          {checks.length > 0 && (
            <div style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ color: C.sub, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Rules Followed</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {checks.map(c => (
                  <div key={c} style={{ display: 'flex', alignItems: 'center', gap: 6, color: C.text, fontSize: 12 }}>
                    <span style={{ color: C.greenBright, fontSize: 14 }}>✓</span> {c}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Screenshot */}
          {trade.screenshot && (
            <div>
              <div style={{ color: C.sub, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Screenshot</div>
              <img src={trade.screenshot} alt="trade screenshot" style={{ width: '100%', borderRadius: 10, border: `1px solid ${C.border}` }} />
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
            <button onClick={() => openReplay(trade.id)} style={{ flex: 1, padding: '11px', border: 'none', borderRadius: 10, background: C.blue, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
              ▶ Open in Replay
            </button>
            <button onClick={onClose} style={{ padding: '11px 20px', border: `1px solid ${C.border}`, borderRadius: 10, background: 'transparent', color: C.sub, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function filterByPeriod(trades,period){const now=new Date();return trades.filter(t=>{const parts=t.date?t.date.split("/"):[];const d=parts.length===3?new Date(parts[2],parts[0]-1,parts[1]):new Date(t.date);if(isNaN(d))return period==="All Time";if(period==="Daily")return d.toDateString()===now.toDateString();if(period==="Weekly"){const w=new Date(now);w.setDate(now.getDate()-7);return d>=w;}if(period==="Monthly")return d.getMonth()===now.getMonth()&&d.getFullYear()===now.getFullYear();if(period==="Yearly")return d.getFullYear()===now.getFullYear();return true;});}
function PnLSummaryBar({ trades }) {
  const [period, setPeriod] = React.useState('All Time');
  const periods = ['Daily', 'Weekly', 'Monthly', 'Yearly', 'All Time'];
  const filtered = filterByPeriod(trades, period);
  const pnl = filtered.reduce((s, t) => s + toNumber(t.pnl), 0);
  const wins = filtered.filter((t) => toNumber(t.pnl) > 0);
  const losses = filtered.filter((t) => toNumber(t.pnl) < 0);
  const winRate = filtered.length > 0 ? ((wins.length / filtered.length) * 100).toFixed(1) : '0.0';
  const avgWin = wins.length > 0 ? (wins.reduce((s, t) => s + toNumber(t.pnl), 0) / wins.length).toFixed(2) : '0.00';
  const avgLoss = losses.length > 0 ? (losses.reduce((s, t) => s + toNumber(t.pnl), 0) / losses.length).toFixed(2) : '0.00';
  const muted = C.sub;
  return (
    <div style={{ ...cardStyle, padding:'16px 20px', marginBottom:'14px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px' }}>
        <div style={{ color:muted, fontSize:'11px', fontWeight:'700', textTransform:'uppercase' }}>P&L Summary</div>
        <div style={{ display:'flex', background:C.card2, borderRadius:'8px', padding:'3px', gap:'2px', border:`1px solid ${C.border}` }}>
          {periods.map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              style={{
                padding:'5px 12px',
                border:'none',
                borderRadius:'6px',
                cursor:'pointer',
                fontSize:'11px',
                fontWeight:'700',
                background: period === p ? C.blue : 'transparent',
                color: period === p ? '#fff' : muted,
              }}
            >
              {p}
            </button>
          ))}
        </div>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'10px' }}>
        {[
          ['Net P&L', `${pnl >= 0 ? '+' : ''}$${pnl.toFixed(2)}`, pnl >= 0 ? C.greenBright : C.redBright],
          ['Trades', `${filtered.length}`, C.text],
          ['Win Rate', `${winRate}%`, C.blueBright],
          ['Avg Win', `$${avgWin}`, C.greenBright],
          ['Avg Loss', `$${avgLoss}`, C.redBright],
        ].map(([label, value, color]) => (
          <div key={label} style={{ background:C.card2, borderRadius:'8px', padding:'12px', textAlign:'center', border:`1px solid ${C.border}` }}>
            <div style={{ color:muted, fontSize:'10px', marginBottom:'5px', fontWeight:'600', textTransform:'uppercase' }}>{label}</div>
            <div style={{ color, fontSize:'15px', fontWeight:'800' }}>{value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
function MobileAddTradeForm({ form, setForm, handleChange, toggleRule, addTrade, fileRef, handleScreenshot, calculateTradePnl, C, cardStyle, inp }) {
  const [step, setStep] = React.useState(0);
  const steps = ['Trade', 'Prices', 'Psychology'];
  const lbl = (t) => <div style={{color:C.sub,fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:'8px'}}>{t}</div>;
  const field = (name, label, type='text', ph='') => (
    <div key={name}>
      {lbl(label)}
      <input name={name} type={type} placeholder={ph} value={form[name]} onChange={handleChange}
        style={{...inp,fontSize:'16px',padding:'14px',marginBottom:'14px'}} inputMode={type==='number'?'decimal':undefined}/>
    </div>
  );
  const sel = (name, label, opts) => (
    <div key={name}>
      {lbl(label)}
      <select name={name} value={form[name]} onChange={handleChange} style={{...inp,fontSize:'16px',padding:'14px',marginBottom:'14px'}}>
        {opts.map(o => Array.isArray(o) ? <option key={o[0]} value={o[0]}>{o[1]}</option> : <option key={o}>{o}</option>)}
      </select>
    </div>
  );
  const estPnl = form.entry&&form.exit&&form.size ? calculateTradePnl(form.type,form.entry,form.exit,form.size) : null;

  return (
    <div style={{maxWidth:480,margin:'0 auto',width:'100%'}}>
      {/* Step indicator */}
      <div style={{display:'flex',gap:6,marginBottom:20}}>
        {steps.map((s,i) => (
          <div key={s} style={{flex:1,height:4,borderRadius:2,background:i<=step?C.blue:C.border,transition:'background 0.2s'}}/>
        ))}
      </div>
      <div style={{color:C.sub,fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'1px',marginBottom:16}}>
        Step {step+1} of {steps.length} — {steps[step]}
      </div>

      {step===0 && (
        <div style={{...cardStyle,padding:'20px'}}>
          {field('asset','Asset','text','AAPL, BTC, ES...')}
          {sel('market','Market',['Stocks','Forex','Crypto','Options','Futures'])}
          {sel('type','Direction',['Buy','Sell'])}
          {sel('strategy','Strategy',[['','— No strategy —'],'Breakout','Scalp','Swing','Trend Following','Reversal'])}
          <div>{lbl('Date')}
            <input name="date" type="date" value={form.date||new Date().toISOString().split('T')[0]} onChange={handleChange} style={{...inp,fontSize:'16px',padding:'14px',marginBottom:'14px'}}/>
          </div>
          {field('tags','Tags (optional)','text','momentum, earnings...')}
        </div>
      )}

      {step===1 && (
        <div style={{...cardStyle,padding:'20px'}}>
          {field('entry','Entry Price','number','0.00')}
          {field('exit','Exit Price','number','0.00')}
          {field('size','Position Size / Qty','number','0')}
          {field('stopLoss','Stop Loss (optional)','number','0.00')}
          {field('target','Target (optional)','number','0.00')}
          {field('commission','Commission ($)','number','0.00')}
          {estPnl !== null && (
            <div style={{padding:'16px',borderRadius:10,marginBottom:8,background:estPnl>=0?`${C.green}15`:`${C.red}15`,border:`1px solid ${estPnl>=0?C.green:C.red}33`,textAlign:'center'}}>
              <div style={{color:C.sub,fontSize:'10px',fontWeight:'700',textTransform:'uppercase',marginBottom:6}}>Estimated P&L</div>
              <div style={{color:estPnl>=0?C.greenBright:C.redBright,fontSize:'28px',fontWeight:'900'}}>{estPnl>=0?'+':''}${estPnl.toFixed(2)}</div>
            </div>
          )}
        </div>
      )}

      {step===2 && (
        <div style={{...cardStyle,padding:'20px'}}>
          <div style={{marginBottom:16}}>
            {lbl('Emotion')}
            <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
              {['😊 Confident','😰 Anxious','😤 Frustrated','😌 Calm','🤑 Greedy','😨 Fearful','🧠 Focused','😴 Tired'].map(e=>(
                <button key={e} onClick={()=>setForm(f=>({...f,emotion:f.emotion===e?'':e}))}
                  style={{padding:'10px 14px',borderRadius:20,border:`1px solid ${form.emotion===e?C.blue:C.border}`,background:form.emotion===e?`${C.blue}22`:C.card,color:form.emotion===e?C.blueBright:C.sub,cursor:'pointer',fontSize:'14px',fontWeight:'600'}}>
                  {e}
                </button>
              ))}
            </div>
          </div>
          <div style={{marginBottom:16}}>
            {lbl(`Mental Score: ${form.psychScore}/10`)}
            <input name="psychScore" type="range" min="1" max="10" value={form.psychScore} onChange={handleChange} style={{width:'100%',accentColor:C.blue,height:6}}/>
            <div style={{display:'flex',justifyContent:'space-between',color:C.sub,fontSize:'10px',marginTop:4}}><span>1</span><span>10</span></div>
          </div>
          <div style={{marginBottom:16}}>
            {lbl('Rules Followed')}
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {['Followed my trading plan','Proper position sizing','Set stop loss before entry','Confirmed trend direction','Waited for my setup','No revenge trading','Checked risk/reward ratio','No overtrading'].map(rule=>(
                <div key={rule} onClick={()=>toggleRule(rule)}
                  style={{display:'flex',alignItems:'center',gap:12,padding:'12px 14px',borderRadius:10,border:`1px solid ${form.rulesChecked.includes(rule)?C.blue+'55':C.border}`,background:form.rulesChecked.includes(rule)?`${C.blue}15`:C.card2,cursor:'pointer'}}>
                  <div style={{width:20,height:20,borderRadius:5,background:form.rulesChecked.includes(rule)?C.blue:C.border,display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,color:'#fff',fontWeight:'700',flexShrink:0}}>
                    {form.rulesChecked.includes(rule)?'✓':''}
                  </div>
                  <span style={{fontSize:'13px',color:form.rulesChecked.includes(rule)?C.text:C.sub}}>{rule}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{marginBottom:14}}>
            {lbl('Notes')}
            <textarea name="notes" placeholder="Setup reasoning, what went well..." value={form.notes} onChange={handleChange}
              style={{...inp,height:'100px',resize:'none',fontSize:'15px',padding:'14px'}}/>
          </div>
          <div style={{marginBottom:8}}>
            {lbl('Screenshot')}
            <input ref={fileRef} type="file" accept="image/*" onChange={handleScreenshot} style={{display:'none'}}/>
            <button onClick={()=>fileRef.current.click()}
              style={{width:'100%',padding:'13px',background:C.card2,color:C.text,border:`2px dashed ${C.border}`,borderRadius:10,cursor:'pointer',fontSize:'14px',fontWeight:'600'}}>
              📸 Tap to upload screenshot
            </button>
            {form.screenshot&&<img src={form.screenshot} alt="preview" style={{width:'100%',borderRadius:8,marginTop:10,border:`1px solid ${C.border}`}}/>}
          </div>
        </div>
      )}

      {/* Navigation */}
      <div style={{display:'flex',gap:10,marginTop:16,paddingBottom:20}}>
        {step>0 && (
          <button onClick={()=>setStep(s=>s-1)} style={{flex:1,padding:'14px',borderRadius:12,border:`1px solid ${C.border}`,background:'transparent',color:C.text,fontWeight:'700',cursor:'pointer',fontSize:'15px'}}>
            ← Back
          </button>
        )}
        {step<steps.length-1 ? (
          <button onClick={()=>setStep(s=>s+1)} style={{flex:2,padding:'14px',borderRadius:12,border:'none',background:C.blue,color:'#fff',fontWeight:'800',cursor:'pointer',fontSize:'15px'}}>
            Next →
          </button>
        ) : (
          <button onClick={()=>{addTrade();setStep(0);}} style={{flex:2,padding:'14px',borderRadius:12,border:'none',background:C.green,color:'#fff',fontWeight:'800',cursor:'pointer',fontSize:'15px'}}>
            ✓ Add Trade
          </button>
        )}
      </div>
    </div>
  );
}

function WeeklyEmailSettings({ user, C, cardStyle, inp, pushToast }) {
  const [enabled, setEnabled] = React.useState(() => localStorage.getItem('weekly_email_enabled') === 'true');
  const [sending, setSending] = React.useState(false);

  const toggle = (v) => { setEnabled(v); localStorage.setItem('weekly_email_enabled', v); };

  const sendNow = async () => {
    setSending(true);
    try {
      const res = await fetch('/api/send-weekly-summary', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send');
      pushToast(`Weekly summary sent to ${user.email}!`, 'success');
    } catch (err) {
      pushToast(err.message || 'Failed to send email', 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 680 }}>
      <div style={{ ...cardStyle, padding: '24px' }}>
        <div style={{ color: C.sub, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 16 }}>📧 Weekly Email Summary</div>
        <div style={{ color: C.text, fontSize: 14, marginBottom: 20, lineHeight: 1.6 }}>
          Receive a weekly trading performance summary every <strong>Monday at 8am</strong> — including P&L, win rate, best/worst trades, and a full trade list for the week.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: C.card2, borderRadius: 10, border: `1px solid ${C.border}`, marginBottom: 14 }}>
          <div>
            <div style={{ color: C.text, fontWeight: 700, fontSize: 13 }}>Weekly Summary Emails</div>
            <div style={{ color: C.sub, fontSize: 12, marginTop: 3 }}>Sent to: {user.email}</div>
          </div>
          <button onClick={() => toggle(!enabled)} style={{
            width: 48, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer',
            background: enabled ? C.green : C.border, position: 'relative', transition: 'background 0.2s',
          }}>
            <div style={{
              width: 20, height: 20, borderRadius: '50%', background: '#fff',
              position: 'absolute', top: 3, left: enabled ? 25 : 3, transition: 'left 0.2s',
            }} />
          </button>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={sendNow} disabled={sending} style={{ padding: '10px 20px', background: C.blue, border: 'none', borderRadius: 10, color: '#fff', fontWeight: 700, cursor: sending ? 'not-allowed' : 'pointer', fontSize: 13, opacity: sending ? 0.7 : 1 }}>
            {sending ? 'Sending...' : '📤 Send Test Email Now'}
          </button>
        </div>
        <div style={{ color: C.sub, fontSize: 11, marginTop: 12 }}>
          The test email will include all trades from the last 7 days sent to your account email.
        </div>
      </div>
    </div>
  );
}

function PaywallScreen({ user, onLogout, customerId }) {
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [err, setErr] = useState('');

  const startTrial = async () => {
    setLoading(true); setErr('');
    try {
      const res = await fetch('/api/stripe-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, email: user.email }),
      });
      const data = await res.json();
      if (data.url) { window.location.href = data.url; }
      else setErr(data.error || 'Could not start checkout');
    } catch (e) { setErr(e.message); }
    setLoading(false);
  };

  const manageSubscription = async () => {
    if (!customerId) return;
    setPortalLoading(true);
    try {
      const res = await fetch('/api/stripe-portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId }),
      });
      const data = await res.json();
      if (data.url) { window.location.href = data.url; }
    } catch { /* ignore */ }
    setPortalLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#050912', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: '"Manrope","Sora","Segoe UI",sans-serif', padding: '40px 20px' }}>
      <div style={{ marginBottom: 32, textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 8 }}>
          <TradournalLogo size={44}/>
          <span style={{ fontSize: 28, fontWeight: 900, color: '#e8eefb', letterSpacing: '-0.5px' }}>Tradournal</span>
        </div>
        <div style={{ color: '#95a5c0', fontSize: 13 }}>Professional Trading Journal</div>
      </div>

      <div style={{ background: 'linear-gradient(160deg,rgba(20,30,48,0.95),rgba(14,22,36,0.97))', border: '1px solid #22314a', borderRadius: 20, padding: '48px 48px', maxWidth: 480, width: '100%', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg,#2f8cff22,#8b7bff22)', border: '1px solid #2f8cff44', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2f8cff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
        </div>
        <h2 style={{ color: '#e8eefb', fontSize: 24, fontWeight: 900, margin: '0 0 12px', letterSpacing: '-0.5px' }}>Start Your Free Trial</h2>
        <p style={{ color: '#95a5c0', fontSize: 14, lineHeight: 1.7, margin: '0 0 32px' }}>
          Get full access to Tradournal for <strong style={{ color: '#e8eefb' }}>7 days free</strong>, then just <strong style={{ color: '#2f8cff' }}>$29.99/month</strong>. Cancel anytime.
        </p>

        {['Unlimited trade logging & analysis', 'AI coaching assistant', 'Pre-market scanner & market news', 'Performance reports, equity curve & calendar', 'Multiple accounts & CSV import'].map(f => (
          <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, textAlign: 'left' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2f8cff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            <span style={{ color: '#95a5c0', fontSize: 13 }}>{f}</span>
          </div>
        ))}

        {err && <div style={{ color: '#ef4444', fontSize: 13, marginTop: 16, marginBottom: -8 }}>{err}</div>}

        <button
          onClick={startTrial}
          disabled={loading}
          style={{ marginTop: 32, width: '100%', padding: '14px', borderRadius: 10, background: loading ? '#22314a' : 'linear-gradient(135deg,#2f8cff,#8b7bff)', color: '#fff', border: 'none', fontWeight: 800, fontSize: 15, cursor: loading ? 'not-allowed' : 'pointer', boxShadow: loading ? 'none' : '0 8px 24px rgba(47,140,255,0.35)', transition: 'all 0.2s' }}
        >
          {loading ? 'Redirecting to checkout…' : 'Start 7-Day Free Trial →'}
        </button>
        <div style={{ color: '#95a5c0', fontSize: 11, marginTop: 10 }}>No charge for 7 days. Credit card required.</div>

        {customerId && (
          <button
            onClick={manageSubscription}
            disabled={portalLoading}
            style={{ marginTop: 12, width: '100%', padding: '11px', borderRadius: 10, background: 'transparent', color: '#95a5c0', border: '1px solid #22314a', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
          >
            {portalLoading ? 'Opening portal…' : 'Manage Subscription'}
          </button>
        )}
      </div>

      <button onClick={onLogout} style={{ marginTop: 24, background: 'none', border: 'none', color: '#95a5c0', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
        Sign out
      </button>
    </div>
  );
}

function LandingPage({ onAuth }) {
  const [authMode, setAuthMode] = useState(null);
  const [activeTab, setActiveTab] = useState('TRADE');
  const [openFaq, setOpenFaq] = useState(null);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactMsg, setContactMsg] = useState('');
  const [showTop, setShowTop] = useState(false);
  const [screenTab, setScreenTab] = useState('dashboard');

  React.useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 400);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (authMode) {
    return <AuthScreen onAuth={onAuth} initialMode={authMode} onBack={() => setAuthMode(null)} />;
  }

  const S = { bg: '#050912', card: '#0d1526', border: 'rgba(34,49,74,0.7)', text: '#e8eefb', sub: '#95a5c0', blue: '#2f8cff', purple: '#8b7bff' };
  const sec = { maxWidth: 1200, margin: '0 auto', padding: '80px 48px' };

  const TABS = {
    TRADE: { title: 'Trade Tracking & Logs', desc: 'Log every trade with entry/exit prices, position size, P&L, emotions, rules followed, and custom notes. Review your complete trade history with powerful filters and sorting.', bullets: ['Record every trade in seconds', 'Track emotions and psychology per trade', 'Rules checklist to enforce your strategy', 'Full trade history with filters & search'] },
    REPORTS: { title: 'Deep Performance Reports', desc: 'Understand exactly where you make and lose money. Analyze performance by symbol, setup, session, day of week, and more — with interactive charts.', bullets: ['Equity curve & daily P&L charts', 'Win/loss breakdown by symbol & setup', 'Profit factor, expectancy, RR analysis', 'Streak tracking and drawdown metrics'] },
    CALENDAR: { title: 'Profit Calendar', desc: 'See your trading performance at a glance with a color-coded monthly calendar. Instantly spot your best and worst trading days.', bullets: ['Color-coded daily P&L visualization', 'Monthly and weekly performance views', 'Best/worst day highlights', 'Pattern recognition across months'] },
    NEWS: { title: 'Market News & Scanner', desc: 'Stay informed with curated news from trusted sources like CNBC, Bloomberg, Reuters, and WSJ — filtered to your watchlist. Plus pre-market gainers with charts.', bullets: ['News from CNBC, Bloomberg, Reuters, WSJ', 'Custom symbol watchlist', 'Pre-market movers with bar charts', 'Auto-refreshes throughout the day'] },
  };

  const FAQS = [
    { q: 'What is Tradournal?', a: 'Tradournal is a professional trading journal designed for traders to log, track, and analyze their trades. It records entry/exit points, P&L, psychology, and calculates key metrics like win rate, risk/reward ratio, and profit factor — helping you improve consistently.' },
    { q: 'Can I use Tradournal to place trades?', a: 'No — Tradournal is a journaling and analytics tool, not a brokerage. It helps you track and analyze trades you execute through your own broker.' },
    { q: 'How much does Tradournal cost?', a: 'Tradournal costs $29.99/month after a 7-day free trial. You get full access to every feature during the trial — no limitations. Cancel anytime before the trial ends and you won\'t be charged.' },
    { q: 'What features are included?', a: 'Trade journal, equity curve & daily P&L charts, profit calendar, performance reports, AI coaching assistant, pre-market scanner, market news (CNBC, Bloomberg, Reuters, WSJ), multiple accounts, CSV import, and more.' },
    { q: 'Is Tradournal suitable for all types of traders?', a: 'Yes — whether you trade stocks, options, futures, forex or crypto, Tradournal works for any asset class. It\'s built for day traders, swing traders, and long-term investors alike.' },
    { q: 'How do I import my trades?', a: 'You can import trades via CSV upload from the Import tab inside the app. You can also log trades manually in seconds using the Add Trade form.' },
    { q: 'Is my data secure?', a: 'Yes. All data is stored securely in Supabase (PostgreSQL) with row-level security. Your trade data is private and only accessible to you.' },
  ];

  /* ── App Panel Mockup Components ── */
  const BrowserChrome = ({ url = 'tradournal.com' }) => (
    <div style={{ background: '#08101d', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid #22314a' }}>
      <div style={{ display: 'flex', gap: 5 }}>{['#ef4444','#f59e0b','#0ecb81'].map(c => <div key={c} style={{ width: 8, height: 8, borderRadius: '50%', background: c, opacity: 0.7 }}/>)}</div>
      <div style={{ flex: 1, background: '#131d2d', borderRadius: 5, padding: '3px 10px', fontSize: 9, color: '#95a5c0', textAlign: 'center', maxWidth: 200, margin: '0 auto' }}>{url}</div>
    </div>
  );

  const DashMockup = () => (
    <div style={{ borderRadius: 14, border: '1px solid #22314a', background: '#0f1725', overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.65)' }}>
      <BrowserChrome url="tradournal.com/dashboard"/>
      <div style={{ display: 'flex', height: 300 }}>
        <div style={{ width: 88, background: '#08101d', borderRight: '1px solid #22314a', padding: '12px 8px', flexShrink: 0 }}>
          <div style={{ marginBottom: 16 }}><TradournalLogo size={26}/></div>
          {[['Portfolio',true],['Trade Log',false],['Reports',false],['Scanner',false],['AI Coach',false]].map(([l,a]) => (
            <div key={l} style={{ padding: '4px 6px', marginBottom: 2, borderRadius: 4, background: a ? '#131d2d' : 'transparent', borderLeft: a ? '2px solid #2f8cff' : '2px solid transparent', marginLeft: -8 }}>
              <div style={{ color: a ? '#e8eefb' : '#95a5c0', fontSize: 7.5, fontWeight: a ? 700 : 400 }}>{l}</div>
            </div>
          ))}
        </div>
        <div style={{ flex: 1, padding: 12, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 7, marginBottom: 9 }}>
            {[{l:'Net P&L',v:'+$8,432',c:'#39e5a5'},{l:'Win Rate',v:'64.2%',c:'#7cb6ff'},{l:'Profit Factor',v:'1.82',c:'#f59e0b'}].map(s => (
              <div key={s.l} style={{ background: '#131d2d', borderRadius: 7, padding: 8, border: '1px solid #22314a' }}>
                <div style={{ color: '#95a5c0', fontSize: 6.5, fontWeight: 700, textTransform: 'uppercase', marginBottom: 3 }}>{s.l}</div>
                <div style={{ color: s.c, fontSize: 13, fontWeight: 900 }}>{s.v}</div>
              </div>
            ))}
          </div>
          <div style={{ background: '#131d2d', borderRadius: 7, padding: 9, border: '1px solid #22314a', marginBottom: 9 }}>
            <div style={{ color: '#95a5c0', fontSize: 6.5, fontWeight: 700, textTransform: 'uppercase', marginBottom: 5 }}>Equity Curve</div>
            <svg width="100%" height="46" viewBox="0 0 300 46" preserveAspectRatio="none">
              <defs><linearGradient id="lpGrad2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0ecb81" stopOpacity="0.35"/><stop offset="100%" stopColor="#0ecb81" stopOpacity="0"/></linearGradient></defs>
              <path d="M0,44 L25,40 L50,34 L70,38 L95,28 L120,21 L140,26 L160,13 L185,10 L210,16 L235,7 L255,3 L280,1 L300,0 L300,46 L0,46Z" fill="url(#lpGrad2)"/>
              <path d="M0,44 L25,40 L50,34 L70,38 L95,28 L120,21 L140,26 L160,13 L185,10 L210,16 L235,7 L255,3 L280,1 L300,0" fill="none" stroke="#0ecb81" strokeWidth="1.5"/>
            </svg>
          </div>
          <div style={{ background: '#131d2d', borderRadius: 7, padding: 9, border: '1px solid #22314a' }}>
            <div style={{ color: '#95a5c0', fontSize: 6.5, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Recently Closed</div>
            {[['AAPL','+$234','#39e5a5','Win'],['TSLA','-$89','#f87171','Loss'],['NVDA','+$512','#39e5a5','Win']].map(([s,p,c,o]) => (
              <div key={s} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0', borderBottom: '1px solid rgba(34,49,74,0.4)' }}>
                <span style={{ color: '#e8eefb', fontSize: 7.5, fontWeight: 700 }}>{s}</span>
                <span style={{ color: c, fontSize: 7.5, fontWeight: 800 }}>{p}</span>
                <span style={{ padding: '1px 5px', borderRadius: 8, background: o==='Win'?'rgba(22,199,132,0.15)':'rgba(239,68,68,0.15)', color: c, fontSize: 7, fontWeight: 700 }}>{o}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const AnalyticsMockup = () => (
    <div style={{ borderRadius: 14, border: '1px solid #22314a', background: '#0f1725', overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.65)' }}>
      <BrowserChrome url="tradournal.com/analytics"/>
      <div style={{ padding: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 7, marginBottom: 11 }}>
          {[{l:'Win Rate',v:'64%',c:'#7cb6ff'},{l:'Profit Factor',v:'1.82',c:'#f59e0b'},{l:'Net P&L',v:'+$8,432',c:'#39e5a5'},{l:'Avg RR',v:'1:2.4',c:'#8b7bff'}].map(m => (
            <div key={m.l} style={{ background: '#131d2d', borderRadius: 7, padding: 8, border: '1px solid #22314a' }}>
              <div style={{ color: '#95a5c0', fontSize: 6, fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>{m.l}</div>
              <div style={{ color: m.c, fontSize: 12, fontWeight: 900 }}>{m.v}</div>
            </div>
          ))}
        </div>
        <div style={{ background: '#131d2d', borderRadius: 7, padding: 10, border: '1px solid #22314a', marginBottom: 9 }}>
          <div style={{ color: '#95a5c0', fontSize: 6.5, fontWeight: 700, textTransform: 'uppercase', marginBottom: 5 }}>Equity Curve — All Time</div>
          <svg width="100%" height="66" viewBox="0 0 320 66" preserveAspectRatio="none">
            <defs><linearGradient id="ag1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0ecb81" stopOpacity="0.4"/><stop offset="100%" stopColor="#0ecb81" stopOpacity="0"/></linearGradient></defs>
            <path d="M0,64 L20,60 L40,55 L55,58 L75,48 L95,38 L115,44 L135,30 L155,22 L175,28 L195,16 L215,10 L235,6 L255,3 L275,1 L295,0 L320,1 L320,66 L0,66Z" fill="url(#ag1)"/>
            <path d="M0,64 L20,60 L40,55 L55,58 L75,48 L95,38 L115,44 L135,30 L155,22 L175,28 L195,16 L215,10 L235,6 L255,3 L275,1 L295,0 L320,1" fill="none" stroke="#0ecb81" strokeWidth="1.5"/>
            <circle cx="320" cy="1" r="3" fill="#0ecb81"/>
          </svg>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9 }}>
          <div style={{ background: '#131d2d', borderRadius: 7, padding: 10, border: '1px solid #22314a' }}>
            <div style={{ color: '#95a5c0', fontSize: 6.5, fontWeight: 700, textTransform: 'uppercase', marginBottom: 5 }}>Daily P&L</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2.5, height: 36 }}>
              {[25,60,-15,80,35,-8,90,45,70,-25,100,55,75,40,50].map((v,i) => (
                <div key={i} style={{ flex: 1, background: v>0?'rgba(14,203,129,0.75)':'rgba(239,68,68,0.75)', height: `${Math.abs(v)*0.35+3}px`, borderRadius: '2px 2px 0 0', marginTop: 'auto' }}/>
              ))}
            </div>
          </div>
          <div style={{ background: '#131d2d', borderRadius: 7, padding: 10, border: '1px solid #22314a' }}>
            <div style={{ color: '#95a5c0', fontSize: 6.5, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Win/Loss by Symbol</div>
            {[['NVDA','72%','#39e5a5'],['AAPL','65%','#7cb6ff'],['TSLA','41%','#f87171']].map(([s,w,c]) => (
              <div key={s} style={{ marginBottom: 5 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                  <span style={{ color: '#e8eefb', fontSize: 7, fontWeight: 700 }}>{s}</span>
                  <span style={{ color: c, fontSize: 7, fontWeight: 700 }}>{w}</span>
                </div>
                <div style={{ height: 3, background: '#22314a', borderRadius: 2 }}>
                  <div style={{ height: '100%', width: w, background: c, borderRadius: 2 }}/>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const TradeTableMockup = () => (
    <div style={{ borderRadius: 14, border: '1px solid #22314a', background: '#0f1725', overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.65)' }}>
      <BrowserChrome url="tradournal.com/trades"/>
      <div style={{ padding: 12 }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          {['All','Wins','Losses','Today'].map((f,i) => (
            <div key={f} style={{ padding: '3px 9px', borderRadius: 20, background: i===0?'rgba(47,140,255,0.18)':'#131d2d', border: i===0?'1px solid rgba(47,140,255,0.5)':'1px solid #22314a', color: i===0?'#7cb6ff':'#95a5c0', fontSize: 7.5, fontWeight: 700, cursor: 'pointer' }}>{f}</div>
          ))}
        </div>
        <div style={{ background: '#131d2d', borderRadius: 8, overflow: 'hidden', border: '1px solid #22314a' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr 1fr 1fr 1fr 0.9fr', padding: '6px 10px', borderBottom: '1px solid #22314a', background: '#0d1825' }}>
            {['Symbol','Side','Entry','Exit','P&L','Result'].map(h => (
              <div key={h} style={{ color: '#95a5c0', fontSize: 6.5, fontWeight: 700, textTransform: 'uppercase' }}>{h}</div>
            ))}
          </div>
          {[['NVDA','Long','$487.20','$502.80','+$612','Win'],['AAPL','Long','$178.40','$181.25','+$228','Win'],['TSLA','Short','$242.10','$248.60','-$520','Loss'],['META','Long','$498.30','$511.70','+$804','Win'],['AMD','Short','$176.50','$172.30','+$336','Win']].map(([sym,side,en,ex,pnl,res],i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr 1fr 1fr 1fr 0.9fr', padding: '6px 10px', borderBottom: '1px solid rgba(34,49,74,0.3)', alignItems: 'center' }}>
              <span style={{ color: '#e8eefb', fontSize: 7.5, fontWeight: 800 }}>{sym}</span>
              <span style={{ padding: '1px 5px', borderRadius: 4, background: side==='Long'?'rgba(14,203,129,0.12)':'rgba(239,68,68,0.12)', color: side==='Long'?'#39e5a5':'#f87171', fontSize: 7, fontWeight: 700, display:'inline-block' }}>{side}</span>
              <span style={{ color: '#95a5c0', fontSize: 7 }}>{en}</span>
              <span style={{ color: '#95a5c0', fontSize: 7 }}>{ex}</span>
              <span style={{ color: pnl.startsWith('+')?'#39e5a5':'#f87171', fontSize: 7.5, fontWeight: 800 }}>{pnl}</span>
              <span style={{ padding: '1px 5px', borderRadius: 8, background: res==='Win'?'rgba(22,199,132,0.15)':'rgba(239,68,68,0.15)', color: res==='Win'?'#39e5a5':'#f87171', fontSize: 7, fontWeight: 700, display:'inline-block' }}>{res}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const AICoachMockup = () => (
    <div style={{ borderRadius: 14, border: '1px solid #22314a', background: '#0f1725', overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.65)' }}>
      <BrowserChrome url="tradournal.com/ai-coach"/>
      <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10, minHeight: 240 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'linear-gradient(135deg,rgba(47,140,255,0.1),rgba(139,123,255,0.1))', borderRadius: 8, border: '1px solid rgba(47,140,255,0.25)' }}>
          <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'linear-gradient(135deg,#2f8cff,#8b7bff)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a10 10 0 1 0 10 10"/><path d="M12 8v4l3 3"/></svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ color: '#7cb6ff', fontSize: 7.5, fontWeight: 700, marginBottom: 2 }}>AI Coach</div>
            <div style={{ color: '#e8eefb', fontSize: 7, lineHeight: 1.5 }}>Your win rate dropped 12% this week. I noticed you're overtrading during the first 30 minutes — your best trades come after 10 AM.</div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ background: '#131d2d', border: '1px solid #22314a', borderRadius: 8, padding: '7px 10px', maxWidth: '70%' }}>
            <div style={{ color: '#e8eefb', fontSize: 7, lineHeight: 1.5 }}>What rules am I breaking most often?</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'linear-gradient(135deg,#2f8cff,#8b7bff)', flexShrink: 0, marginTop: 2 }}/>
          <div style={{ background: 'rgba(47,140,255,0.08)', border: '1px solid rgba(47,140,255,0.2)', borderRadius: 8, padding: '7px 10px' }}>
            <div style={{ color: '#e8eefb', fontSize: 7, lineHeight: 1.5 }}>You've broken "No revenge trading" 6 times this month — always after a losing trade. Also, position sizing on TSLA trades is 40% too large.</div>
          </div>
        </div>
        <div style={{ marginTop: 'auto', display: 'flex', gap: 6, padding: '8px', background: '#131d2d', borderRadius: 8, border: '1px solid #22314a' }}>
          <div style={{ flex: 1, color: '#95a5c0', fontSize: 7 }}>Ask your AI coach anything...</div>
          <div style={{ padding: '2px 8px', background: 'linear-gradient(135deg,#2f8cff,#8b7bff)', borderRadius: 5, color: '#fff', fontSize: 7, fontWeight: 700 }}>Send</div>
        </div>
      </div>
    </div>
  );

  const ScannerMockup = () => (
    <div style={{ borderRadius: 14, border: '1px solid #22314a', background: '#0f1725', overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.65)' }}>
      <BrowserChrome url="tradournal.com/scanner"/>
      <div style={{ padding: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ color: '#e8eefb', fontSize: 9, fontWeight: 800 }}>Pre-Market Gainers</div>
          <div style={{ padding: '2px 8px', background: 'rgba(14,203,129,0.12)', border: '1px solid rgba(14,203,129,0.3)', borderRadius: 10, color: '#0ecb81', fontSize: 7, fontWeight: 700 }}>LIVE 4:32 AM</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {[['NVDA','+8.42%','$521.30','2.8M'],['META','+5.17%','$508.60','1.6M'],['AAPL','+3.28%','$183.40','3.1M'],['AMZN','+2.91%','$188.70','980K'],['MSFT','+1.84%','$412.20','1.2M']].map(([sym,chg,px,vol],i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 0.8fr', alignItems: 'center', padding: '5px 8px', background: '#131d2d', borderRadius: 7, border: '1px solid #22314a' }}>
              <span style={{ color: '#e8eefb', fontSize: 8, fontWeight: 800 }}>{sym}</span>
              <span style={{ color: '#39e5a5', fontSize: 8, fontWeight: 800 }}>{chg}</span>
              <span style={{ color: '#95a5c0', fontSize: 7 }}>{px}</span>
              <div style={{ height: 4, background: '#22314a', borderRadius: 2 }}>
                <div style={{ height: '100%', width: `${85 - i*14}%`, background: 'linear-gradient(90deg,#0ecb81,#39e5a5)', borderRadius: 2 }}/>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 8, color: '#95a5c0', fontSize: 7, textAlign: 'center' }}>Tracking 100+ large-cap stocks in real time</div>
      </div>
    </div>
  );

  const CalendarMockup = () => {
    const days = [null,null,null,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29,30,31];
    const pnl = {1:280,2:-120,3:540,4:0,5:820,6:0,7:0,8:-340,9:910,10:200,11:650,12:-80,13:420,14:0,15:0,16:750,17:-180,18:430,19:1100,20:-250,21:590,22:0,23:0,24:380,25:-90,26:720,27:310,28:0,29:0,30:840,31:-150};
    return (
      <div style={{ borderRadius: 14, border: '1px solid #22314a', background: '#0f1725', overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.65)' }}>
        <BrowserChrome url="tradournal.com/calendar"/>
        <div style={{ padding: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ color: '#e8eefb', fontSize: 9, fontWeight: 800 }}>March 2026</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <span style={{ color: '#39e5a5', fontSize: 7, fontWeight: 700 }}>+$7,530</span>
              <span style={{ color: '#95a5c0', fontSize: 7 }}>this month</span>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 3 }}>
            {['S','M','T','W','T','F','S'].map(d => <div key={d} style={{ color: '#95a5c0', fontSize: 6.5, fontWeight: 700, textAlign: 'center', marginBottom: 3 }}>{d}</div>)}
            {days.map((d,i) => {
              const v = d ? pnl[d] : null;
              return (
                <div key={i} style={{ aspectRatio: '1', borderRadius: 4, background: d===0||!d ? 'transparent' : v>0 ? `rgba(14,203,129,${Math.min(v/1200*0.6+0.15,0.75)})` : v<0 ? `rgba(239,68,68,${Math.min(Math.abs(v)/400*0.5+0.15,0.7)})` : '#131d2d', border: d ? '1px solid rgba(34,49,74,0.4)' : 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 1 }}>
                  {d ? <><span style={{ color: v>0?'#39e5a5':v<0?'#f87171':'#95a5c0', fontSize: 6, fontWeight: 700 }}>{d}</span>{v!==0&&v!==null&&<span style={{ color: v>0?'#39e5a5':'#f87171', fontSize: 4.5 }}>{v>0?'+':''}{v>999?`+${(v/1000).toFixed(1)}k`:v}</span>}</> : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const ReportsMockup = () => (
    <div style={{ borderRadius: 14, border: '1px solid #22314a', background: '#0f1725', overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.65)' }}>
      <BrowserChrome url="tradournal.com/reports"/>
      <div style={{ padding: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ color: '#e8eefb', fontSize: 9, fontWeight: 800 }}>Performance Report — March 2026</div>
          <div style={{ display: 'flex', gap: 5 }}>
            {['Week','Month','Year'].map((p,i) => <div key={p} style={{ padding: '2px 8px', borderRadius: 10, background: i===1?'rgba(47,140,255,0.2)':'#131d2d', border: i===1?'1px solid rgba(47,140,255,0.5)':'1px solid #22314a', color: i===1?'#7cb6ff':'#95a5c0', fontSize: 7, fontWeight: 700 }}>{p}</div>)}
          </div>
        </div>
        {/* Summary stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 6, marginBottom: 10 }}>
          {[{l:'Total P&L',v:'+$7,530',c:'#39e5a5'},{l:'Win Rate',v:'67.3%',c:'#7cb6ff'},{l:'Profit Factor',v:'2.14',c:'#f59e0b'},{l:'Total Trades',v:'89',c:'#8b7bff'}].map(m => (
            <div key={m.l} style={{ background: '#131d2d', borderRadius: 7, padding: '7px 8px', border: '1px solid #22314a' }}>
              <div style={{ color: '#95a5c0', fontSize: 5.5, fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>{m.l}</div>
              <div style={{ color: m.c, fontSize: 11, fontWeight: 900 }}>{m.v}</div>
            </div>
          ))}
        </div>
        {/* P&L bar chart */}
        <div style={{ background: '#131d2d', borderRadius: 7, padding: 10, border: '1px solid #22314a', marginBottom: 9 }}>
          <div style={{ color: '#95a5c0', fontSize: 6.5, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Daily P&L Breakdown</div>
          <div style={{ position: 'relative', height: 56 }}>
            {/* Baseline */}
            <div style={{ position: 'absolute', left: 0, right: 0, top: '50%', height: 1, background: 'rgba(34,49,74,0.8)', zIndex: 1 }}/>
            <div style={{ display: 'flex', alignItems: 'stretch', gap: 2.5, height: '100%' }}>
              {[40,80,-30,120,60,-20,150,90,70,-40,110,55,85,45,65,-15,95,130,75,50,100,-25,80,60,140,70,-10,90,110,60,80].map((v,i) => (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* top half: green bars grow DOWN from center = align to bottom of top half */}
                  <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end' }}>
                    {v > 0 && <div style={{ width: '100%', height: `${Math.min(v * 0.17, 26)}px`, background: '#0ecb81', borderRadius: '2px 2px 0 0' }}/>}
                  </div>
                  {/* bottom half: red bars grow DOWN from center */}
                  <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start' }}>
                    {v < 0 && <div style={{ width: '100%', height: `${Math.min(Math.abs(v) * 0.17, 26)}px`, background: '#ef4444', borderRadius: '0 0 2px 2px' }}/>}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
            {['Mar 1','Mar 8','Mar 15','Mar 22','Mar 31'].map(d => <span key={d} style={{ color: '#95a5c0', fontSize: 5.5 }}>{d}</span>)}
          </div>
        </div>
        {/* Bottom two panels */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div style={{ background: '#131d2d', borderRadius: 7, padding: 9, border: '1px solid #22314a' }}>
            <div style={{ color: '#95a5c0', fontSize: 6.5, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Best Symbols</div>
            {[['NVDA','+$2,140','#39e5a5'],['AAPL','+$1,280','#39e5a5'],['META','+$980','#7cb6ff'],['TSLA','-$340','#f87171']].map(([s,v,c]) => (
              <div key={s} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid rgba(34,49,74,0.3)' }}>
                <span style={{ color: '#e8eefb', fontSize: 7, fontWeight: 700 }}>{s}</span>
                <span style={{ color: c, fontSize: 7, fontWeight: 800 }}>{v}</span>
              </div>
            ))}
          </div>
          <div style={{ background: '#131d2d', borderRadius: 7, padding: 9, border: '1px solid #22314a' }}>
            <div style={{ color: '#95a5c0', fontSize: 6.5, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Win Rate by Day</div>
            {[['Monday','78%','#39e5a5'],['Tuesday','61%','#7cb6ff'],['Wednesday','43%','#f87171'],['Thursday','70%','#39e5a5'],['Friday','55%','#f59e0b']].map(([d,r,c]) => (
              <div key={d} style={{ marginBottom: 5 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1.5 }}>
                  <span style={{ color: '#95a5c0', fontSize: 6.5 }}>{d}</span>
                  <span style={{ color: c, fontSize: 6.5, fontWeight: 700 }}>{r}</span>
                </div>
                <div style={{ height: 3, background: '#22314a', borderRadius: 2 }}>
                  <div style={{ height: '100%', width: r, background: c, borderRadius: 2 }}/>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const NewsMockup = () => (
    <div style={{ borderRadius: 14, border: '1px solid #22314a', background: '#0f1725', overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.65)' }}>
      <BrowserChrome url="tradournal.com/news"/>
      <div style={{ padding: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ color: '#e8eefb', fontSize: 9, fontWeight: 800 }}>Market News</div>
          <div style={{ display: 'flex', gap: 5 }}>
            {['All','CNBC','Bloomberg','Reuters','WSJ'].map((s,i) => (
              <div key={s} style={{ padding: '2px 7px', borderRadius: 10, background: i===0?'rgba(47,140,255,0.2)':'#131d2d', border: i===0?'1px solid rgba(47,140,255,0.4)':'1px solid #22314a', color: i===0?'#7cb6ff':'#95a5c0', fontSize: 6.5, fontWeight: 700 }}>{s}</div>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {[
            { src: 'CNBC', color: '#f59e0b', title: 'Fed signals pause in rate hikes as inflation cools to 2.8%', time: '2m ago', tag: 'Markets' },
            { src: 'Bloomberg', color: '#2f8cff', title: 'NVDA surges 8% after earnings beat — data center revenue hits record $22B', time: '14m ago', tag: 'Earnings' },
            { src: 'Reuters', color: '#39e5a5', title: 'S&P 500 futures up 0.6% ahead of jobs report; tech leads gains', time: '31m ago', tag: 'Futures' },
            { src: 'WSJ', color: '#8b7bff', title: 'Apple set to unveil AI-powered Siri upgrade at WWDC; shares rise pre-market', time: '48m ago', tag: 'Tech' },
            { src: 'CNBC', color: '#f59e0b', title: 'Oil drops 3% on surprise inventory build; energy sector under pressure', time: '1h ago', tag: 'Commodities' },
          ].map((n,i) => (
            <div key={i} style={{ background: '#131d2d', borderRadius: 8, padding: '8px 10px', border: '1px solid #22314a', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <div style={{ padding: '2px 6px', borderRadius: 5, background: `${n.color}22`, border: `1px solid ${n.color}44`, color: n.color, fontSize: 6, fontWeight: 800, whiteSpace: 'nowrap', marginTop: 1 }}>{n.src}</div>
              <div style={{ flex: 1 }}>
                <div style={{ color: '#e8eefb', fontSize: 7.5, fontWeight: 600, lineHeight: 1.4, marginBottom: 4 }}>{n.title}</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ color: '#95a5c0', fontSize: 6.5 }}>{n.time}</span>
                  <span style={{ padding: '1px 5px', borderRadius: 8, background: 'rgba(47,140,255,0.12)', color: '#7cb6ff', fontSize: 6, fontWeight: 700 }}>{n.tag}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 8, textAlign: 'center', color: '#95a5c0', fontSize: 7 }}>Auto-refreshes every 5 minutes • Filtered to your watchlist</div>
      </div>
    </div>
  );

  const TAB_MOCKUPS = {
    TRADE: <TradeTableMockup/>,
    REPORTS: <ReportsMockup/>,
    CALENDAR: <CalendarMockup/>,
    NEWS: <NewsMockup/>,
  };

  const SCREEN_PANELS = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊', component: <DashMockup/>,
      title: 'Your Trading HQ', desc: 'Get a complete picture of your performance at a glance. Net P&L, win rate, profit factor, and your equity curve — all front and center.',
      bullets: ['Live equity curve chart', 'Net P&L & win rate at a glance', 'Recently closed trades', 'Quick access to all tools'] },
    { id: 'analytics', label: 'Analytics', icon: '📈', component: <AnalyticsMockup/>,
      title: 'Deep Analytics', desc: 'Dig into every performance metric. Break down your results by symbol, session, day of week, and setup to find your real edge.',
      bullets: ['Performance by symbol & session', 'Best and worst trading days', 'Risk/reward distribution', 'Drawdown & streak analysis'] },
    { id: 'trades', label: 'Trade Log', icon: '📋', component: <TradeTableMockup/>,
      title: 'Complete Trade History', desc: 'Every trade logged with full detail — entry, exit, P&L, size, setup tags, notes, and psychology. Searchable and filterable.',
      bullets: ['Full entry/exit details', 'Custom tags & setup labels', 'Notes and psychology tracking', 'Filter, sort, and export'] },
    { id: 'ai', label: 'AI Coach', icon: '🤖', component: <AICoachMockup/>,
      title: 'Your Personal AI Coach', desc: 'An AI that knows your trading history. Ask it anything — it spots patterns in your data and tells you exactly how to improve.',
      bullets: ['Pattern detection in your trades', 'Rule violation alerts', 'Session timing advice', 'Personalized improvement tips'] },
    { id: 'scanner', label: 'Scanner', icon: '🔍', component: <ScannerMockup/>,
      title: 'Pre-Market Scanner', desc: 'Find the biggest movers before the market opens. Filter by gap %, volume, float, and sector to build your watchlist fast.',
      bullets: ['Top pre-market gainers/losers', 'Filter by gap, volume, float', 'Real-time news per ticker', 'Add directly to watchlist'] },
    { id: 'calendar', label: 'Calendar', icon: '📅', component: <CalendarMockup/>,
      title: 'Profit Calendar', desc: 'See your trading performance across the month at a glance. Spot your best days, worst slumps, and recurring patterns instantly.',
      bullets: ['Color-coded daily P&L view', 'Best & worst day highlights', 'Monthly summary stats', 'Spot patterns across weeks'] },
  ];

  return (
    <div style={{ background: S.bg, minHeight: '100vh', fontFamily: C.font, color: S.text, overflowX: 'hidden' }}>

      {/* ── Navbar ── */}
      <nav style={{ height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 48px', borderBottom: `1px solid ${S.border}`, position: 'sticky', top: 0, background: 'rgba(5,9,18,0.95)', backdropFilter: 'blur(14px)', zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 36 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <TradournalLogo size={44}/>
            <span style={{ fontWeight: 800, fontSize: 9, letterSpacing: '1px', textTransform: 'uppercase', color: S.sub }}>Tradournal</span>
          </div>
          <div style={{ display: 'flex', gap: 28 }}>
            <span style={{ color: S.sub, fontSize: 13, fontWeight: 600, cursor: 'pointer', letterSpacing: '0.5px' }}>HOME</span>
            <a href="#features" style={{ color: S.sub, fontSize: 13, fontWeight: 600, textDecoration: 'none', letterSpacing: '0.5px' }}>FEATURES</a>
            <a href="#contact" style={{ color: S.sub, fontSize: 13, fontWeight: 600, textDecoration: 'none', letterSpacing: '0.5px' }}>CONTACT</a>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setAuthMode('signup')} style={{ padding: '8px 22px', borderRadius: 20, border: '1.5px solid rgba(149,165,192,0.5)', background: 'transparent', color: S.text, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>SIGN UP</button>
          <button onClick={() => setAuthMode('login')} style={{ padding: '8px 22px', borderRadius: 20, background: 'linear-gradient(135deg,#2f8cff,#8b7bff)', border: 'none', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>LOG IN</button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <div style={{ ...sec, display: 'flex', alignItems: 'center', gap: 64, flexWrap: 'wrap', paddingTop: 40, paddingBottom: 64 }}>
        <div style={{ flex: '1 1 360px', maxWidth: 480 }}>
          <h1 style={{ margin: '0 0 20px', fontSize: 50, fontWeight: 900, lineHeight: 1.05, letterSpacing: '-2px' }}>
            Track Your Trades.<br/>
            <span style={{ background: 'linear-gradient(90deg,#2f8cff,#8b7bff)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Master Your Edge.</span>
          </h1>
          <p style={{ color: S.sub, fontSize: 15, lineHeight: 1.75, margin: '0 0 36px' }}>Log trades, track P&L, spot patterns, and improve with an AI coach — all in one powerful journal built for serious traders.</p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button onClick={() => setAuthMode('signup')} style={{ padding: '15px 34px', borderRadius: 10, background: 'linear-gradient(135deg,#2f8cff,#8b7bff)', color: '#fff', border: 'none', fontWeight: 800, fontSize: 15, cursor: 'pointer', boxShadow: '0 8px 28px rgba(47,140,255,0.38)', letterSpacing: '-0.2px' }}>Start Free Trial →</button>
            <button onClick={() => setAuthMode('login')} style={{ padding: '15px 26px', borderRadius: 10, background: 'transparent', color: S.sub, border: `1px solid ${S.border}`, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>Log In</button>
          </div>
          <div style={{ display: 'flex', gap: 32, marginTop: 44, flexWrap: 'wrap' }}>
            {[['7-Day','Free trial'],['AI','Built-in coach'],['$29.99','Per month'],['Real-time','News & scanner']].map(([v,l]) => (
              <div key={v}><div style={{color:S.text,fontSize:17,fontWeight:900}}>{v}</div><div style={{color:S.sub,fontSize:11,marginTop:3}}>{l}</div></div>
            ))}
          </div>
        </div>
        <div style={{ flex: '1 1 440px', maxWidth: 620, position: 'relative' }}>
          <div style={{ position: 'absolute', inset: '-60px', background: 'radial-gradient(ellipse at 50% 50%,rgba(47,140,255,0.11),transparent 68%)', pointerEvents: 'none' }}/>
          <DashMockup/>
        </div>
      </div>

      {/* ── Stats bar ── */}
      <div style={{ borderTop: `1px solid ${S.border}`, borderBottom: `1px solid ${S.border}`, background: '#080f1c', padding: '24px 0' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 48px', display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 24 }}>
          {[['2,400+','Active Traders'],['$12M+','P&L Tracked'],['500K+','Trades Logged'],['4.9★','User Rating']].map(([v,l]) => (
            <div key={l} style={{ textAlign: 'center' }}>
              <div style={{ color: S.text, fontSize: 28, fontWeight: 900, letterSpacing: '-0.5px' }}>{v}</div>
              <div style={{ color: S.sub, fontSize: 12, marginTop: 4 }}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Inside Screenshots Gallery ── */}
      <div id="features" style={{ borderTop: `1px solid ${S.border}` }}>
        <div style={{ ...sec, paddingTop: 80, paddingBottom: 80 }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div style={{ color: S.sub, fontSize: 12, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 12 }}>● INSIDE TRADOURNAL ●</div>
            <h2 style={{ color: S.text, fontSize: 36, fontWeight: 900, margin: '0 0 14px', letterSpacing: '-0.8px' }}>Every Tool You Need, In One Place</h2>
            <p style={{ color: S.sub, fontSize: 15, margin: 0, maxWidth: 560, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.7 }}>See exactly what you get — a beautifully designed journal with powerful analytics, AI coaching, and real-time market data.</p>
          </div>
          <div style={{ border: `1px solid ${S.border}`, borderRadius: 14, overflow: 'hidden' }}>
            {/* Tab bar */}
            <div style={{ display: 'flex', borderBottom: `1px solid ${S.border}`, background: '#0d1526', overflowX: 'auto' }}>
              {SCREEN_PANELS.map(p => (
                <button key={p.id} onClick={() => setScreenTab(p.id)} style={{ flex: 1, minWidth: 100, padding: '15px 8px', background: 'transparent', border: 'none', borderBottom: screenTab===p.id ? '2px solid #2f8cff' : '2px solid transparent', color: screenTab===p.id ? '#7cb6ff' : S.sub, fontWeight: 700, fontSize: 12, cursor: 'pointer', letterSpacing: '0.3px', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                  <span>{p.icon}</span>{p.label.toUpperCase()}
                </button>
              ))}
            </div>
            {/* Two-column content */}
            {(() => {
              const panel = SCREEN_PANELS.find(p => p.id === screenTab);
              return (
                <div style={{ padding: '40px 48px', display: 'flex', gap: 56, alignItems: 'center', flexWrap: 'wrap', background: '#080f1c' }}>
                  <div style={{ flex: '1 1 280px' }}>
                    <h3 style={{ color: S.text, fontSize: 22, fontWeight: 800, margin: '0 0 14px', letterSpacing: '-0.3px' }}>{panel.title}</h3>
                    <p style={{ color: S.sub, fontSize: 14, lineHeight: 1.75, margin: '0 0 24px' }}>{panel.desc}</p>
                    {panel.bullets.map(b => (
                      <div key={b} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginTop: 1, flexShrink: 0 }} stroke="#2f8cff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                        <span style={{ color: S.sub, fontSize: 14 }}>{b}</span>
                      </div>
                    ))}
                    <button onClick={() => setAuthMode('signup')} style={{ marginTop: 24, padding: '12px 28px', borderRadius: 8, background: 'linear-gradient(135deg,#2f8cff,#8b7bff)', color: '#fff', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>Get Started Free →</button>
                  </div>
                  <div style={{ flex: '1 1 340px', maxWidth: 520 }}>{panel.component}</div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* ── Craft Winning Strategies (tabbed) ── */}
      <div style={{ borderTop: `1px solid ${S.border}` }}>
        <div style={{ ...sec }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ color: S.text, fontSize: 34, fontWeight: 900, margin: '0 0 12px', letterSpacing: '-0.6px' }}>Craft Winning Trading Strategies<br/>With Tradournal</h2>
            <p style={{ color: S.sub, fontSize: 14, margin: 0 }}>Everything you need to analyze, improve, and stay consistent.</p>
          </div>
          <div style={{ border: `1px solid ${S.border}`, borderRadius: 14, overflow: 'hidden' }}>
            <div style={{ display: 'flex', borderBottom: `1px solid ${S.border}`, background: '#0d1526' }}>
              {Object.keys(TABS).map(t => (
                <button key={t} onClick={() => setActiveTab(t)} style={{ flex: 1, padding: '16px 8px', background: 'transparent', border: 'none', borderBottom: activeTab===t ? '2px solid #2f8cff' : '2px solid transparent', color: activeTab===t ? '#7cb6ff' : S.sub, fontWeight: 700, fontSize: 12.5, cursor: 'pointer', letterSpacing: '0.4px' }}>{t==='CALENDAR' ? 'PROFIT CALENDAR' : t}</button>
              ))}
            </div>
            <div style={{ padding: '40px 48px', display: 'flex', gap: 56, alignItems: 'center', flexWrap: 'wrap', background: '#080f1c' }}>
              <div style={{ flex: '1 1 300px' }}>
                <h3 style={{ color: S.text, fontSize: 22, fontWeight: 800, margin: '0 0 14px', letterSpacing: '-0.3px' }}>{TABS[activeTab].title}</h3>
                <p style={{ color: S.sub, fontSize: 14, lineHeight: 1.75, margin: '0 0 24px' }}>{TABS[activeTab].desc}</p>
                {TABS[activeTab].bullets.map(b => (
                  <div key={b} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginTop: 1, flexShrink: 0 }} stroke="#2f8cff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    <span style={{ color: S.sub, fontSize: 14 }}>{b}</span>
                  </div>
                ))}
                <button onClick={() => setAuthMode('signup')} style={{ marginTop: 24, padding: '12px 28px', borderRadius: 8, background: 'linear-gradient(135deg,#2f8cff,#8b7bff)', color: '#fff', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>Get Started Free →</button>
              </div>
              <div style={{ flex: '1 1 320px', maxWidth: 480 }}>{TAB_MOCKUPS[activeTab]}</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── What We Offer — Visual Cards ── */}
      <div style={{ background: '#080f1c', borderTop: `1px solid ${S.border}`, borderBottom: `1px solid ${S.border}` }}>
        <div style={{ ...sec, paddingTop: 80, paddingBottom: 80 }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div style={{ color: S.sub, fontSize: 12, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 12 }}>● WHAT WE OFFER ●</div>
            <h2 style={{ color: S.text, fontSize: 36, fontWeight: 900, margin: '0 0 14px', letterSpacing: '-0.8px' }}>Everything a Serious Trader Needs</h2>
            <p style={{ color: S.sub, fontSize: 15, margin: 0, lineHeight: 1.7 }}>Built to help you track, analyze, and consistently improve your performance.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 24 }}>
            {[
              {
                title: 'Smart Trade Journal',
                desc: 'Log every trade in seconds — entry, exit, size, P&L, emotions, and rules. Search, filter, and sort your full history.',
                color: '#2f8cff',
                preview: <TradeTableMockup/>,
              },
              {
                title: 'Deep Performance Analytics',
                desc: 'Equity curve, daily P&L charts, win rate by symbol, profit factor, risk/reward breakdowns and more — all interactive.',
                color: '#0ecb81',
                preview: <AnalyticsMockup/>,
              },
              {
                title: 'AI Trading Coach',
                desc: 'An intelligent coach powered by Claude AI that analyzes your actual trades and gives personalized, data-driven advice.',
                color: '#8b7bff',
                preview: <AICoachMockup/>,
              },
              {
                title: 'Pre-Market Scanner',
                desc: 'See the biggest large-cap movers before the open — with real-time price, % change, and volume data.',
                color: '#f59e0b',
                preview: <ScannerMockup/>,
              },
              {
                title: 'Profit Calendar',
                desc: 'A color-coded monthly calendar showing every trading day\'s P&L. Instantly spot patterns and your best sessions.',
                color: '#39e5a5',
                preview: <CalendarMockup/>,
              },
              {
                title: 'Portfolio Dashboard',
                desc: 'A real-time overview of your account — net P&L, win rate, profit factor, equity curve, and recent trades at a glance.',
                color: '#7cb6ff',
                preview: <DashMockup/>,
              },
            ].map(f => (
              <div key={f.title} style={{ background: 'linear-gradient(160deg,rgba(13,21,38,0.95),rgba(8,15,28,0.95))', border: `1px solid ${S.border}`, borderRadius: 16, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '24px 24px 0' }}>
                  <div style={{ display: 'inline-block', padding: '4px 12px', background: `rgba(${f.color==='#2f8cff'?'47,140,255':f.color==='#0ecb81'?'14,203,129':f.color==='#8b7bff'?'139,123,255':f.color==='#f59e0b'?'245,158,11':f.color==='#39e5a5'?'57,229,165':'124,182,255'},0.14)`, border: `1px solid ${f.color}44`, borderRadius: 20, marginBottom: 12 }}>
                    <span style={{ color: f.color, fontSize: 11, fontWeight: 700 }}>{f.title}</span>
                  </div>
                  <p style={{ color: S.sub, fontSize: 13, lineHeight: 1.7, margin: '0 0 20px' }}>{f.desc}</p>
                </div>
                <div style={{ padding: '0 12px 12px', marginTop: 'auto' }}>
                  <div style={{ transform: 'scale(0.72)', transformOrigin: 'top left', width: '138.9%', pointerEvents: 'none' }}>
                    {f.preview}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 44 }}>
            <button onClick={() => setAuthMode('signup')} style={{ padding: '15px 40px', borderRadius: 10, background: 'linear-gradient(135deg,#2f8cff,#8b7bff)', color: '#fff', border: 'none', fontWeight: 800, fontSize: 15, cursor: 'pointer', boxShadow: '0 8px 28px rgba(47,140,255,0.38)' }}>
              Start Your Free Trial →
            </button>
          </div>
        </div>
      </div>

      {/* ── Pricing ── */}
      <div style={{ borderTop: `1px solid ${S.border}`, borderBottom: `1px solid ${S.border}` }}>
        <div style={{ ...sec, textAlign: 'center' }}>
          <div style={{ color: S.sub, fontSize: 12, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 10 }}>● PRICING ●</div>
          <h2 style={{ color: S.text, fontSize: 34, fontWeight: 900, margin: '0 0 8px', letterSpacing: '-0.5px' }}>Simple, Transparent Pricing</h2>
          <p style={{ color: S.sub, fontSize: 15, margin: '0 0 48px' }}>Start free for 7 days. Cancel anytime.</p>
          <div style={{ display: 'inline-block', background: 'linear-gradient(160deg,#0f1a2e,#0a1220)', border: '1px solid #2f8cff55', borderRadius: 20, padding: '40px 52px', minWidth: 340, textAlign: 'left', boxShadow: '0 0 60px rgba(47,140,255,0.1)', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, right: 0, background: 'linear-gradient(135deg,#2f8cff,#8b7bff)', color: '#fff', fontSize: 11, fontWeight: 800, padding: '6px 16px', borderRadius: '0 20px 0 10px', letterSpacing: '0.5px' }}>7-DAY FREE TRIAL</div>
            <div style={{ color: '#39e5a5', fontSize: 13, fontWeight: 700, marginBottom: 12, marginTop: 12 }}>Full access — No commitment</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 28 }}>
              <span style={{ color: S.text, fontSize: 46, fontWeight: 900, letterSpacing: '-2px' }}>$29.99</span>
              <span style={{ color: S.sub, fontSize: 16 }}>/month</span>
            </div>
            {['Unlimited trade logging & history','Multiple trading accounts','AI coaching assistant','Pre-market scanner & trusted news','Performance reports & equity curve','Profit calendar','CSV import support'].map(f => (
              <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2f8cff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                <span style={{ color: S.sub, fontSize: 14 }}>{f}</span>
              </div>
            ))}
            <button onClick={() => setAuthMode('signup')} style={{ marginTop: 28, width: '100%', padding: '14px', borderRadius: 10, background: 'linear-gradient(135deg,#2f8cff,#8b7bff)', color: '#fff', border: 'none', fontWeight: 800, fontSize: 15, cursor: 'pointer', boxShadow: '0 8px 24px rgba(47,140,255,0.35)' }}>
              Start Free Trial →
            </button>
            <div style={{ color: S.sub, fontSize: 11, marginTop: 10, textAlign: 'center' }}>No charge for 7 days. Cancel anytime.</div>
          </div>
        </div>
      </div>

      {/* ── FAQ ── */}
      <div style={{ ...sec }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={{ color: S.sub, fontSize: 12, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 10 }}>● FAQ ●</div>
          <h2 style={{ color: S.text, fontSize: 32, fontWeight: 900, margin: 0, letterSpacing: '-0.5px' }}>Frequently Asked Questions</h2>
        </div>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          {FAQS.map((f, i) => (
            <div key={i} style={{ borderBottom: `1px solid ${S.border}` }}>
              <button onClick={() => setOpenFaq(openFaq===i ? null : i)} style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '22px 0', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', gap: 16 }}>
                <span style={{ color: S.text, fontSize: 16, fontWeight: 600 }}>{f.q}</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={S.sub} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: openFaq===i ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              {openFaq===i && (
                <div style={{ paddingBottom: 20 }}>
                  <p style={{ color: S.sub, fontSize: 14, lineHeight: 1.75, margin: 0 }}>{f.a}</p>
                  <div style={{ height: 2, background: 'linear-gradient(90deg,#2f8cff,transparent)', marginTop: 16, borderRadius: 1 }}/>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── Contact ── */}
      <div id="contact" style={{ background: '#080f1c', borderTop: `1px solid ${S.border}` }}>
        <div style={{ ...sec }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div style={{ color: S.sub, fontSize: 12, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 20 }}>● CONTACT ●</div>
            <div style={{ width: 64, height: 64, borderRadius: '50%', border: '2px solid #f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v12H4z"/><polyline points="4,4 12,13 20,4"/></svg>
            </div>
            <a href="mailto:support@tradournal.com" style={{ color: S.text, fontSize: 20, fontWeight: 700, textDecoration: 'none' }}>support@tradournal.com</a>
          </div>
          <div style={{ maxWidth: 780, margin: '0 auto', background: 'rgba(47,140,255,0.04)', border: `1px solid ${S.border}`, borderRadius: 16, padding: '40px', backgroundImage: 'radial-gradient(ellipse at 60% 0%, rgba(47,140,255,0.06), transparent 60%)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <input value={contactName} onChange={e=>setContactName(e.target.value)} placeholder="Your name" style={{ width: '100%', boxSizing: 'border-box', padding: '13px 16px', background: 'rgba(255,255,255,0.04)', border: `1px solid ${S.border}`, borderRadius: 8, color: S.text, fontSize: 14, outline: 'none', fontFamily: 'inherit' }}/>
              <input value={contactEmail} onChange={e=>setContactEmail(e.target.value)} placeholder="Your email" style={{ width: '100%', boxSizing: 'border-box', padding: '13px 16px', background: 'rgba(255,255,255,0.04)', border: `1px solid ${S.border}`, borderRadius: 8, color: S.text, fontSize: 14, outline: 'none', fontFamily: 'inherit' }}/>
            </div>
            <textarea value={contactMsg} onChange={e=>setContactMsg(e.target.value)} placeholder="Enter your message" rows={5} style={{ width: '100%', boxSizing: 'border-box', padding: '13px 16px', background: 'rgba(255,255,255,0.04)', border: `1px solid ${S.border}`, borderRadius: 8, color: S.text, fontSize: 14, outline: 'none', resize: 'vertical', fontFamily: 'inherit', marginBottom: 20 }}/>
            <div style={{ textAlign: 'center' }}>
              <a href={`mailto:support@tradournal.com?subject=Message from ${contactName}&body=${encodeURIComponent(contactMsg)}`} style={{ display: 'inline-block', padding: '13px 44px', borderRadius: 30, border: '1.5px solid rgba(47,140,255,0.6)', color: S.text, fontWeight: 700, fontSize: 14, textDecoration: 'none', letterSpacing: '0.5px', cursor: 'pointer' }}>
                SEND MESSAGE
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ── CTA banner ── */}
      <div style={{ ...sec, paddingTop: 64, paddingBottom: 64 }}>
        <div style={{ background: 'linear-gradient(135deg,rgba(47,140,255,0.12),rgba(139,123,255,0.12))', border: '1px solid rgba(47,140,255,0.25)', borderRadius: 20, padding: '60px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 0%,rgba(47,140,255,0.08),transparent 60%)', pointerEvents: 'none' }}/>
          <h2 style={{ color: S.text, fontSize: 32, fontWeight: 900, margin: '0 0 14px', letterSpacing: '-0.6px' }}>Start Journaling Your Trades Today</h2>
          <p style={{ color: S.sub, fontSize: 15, margin: '0 0 32px', lineHeight: 1.7 }}>7-day free trial. No credit card required. Set up in under 60 seconds.</p>
          <button onClick={() => setAuthMode('signup')} style={{ padding: '15px 40px', borderRadius: 10, background: 'linear-gradient(135deg,#2f8cff,#8b7bff)', color: '#fff', border: 'none', fontWeight: 800, fontSize: 15, cursor: 'pointer', boxShadow: '0 8px 28px rgba(47,140,255,0.38)' }}>
            Create Free Account →
          </button>
        </div>
      </div>

      {/* ── Footer ── */}
      <div style={{ borderTop: `1px solid ${S.border}`, background: '#080f1c' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '56px 48px 32px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 48, flexWrap: 'wrap' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <TradournalLogo size={34}/>
              <span style={{ fontWeight: 800, fontSize: 16, letterSpacing: '-0.3px' }}>Tradournal</span>
            </div>
            <p style={{ color: S.sub, fontSize: 13, lineHeight: 1.7, margin: '0 0 20px' }}>Analyze and optimize your trading strategies with Tradournal.</p>
            <div style={{ display: 'flex', gap: 10 }}>
              {[
                { href: 'https://instagram.com', icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg> },
                { href: 'https://x.com', icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4l16 16M20 4L4 20"/></svg> },
              ].map((s,i) => (
                <a key={i} href={s.href} target="_blank" rel="noopener noreferrer" style={{ width: 36, height: 36, borderRadius: '50%', border: `1px solid ${S.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: S.sub, textDecoration: 'none' }}>{s.icon}</a>
              ))}
            </div>
          </div>
          <div>
            <div style={{ color: S.text, fontSize: 14, fontWeight: 700, marginBottom: 16 }}>Features</div>
            {['Trade Journal','Performance Reports','Profit Calendar','AI Coach','Market News','Pre-Market Scanner'].map(l => (
              <div key={l} style={{ color: S.sub, fontSize: 13, marginBottom: 10, cursor: 'pointer' }} onClick={() => setAuthMode('signup')}>{l}</div>
            ))}
          </div>
          <div>
            <div style={{ color: S.text, fontSize: 14, fontWeight: 700, marginBottom: 16 }}>Legal</div>
            {[['Terms & Conditions','/terms.html'],['Privacy Policy','/privacy.html'],['Contact Us','mailto:support@tradournal.com']].map(([l,h]) => (
              <a key={l} href={h} target="_blank" rel="noopener noreferrer" style={{ display: 'block', color: S.sub, fontSize: 13, marginBottom: 10, textDecoration: 'none' }}>{l}</a>
            ))}
          </div>
        </div>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 48px 28px', borderTop: `1px solid ${S.border}`, paddingTop: 20, display: 'flex', justifyContent: 'center' }}>
          <span style={{ color: S.sub, fontSize: 12 }}>© 2026 Tradournal. All Rights Reserved.</span>
        </div>
      </div>

      {/* ── Scroll to top ── */}
      {showTop && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} style={{ position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)', width: 44, height: 44, borderRadius: '50%', background: '#131d2d', border: `1px solid ${S.border}`, color: S.sub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99, boxShadow: '0 4px 16px rgba(0,0,0,0.4)' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
        </button>
      )}
    </div>
  );
}

function AuthScreen({ onAuth, initialMode = 'login', onBack }) {
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const handleResendConfirmation = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setError('Enter your email first, then resend the confirmation email.');
      return;
    }
    setLoading(true); setError(''); setMessage('');
    try {
      const redirectTo = typeof window !== 'undefined' ? `${window.location.origin}/` : undefined;
      await runAuthActionFallback('resend-confirmation', normalizedEmail, redirectTo);
      setMessage('Confirmation email sent. Check your inbox and spam folder, then try logging in again.');
    } catch (err) {
      setError(err?.message || 'Unable to resend confirmation email right now.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setError('Enter your email first, then request a password reset link.');
      return;
    }
    setLoading(true); setError(''); setMessage('');
    try {
      const redirectTo = typeof window !== 'undefined' ? `${window.location.origin}/` : undefined;
      if (typeof window !== 'undefined') localStorage.setItem('pendingPasswordReset', Date.now().toString());
      await runAuthActionFallback('reset', normalizedEmail, redirectTo);
      setMessage('Password reset email sent. Check your inbox and spam folder.');
    } catch (err) {
      setError(err?.message || 'Unable to send password reset email right now.');
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setError('Enter your email first, then request a sign-in link.');
      return;
    }
    setLoading(true); setError(''); setMessage('');
    try {
      const redirectTo = typeof window !== 'undefined' ? `${window.location.origin}/` : undefined;
      await runAuthActionFallback('magic-link', normalizedEmail, redirectTo);
      setMessage('Sign-in link sent. Open the email on this device, then tap the link to enter your journal.');
    } catch (err) {
      setError(err?.message || 'Unable to send sign-in link right now.');
    } finally {
      setLoading(false);
    }
  };

  const handle = async () => {
    if (!email || !password) { setError('Please enter email and password.'); return; }
    const normalizedEmail = email.trim().toLowerCase();
    setLoading(true); setError(''); setMessage('');
    try {
      if (mode === 'login') {
        let data = null;
        let err = null;
        try {
          const result = await supabase.auth.signInWithPassword({ email: normalizedEmail, password });
          data = result.data;
          err = result.error;
        } catch (sdkErr) {
          err = sdkErr;
        }

        if (err && isTransientNetworkError(err)) {
          const fallback = await signInWithPasswordFallback(normalizedEmail, password);
          data = fallback;
          err = null;
        }
        if (err) throw err;

        const authUser = data?.user || data?.session?.user || null;
        if (!authUser) throw new Error('Login succeeded but the user session was not restored. Please try again.');
        onAuth({ user: authUser, session: data?.session || null });
      } else {
        const { data, error: err } = await supabase.auth.signUp({ email: normalizedEmail, password });
        if (err) throw err;
        if (data.user && !data.user.confirmed_at) {
          setMessage('Check your email to confirm your account, then log in.');
        } else {
          onAuth({ user: data.user, session: data?.session || null });
        }
      }
    } catch (err) {
      const raw = `${err?.message || err || 'Login failed.'}`;
      const friendly = raw.toLowerCase().includes('email not confirmed')
        ? 'Your email is not confirmed yet. Open the confirmation email, then try logging in again.'
        : raw.toLowerCase().includes('invalid login credentials')
          ? 'Incorrect email or password. Please check your details and try again.'
          : raw;
      setError(friendly);
    } finally {
      setLoading(false);
    }
  };

  const [showPw, setShowPw] = useState(false);
  const isLogin = mode === 'login';

  const authInpStyle = {
    width: '100%', boxSizing: 'border-box', padding: '12px 14px',
    border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14,
    color: '#1a202c', background: '#fff', outline: 'none',
    fontFamily: 'inherit', transition: 'border-color 0.15s',
  };

  const RightPanel = () => (
    <div style={{ flex: '0 0 50%', background: '#111827', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 40px' }}>
      <h2 style={{ color: '#fff', fontSize: 30, fontWeight: 900, textAlign: 'center', margin: '0 0 14px', lineHeight: 1.2, letterSpacing: '-0.5px' }}>
        Unlock Your Trading<br />Potential!
      </h2>
      <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 14, textAlign: 'center', margin: '0 0 36px', lineHeight: 1.7, maxWidth: 320 }}>
        Start optimizing your trading strategy today — sign up for free and take the first step towards smarter trading!
      </p>
      {/* App mockup */}
      <div style={{ width: '100%', maxWidth: 520, borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)', background: '#0f1725', overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}>
        <div style={{ background: '#08101d', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 7, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ display: 'flex', gap: 5 }}>
            {['#ef4444','#f59e0b','#0ecb81'].map(c => <div key={c} style={{ width:7,height:7,borderRadius:'50%',background:c,opacity:0.7 }}/>)}
          </div>
          <div style={{ flex:1, background:'#131d2d', borderRadius:4, padding:'2px 10px', fontSize:9, color:'#95a5c0', textAlign:'center', maxWidth:160, margin:'0 auto' }}>tradournal.com</div>
        </div>
        <div style={{ display:'flex', height:280 }}>
          <div style={{ width:82, background:'#08101d', borderRight:'1px solid rgba(255,255,255,0.05)', padding:'12px 8px', flexShrink:0 }}>
            <div style={{ marginBottom:16 }}><TradournalLogo size={26}/></div>
            {[['Portfolio',true],['Trade Log',false],['Reports',false],['Scanner',false],['AI Coach',false]].map(([l,a])=>(
              <div key={l} style={{ padding:'4px 6px',marginBottom:2,borderRadius:4,background:a?'#131d2d':'transparent',borderLeft:a?'2px solid #2f8cff':'2px solid transparent',marginLeft:-8 }}>
                <div style={{ color:a?'#e8eefb':'#95a5c0',fontSize:7.5,fontWeight:a?700:400 }}>{l}</div>
              </div>
            ))}
          </div>
          <div style={{ flex:1, padding:12, overflow:'hidden' }}>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:7, marginBottom:9 }}>
              {[{l:'Net P&L',v:'+$8,432',c:'#39e5a5'},{l:'Win Rate',v:'64.2%',c:'#7cb6ff'},{l:'Profit Factor',v:'1.82',c:'#f59e0b'}].map(s=>(
                <div key={s.l} style={{ background:'#131d2d',borderRadius:7,padding:'8px',border:'1px solid #22314a' }}>
                  <div style={{ color:'#95a5c0',fontSize:6.5,fontWeight:700,textTransform:'uppercase',marginBottom:3 }}>{s.l}</div>
                  <div style={{ color:s.c,fontSize:13,fontWeight:900 }}>{s.v}</div>
                </div>
              ))}
            </div>
            <div style={{ background:'#131d2d',borderRadius:7,padding:9,border:'1px solid #22314a',marginBottom:9 }}>
              <div style={{ color:'#95a5c0',fontSize:6.5,fontWeight:700,textTransform:'uppercase',marginBottom:5 }}>Equity Curve</div>
              <svg width="100%" height="48" viewBox="0 0 300 48" preserveAspectRatio="none">
                <defs><linearGradient id="authGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0ecb81" stopOpacity="0.35"/><stop offset="100%" stopColor="#0ecb81" stopOpacity="0"/></linearGradient></defs>
                <path d="M0,46 L25,42 L50,36 L70,40 L95,30 L120,23 L140,28 L160,15 L185,12 L210,18 L235,9 L255,5 L280,3 L300,1 L300,48 L0,48Z" fill="url(#authGrad)"/>
                <path d="M0,46 L25,42 L50,36 L70,40 L95,30 L120,23 L140,28 L160,15 L185,12 L210,18 L235,9 L255,5 L280,3 L300,1" fill="none" stroke="#0ecb81" strokeWidth="1.5"/>
              </svg>
            </div>
            <div style={{ background:'#131d2d',borderRadius:7,padding:9,border:'1px solid #22314a' }}>
              <div style={{ color:'#95a5c0',fontSize:6.5,fontWeight:700,textTransform:'uppercase',marginBottom:6 }}>Recently Closed</div>
              {[['AAPL','+$234','#39e5a5','Win'],['TSLA','-$89','#f87171','Loss'],['NVDA','+$512','#39e5a5','Win']].map(([sym,pnl,c,out])=>(
                <div key={sym} style={{ display:'flex',justifyContent:'space-between',alignItems:'center',padding:'3px 0',borderBottom:'1px solid rgba(34,49,74,0.4)' }}>
                  <span style={{ color:'#e8eefb',fontSize:7.5,fontWeight:700 }}>{sym}</span>
                  <span style={{ color:c,fontSize:7.5,fontWeight:800 }}>{pnl}</span>
                  <span style={{ padding:'1px 5px',borderRadius:8,background:out==='Win'?'rgba(22,199,132,0.15)':'rgba(239,68,68,0.15)',color:c,fontSize:7,fontWeight:700 }}>{out}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ display:'flex', height:'100vh', fontFamily:C.font, overflow:'hidden' }}>
      {/* ── Left panel ── */}
      <div style={{ flex:'0 0 50%', background:'#fff', overflowY:'auto', display:'flex', flexDirection:'column', padding:'40px 56px' }}>
        {/* Back link */}
        {onBack && (
          <button onClick={onBack} style={{ alignSelf:'flex-start', background:'none', border:'none', color:'#64748b', cursor:'pointer', fontSize:13, fontWeight:600, padding:0, marginBottom:32, display:'flex', alignItems:'center', gap:5 }}>
            ← Back to home
          </button>
        )}
        {/* Logo */}
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:48 }}>
          <div style={{ width:52,height:52,borderRadius:12,background:'linear-gradient(135deg,#2f8cff,#8b7bff)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19h16"/><path d="M7 15l3-3 3 2 4-5"/></svg>
          </div>
          <span style={{ fontSize:20,fontWeight:900,color:'#111827',letterSpacing:'-0.3px',textTransform:'uppercase' }}>TRADOURNAL</span>
        </div>

        {/* Headline */}
        <div style={{ marginBottom:28 }}>
          <h1 style={{ margin:0,fontSize:28,fontWeight:900,color:'#111827',lineHeight:1.2,letterSpacing:'-0.5px' }}>
            {isLogin ? <>Welcome back!<br/>Please Sign in to continue.</> : <>Join now and elevate<br/>your trading game —<br/><span style={{ color:'#111827' }}>SIGN UP!</span></>}
          </h1>
          <div style={{ width:64,height:3,background:'linear-gradient(90deg,#2f8cff,#8b7bff)',borderRadius:2,marginTop:10 }}/>
        </div>

        {!isLogin && (
          <p style={{ color:'#64748b',fontSize:14,lineHeight:1.7,margin:'0 0 22px' }}>
            By signing up, you will gain access to powerful tools and insights that will help you analyze, strategize, and enhance your trading performance.
          </p>
        )}

        {/* Email */}
        <div style={{ marginBottom:16 }}>
          <label style={{ display:'block',fontSize:13,fontWeight:600,color:'#374151',marginBottom:6 }}>Email</label>
          <input value={email} onChange={e=>setEmail(e.target.value)} placeholder="Enter your email" style={authInpStyle} type="email" onKeyDown={e=>e.key==='Enter'&&handle()}/>
        </div>

        {/* Password */}
        <div style={{ marginBottom: isLogin ? 6 : 20 }}>
          <label style={{ display:'block',fontSize:13,fontWeight:600,color:'#374151',marginBottom:6 }}>Password</label>
          <div style={{ position:'relative' }}>
            <input value={password} onChange={e=>setPassword(e.target.value)} placeholder="Enter your password" style={{ ...authInpStyle, paddingRight:42 }} type={showPw?'text':'password'} onKeyDown={e=>e.key==='Enter'&&handle()}/>
            <button onClick={()=>setShowPw(p=>!p)} style={{ position:'absolute',right:12,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',padding:0,color:'#94a3b8' }}>
              {showPw
                ? <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                : <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              }
            </button>
          </div>
        </div>

        {isLogin && (
          <div style={{ textAlign:'right', marginBottom:22 }}>
            <button onClick={handleResetPassword} disabled={loading} style={{ background:'none',border:'none',color:'#2f8cff',cursor:'pointer',fontSize:13,fontWeight:600,padding:0 }}>
              Forget Password?
            </button>
          </div>
        )}

        {!isLogin && (
          <label style={{ display:'flex',alignItems:'flex-start',gap:10,marginBottom:22,cursor:'pointer' }}>
            <input type="checkbox" style={{ marginTop:2,width:15,height:15,accentColor:'#2f8cff',cursor:'pointer' }}/>
            <span style={{ fontSize:13,color:'#64748b',lineHeight:1.5 }}>
              By signing up you have agreed to our{' '}
              <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ color:'#2f8cff',fontWeight:600 }}>Terms</a>
              {' & '}
              <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color:'#2f8cff',fontWeight:600 }}>Privacy Policy</a>
            </span>
          </label>
        )}

        {error && <div style={{ background:'#fef2f2',border:'1px solid #fca5a5',borderRadius:8,padding:'10px 14px',color:'#dc2626',fontSize:13,marginBottom:14 }}>{error}</div>}
        {message && <div style={{ background:'#f0fdf4',border:'1px solid #86efac',borderRadius:8,padding:'10px 14px',color:'#16a34a',fontSize:13,marginBottom:14 }}>{message}</div>}

        <button onClick={handle} disabled={loading} style={{ width:'100%',padding:'13px',background:loading?'#94a3b8':'#111827',color:'#fff',border:'none',borderRadius:8,cursor:loading?'not-allowed':'pointer',fontSize:15,fontWeight:700,letterSpacing:'-0.2px' }}>
          {loading ? '...' : isLogin ? 'Sign in →' : 'Create Account →'}
        </button>

        <div style={{ textAlign:'center',marginTop:18,fontSize:13,color:'#64748b' }}>
          {isLogin ? (
            <>Don't have an account?{' '}<button onClick={()=>{setMode('signup');setError('');setMessage('');}} style={{ background:'none',border:'none',color:'#111827',fontWeight:700,cursor:'pointer',fontSize:13,padding:0 }}>Sign Up</button></>
          ) : (
            <>Already have an account?{' '}<button onClick={()=>{setMode('login');setError('');setMessage('');}} style={{ background:'none',border:'none',color:'#111827',fontWeight:700,cursor:'pointer',fontSize:13,padding:0 }}>Log In</button></>
          )}
        </div>

        {isLogin && (
          <div style={{ display:'flex',justifyContent:'center',gap:16,marginTop:12,flexWrap:'wrap' }}>
            <button onClick={handleResendConfirmation} disabled={loading} style={{ background:'none',border:'none',color:'#94a3b8',cursor:'pointer',fontSize:12,padding:0 }}>Resend Confirmation</button>
            <button onClick={handleMagicLink} disabled={loading} style={{ background:'none',border:'none',color:'#94a3b8',cursor:'pointer',fontSize:12,padding:0 }}>Email Sign-In Link</button>
          </div>
        )}

        <div style={{ textAlign:'center',marginTop:20,fontSize:12,color:'#94a3b8' }}>
          Need Help? <a href="mailto:support@tradournal.com" style={{ color:'#111827',fontWeight:600 }}>support@tradournal.com</a>
        </div>
      </div>

      {/* ── Right panel ── */}
      <RightPanel/>
    </div>
  );
}

function PasswordRecoveryScreen({ onComplete, onCancel }) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = async () => {
    if (!password || !confirmPassword) {
      setError('Enter your new password in both fields.');
      return;
    }
    if (password.length < 8) {
      setError('Use at least 8 characters for your new password.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true); setError(''); setMessage('');
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      setMessage('Password updated successfully. You can continue into your journal now.');
      setTimeout(() => onComplete(), 700);
    } catch (err) {
      setError(err?.message || 'Unable to update password right now.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh', background:C.bg, fontFamily:C.font, padding:'24px' }}>
      <div style={{ ...cardStyle, padding:'40px', width:'min(420px, 100%)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'28px', justifyContent:'center' }}>
          <div style={{ width:'44px', height:'44px', borderRadius:'10px', background:'linear-gradient(135deg,#388bfd,#8957e5)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'22px' }}>📊</div>
          <div>
            <div style={{ color:C.text, fontWeight:'800', fontSize:'20px' }}>Tradournal</div>
            <div style={{ color:C.sub, fontSize:'11px' }}>Reset your password</div>
          </div>
        </div>
        <div style={{ color:C.sub, fontSize:'13px', lineHeight:1.6, marginBottom:'18px' }}>
          Create a new password for your account, then continue back into the app.
        </div>
        <div style={{ marginBottom:'14px' }}>
          <div style={{ color:C.sub, fontSize:'10px', marginBottom:'5px', fontWeight:'700', textTransform:'uppercase' }}>New Password</div>
          <input value={password} onChange={e=>setPassword(e.target.value)} placeholder="At least 8 characters" style={inp} type="password" onKeyDown={e=>e.key==='Enter'&&handleSubmit()} />
        </div>
        <div style={{ marginBottom:'22px' }}>
          <div style={{ color:C.sub, fontSize:'10px', marginBottom:'5px', fontWeight:'700', textTransform:'uppercase' }}>Confirm Password</div>
          <input value={confirmPassword} onChange={e=>setConfirmPassword(e.target.value)} placeholder="Repeat password" style={inp} type="password" onKeyDown={e=>e.key==='Enter'&&handleSubmit()} />
        </div>
        {error && <div style={{ background:'rgba(218,54,51,0.1)', border:'1px solid #da363355', borderRadius:'6px', padding:'10px 14px', color:C.redBright, fontSize:'12px', marginBottom:'14px' }}>{error}</div>}
        {message && <div style={{ background:'rgba(46,160,67,0.1)', border:'1px solid #2ea04355', borderRadius:'6px', padding:'10px 14px', color:C.greenBright, fontSize:'12px', marginBottom:'14px' }}>{message}</div>}
        <button onClick={handleSubmit} disabled={loading}
          style={{ width:'100%', padding:'12px', background: loading ? C.card2 : C.blue, color: loading ? C.sub : '#fff', border:'none', borderRadius:'8px', cursor: loading ? 'not-allowed' : 'pointer', fontSize:'14px', fontWeight:'800' }}>
          {loading ? 'Updating...' : 'Save New Password'}
        </button>
        <button onClick={onCancel} disabled={loading}
          style={{ width:'100%', padding:'11px', marginTop:'10px', background:'transparent', color:C.sub, border:`1px solid ${C.border}`, borderRadius:'8px', cursor: loading ? 'not-allowed' : 'pointer', fontSize:'13px', fontWeight:'700' }}>
          Back to Login
        </button>
      </div>
    </div>
  );
}

function DonutChart({ value, max, color, size=80, thickness=10 }) {
  const r=(size-thickness)/2, circ=2*Math.PI*r, dash=Math.min(value/max,1)*circ;
  return <svg width={size} height={size} style={{transform:'rotate(-90deg)'}}><circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#21262d" strokeWidth={thickness}/><circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={thickness} strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"/></svg>;
}

function Sparkline({ data, color }) {
  if (!data||data.length<2) return null;
  const max=Math.max(...data),min=Math.min(...data),range=max-min||1,w=60,h=30;
  const pts=data.map((v,i)=>`${(i/(data.length-1))*w},${h-((v-min)/range)*h}`).join(' ');
  return <svg width={w} height={h}><polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>;
}

function TickerBar({ trades, themeMode }) {
  const rootRef = useRef(null);
  const mapTickerSymbol = (assetRaw) => {
    const asset = (assetRaw || '').trim().toUpperCase();
    if (!asset) return null;
    if (asset.startsWith('BINANCE:') || asset.startsWith('NASDAQ:') || asset.startsWith('NYSE:')) return asset;
    if (asset === 'BTC' || asset === 'BTCUSD') return 'BINANCE:BTCUSDT';
    if (asset === 'ETH' || asset === 'ETHUSD') return 'BINANCE:ETHUSDT';
    if (/^[A-Z]{1,5}$/.test(asset)) return `NASDAQ:${asset}`;
    return null;
  };
  const symbols = React.useMemo(() => {
    const liveSymbols = trades.length > 0
      ? [...new Set(trades.map((t) => t.asset))]
          .map((asset) => mapTickerSymbol(asset))
          .filter(Boolean)
          .slice(0, 12)
      : [];
    const fallbackSymbols = [
      'NASDAQ:AAPL',
      'NASDAQ:MSFT',
      'NASDAQ:NVDA',
      'NASDAQ:TSLA',
      'NASDAQ:AMZN',
      'BINANCE:BTCUSDT',
      'BINANCE:ETHUSDT',
    ];
    return (liveSymbols.length ? liveSymbols : fallbackSymbols).map((s) => ({ proName: s, title: s.split(':')[1] }));
  }, [trades]);

  useEffect(() => {
    const host = rootRef.current;
    if (!host) return undefined;
    host.innerHTML = '';
    const container = document.createElement('div');
    container.className = 'tradingview-widget-container';
    container.style.height = '34px';
    container.style.width = '100%';
    const widget = document.createElement('div');
    widget.className = 'tradingview-widget-container__widget';
    widget.style.height = '100%';
    widget.style.width = '100%';
    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-ticker-tape.js';
    script.async = true;
    script.innerHTML = JSON.stringify({
      symbols,
      showSymbolLogo: false,
      isTransparent: true,
      displayMode: 'adaptive',
      colorTheme: themeMode === 'day' ? 'light' : 'dark',
      locale: 'en',
    });
    container.appendChild(widget);
    container.appendChild(script);
    host.appendChild(container);
    return () => {
      host.innerHTML = '';
    };
  }, [symbols, themeMode]);

  return (
    <div style={{ background:C.card2, borderBottom:`1px solid ${C.border}`, padding:'4px 10px', minHeight:'42px', display:'flex', alignItems:'center' }}>
      <div ref={rootRef} style={{ width:'100%' }} />
    </div>
  );
}

function Sidebar({ activeTab, setActiveTab, accounts, activeAccount, setActiveAccount, onSignOut, themeMode, onManageSub, subStatus }) {
  const [showAllNav, setShowAllNav] = useState(false);
  const NavIcon = ({ kind, active, color }) => {
    const iconColor = color || (active ? C.blue : C.sub);
    const shell = { width:'16px', height:'16px', display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0 };
    const svgCommon = { width:'15', height:'15', viewBox:'0 0 24 24', fill:'none', stroke:iconColor, strokeWidth:'1.8', strokeLinecap:'round', strokeLinejoin:'round' };
    if (kind === 'overview') return <span style={shell}><svg {...svgCommon}><rect x="3" y="3" width="8" height="8"/><rect x="13" y="3" width="8" height="5"/><rect x="13" y="10" width="8" height="11"/><rect x="3" y="13" width="8" height="8"/></svg></span>;
    if (kind === 'insights') return <span style={shell}><svg {...svgCommon}><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="2"/><path d="M12 7v2"/><path d="M12 15v2"/><path d="M7 12h2"/><path d="M15 12h2"/></svg></span>;
    if (kind === 'trades') return <span style={shell}><svg {...svgCommon}><path d="M4 19h16"/><path d="M7 15l3-3 3 2 4-5"/></svg></span>;
    if (kind === 'calendar') return <span style={shell}><svg {...svgCommon}><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M8 3v4"/><path d="M16 3v4"/><path d="M3 10h18"/></svg></span>;
    if (kind === 'replay') return <span style={shell}><svg {...svgCommon}><circle cx="12" cy="12" r="9"/><path d="M10 8l6 4-6 4z"/></svg></span>;
    if (kind === 'live') return <span style={shell}><svg {...svgCommon}><circle cx="12" cy="12" r="7"/><path d="M5 12h2"/><path d="M17 12h2"/><path d="M12 5v2"/><path d="M12 17v2"/></svg></span>;
    if (kind === 'scanner') return <span style={shell}><svg {...svgCommon}><circle cx="10" cy="10" r="6"/><path d="M20 20l-4.35-4.35"/><path d="M7 10h6"/><path d="M10 7v6"/></svg></span>;
    if (kind === 'reports') return <span style={shell}><svg {...svgCommon}><path d="M4 19V5"/><path d="M4 19h16"/><path d="M8 15l3-3 3 2 4-6"/></svg></span>;
    if (kind === 'coach') return <span style={shell}><svg {...svgCommon}><path d="M12 3l2.6 5.2L20 9l-4 3.8 1 5.2-5-2.8-5 2.8 1-5.2L4 9l5.4-.8z"/></svg></span>;
    if (kind === 'import') return <span style={shell}><svg {...svgCommon}><path d="M12 3v12"/><path d="M8 11l4 4 4-4"/><rect x="4" y="17" width="16" height="4" rx="1"/></svg></span>;
    if (kind === 'futures') return <span style={shell}><svg {...svgCommon}><rect x="2" y="7" width="4" height="10"/><rect x="10" y="4" width="4" height="13"/><rect x="18" y="2" width="4" height="15"/><path d="M2 20h20"/></svg></span>;
    if (kind === 'maalerts') return <span style={shell}><svg {...svgCommon}><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/><line x1="12" y1="3" x2="12" y2="1"/></svg></span>;
    if (kind === 'news') return <span style={shell}><svg {...svgCommon}><path d="M4 4h16v12H4z"/><path d="M4 8h16"/><path d="M8 12h4"/><path d="M8 15h8"/></svg></span>;
    if (kind === 'ai') return <span style={shell}><svg {...svgCommon}><circle cx="12" cy="12" r="3"/><path d="M12 5v2"/><path d="M12 17v2"/><path d="M5 12h2"/><path d="M17 12h2"/><path d="M7.05 7.05l1.41 1.41"/><path d="M15.54 15.54l1.41 1.41"/><path d="M7.05 16.95l1.41-1.41"/><path d="M15.54 8.46l1.41-1.41"/></svg></span>;
    return <span style={shell}><svg {...svgCommon}><rect x="4" y="4" width="16" height="16" rx="3"/><path d="M8 12h8"/></svg></span>;
  };
  const collapsibleSections = [
    {label:'Sections',     icon:'trades',  color:'#a78bfa', items:[{tab:'trades',icon:'trades',label:'Trade Log'},{tab:'calendar',icon:'calendar',label:'Calendar'},{tab:'briefing',icon:'calendar',label:'Daily Briefing'},{tab:'insights',icon:'insights',label:'Insights'},{tab:'replay',icon:'replay',label:'Replay'}]},
    {label:'Analyze',      icon:'reports', color:'#34d399', items:[{tab:'reports',icon:'reports',label:'Reports'},{tab:'coach',icon:'coach',label:'Premium Coach'}]},
    {label:'Tools',        icon:'live',    color:'#f59e0b', items:[{tab:'live',icon:'live',label:'Live Session'},{tab:'watchlist',icon:'scanner',label:'Watchlist & Alerts'},{tab:'futures',icon:'futures',label:'Futures'},{tab:'maalerts',icon:'maalerts',label:'MA Alerts'},{tab:'premarket',icon:'scanner',label:'Pre-Market'},{tab:'scanner',icon:'scanner',label:'Gap Scanner'},{tab:'news',icon:'news',label:'Market News'},{tab:'import',icon:'import',label:'Import'}]},
    {label:'Miscellaneous',icon:'ai',      color:'#f472b6', items:[{tab:'ai',icon:'ai',label:'AI Assistant'},{tab:'accounts',icon:'accounts',label:'Accounts'},{tab:'settings',icon:'accounts',label:'Settings'}]},
  ];
  const [openSections, setOpenSections] = React.useState(()=>new Set(['Sections','Analyze','Tools','Miscellaneous']));
  const toggleSection = (label) => setOpenSections(prev => {
    const next = new Set(prev);
    next.has(label) ? next.delete(label) : next.add(label);
    return next;
  });
  const NavItem = ({item}) => (
    <div onClick={()=>setActiveTab(item.tab)} style={{display:'flex',alignItems:'center',gap:'10px',padding:'7px 10px 7px 22px',borderRadius:'6px',cursor:'pointer',background:activeTab===item.tab?C.card2:'transparent',color:activeTab===item.tab?C.text:C.sub,fontSize:'13px',fontWeight:activeTab===item.tab?'600':'400',borderLeft:activeTab===item.tab?`2px solid ${C.blue}`:'2px solid transparent',marginBottom:'1px'}}>
      <NavIcon kind={item.icon} active={activeTab===item.tab}/><span>{item.label}</span>
    </div>
  );
  return (
    <div style={{width:'212px',background:`linear-gradient(180deg,${C.sidebar},${C.bg})`,borderRight:`1px solid ${C.border}`,display:'flex',flexDirection:'column',flexShrink:0,boxShadow:'inset -1px 0 0 rgba(255,255,255,0.03)'}}>
      <div style={{padding:'20px 16px',borderBottom:`1px solid ${C.border}`}}>
        <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
          <TradournalLogo size={36} themeMode={themeMode}/>
          <div><div style={{color:C.text,fontWeight:'800',fontSize:'15px',letterSpacing:'-0.3px'}}>Tradournal</div><div style={{color:C.sub,fontSize:'10px'}}>Trading Journal</div></div>
        </div>
      </div>
      <div style={{padding:'10px 12px',borderBottom:`1px solid ${C.border}`}}>
        <select value={activeAccount} onChange={e=>setActiveAccount(e.target.value)} style={{...inp,fontSize:'12px',padding:'7px 10px',background:C.card2}}>
          {accounts.map(a=><option key={a.id} value={a.name}>{a.name}</option>)}
        </select>
      </div>
      <div style={{flex:1,overflowY:'auto',padding:'10px 8px'}}>
        {/* Dashboard — standalone */}
        <div style={{marginBottom:'6px'}}>
          <div onClick={()=>setActiveTab('dashboard')} style={{display:'flex',alignItems:'center',gap:'10px',padding:'8px 10px',borderRadius:'6px',cursor:'pointer',background:activeTab==='dashboard'?C.card2:'transparent',color:activeTab==='dashboard'?C.text:C.sub,fontSize:'13px',fontWeight:activeTab==='dashboard'?'700':'500',borderLeft:activeTab==='dashboard'?`2px solid ${C.blue}`:'2px solid transparent',marginBottom:'1px'}}>
            <NavIcon kind='overview' active={activeTab==='dashboard'}/><span>Dashboard</span>
          </div>
        </div>
        {/* Collapsible sections */}
        {collapsibleSections.map(section=>{
          const isOpen = openSections.has(section.label);
          const hasActive = section.items.some(i=>i.tab===activeTab);
          const sc = section.color;
          return (
            <div key={section.label} style={{marginBottom:'3px'}}>
              <div onClick={()=>toggleSection(section.label)} style={{display:'flex',alignItems:'center',gap:'9px',padding:'8px 10px',borderRadius:'8px',cursor:'pointer',userSelect:'none',background:hasActive?`${sc}18`:isOpen?`${sc}08`:'transparent',border:`1px solid ${hasActive?sc+'44':isOpen?sc+'22':'transparent'}`,transition:'all 0.15s'}}>
                {/* Colored icon container */}
                <div style={{width:24,height:24,borderRadius:6,background:`${sc}20`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <NavIcon kind={section.icon} active={true} color={sc}/>
                </div>
                <span style={{flex:1,color:hasActive?sc:isOpen?sc+'cc':C.sub,fontSize:'13px',fontWeight:'700'}}>{section.label}</span>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={isOpen?sc:C.sub} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{transition:'transform 0.2s',transform:isOpen?'rotate(180deg)':'rotate(0deg)',flexShrink:0}}>
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </div>
              {isOpen && (
                <div style={{marginTop:'2px',marginBottom:'6px',borderLeft:`2px solid ${sc}33`,marginLeft:'18px',paddingLeft:'8px'}}>
                  {section.items.map(item=>(
                    <div key={item.tab} onClick={()=>setActiveTab(item.tab)} style={{display:'flex',alignItems:'center',gap:'9px',padding:'7px 8px',borderRadius:'6px',cursor:'pointer',background:activeTab===item.tab?`${sc}15`:'transparent',color:activeTab===item.tab?sc:C.sub,fontSize:'12.5px',fontWeight:activeTab===item.tab?'700':'400',marginBottom:'1px',transition:'all 0.1s'}}>
                      <NavIcon kind={item.icon} active={activeTab===item.tab} color={activeTab===item.tab?sc:undefined}/>
                      <span>{item.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div style={{padding:'12px',borderTop:`1px solid ${C.border}`}}>
        <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'10px'}}>
          <div style={{width:'30px',height:'30px',borderRadius:'50%',background:'linear-gradient(135deg,#388bfd,#8957e5)',display:'flex',alignItems:'center',justifyContent:'center'}}>
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
</div>
          <div>
            <div style={{color:C.text,fontSize:'12px',fontWeight:'600'}}>Trader</div>
            <div style={{color: subStatus === 'trialing' ? C.orange : C.green, fontSize:'10px'}}>{subStatus === 'trialing' ? 'Trial Active' : 'Pro'}</div>
          </div>
        </div>
        {onManageSub && <button onClick={onManageSub} style={{width:'100%',padding:'6px',background:'transparent',color:C.sub,border:`1px solid ${C.border}`,borderRadius:'6px',cursor:'pointer',fontSize:'10px',fontWeight:'600',marginBottom:'6px'}}>Manage Subscription</button>}
        <button onClick={onSignOut} style={{width:'100%',padding:'7px',background:C.card2,color:C.sub,border:`1px solid ${C.border}`,borderRadius:'6px',cursor:'pointer',fontSize:'11px',fontWeight:'600',display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
  Sign Out
</button>
      </div>
    </div>
  );
}

function ProfitCalendar({ trades, themeMode }) {
  const today = new Date(); today.setHours(0,0,0,0);
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState(null);
  const lastAutoFocusedTradeRef = useRef('');

  const latestTradeMeta = useMemo(() => trades.reduce((latest, trade, index) => {
    const parsed = parseTradeDate(trade.date);
    if (Number.isNaN(parsed.getTime())) return latest;
    parsed.setHours(0,0,0,0);
    if (!latest || parsed > latest.date) {
      return {
        date: parsed,
        signature: `${parsed.getTime()}:${trade.id || index}:${trade.asset || ''}:${trade.pnl || ''}`,
      };
    }
    return latest;
  }, null), [trades]);
  const latestTradeDate = latestTradeMeta?.date || null;

  useEffect(() => {
    if (!latestTradeDate) {
      lastAutoFocusedTradeRef.current = '';
      setSelectedDate(null);
      return;
    }

    const latestKey = latestTradeMeta?.signature || `${latestTradeDate.getTime()}:${trades.length}`;
    if (lastAutoFocusedTradeRef.current !== latestKey) {
      lastAutoFocusedTradeRef.current = latestKey;
      setViewMonth(latestTradeDate.getMonth());
      setViewYear(latestTradeDate.getFullYear());
      setSelectedDate(new Date(latestTradeDate));
      return;
    }

    setSelectedDate((prev) => {
      if (!prev) return prev;
      const stillExists = trades.some((trade) => {
        const parsed = parseTradeDate(trade.date);
        return !Number.isNaN(parsed.getTime())
          && parsed.getDate() === prev.getDate()
          && parsed.getMonth() === prev.getMonth()
          && parsed.getFullYear() === prev.getFullYear();
      });
      return stillExists ? prev : null;
    });
  }, [trades, latestTradeDate, latestTradeMeta]);

  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

  const dailyPnlIso = {};
  const dailyCountIso = {};
  const dailyTradesIso = {};
  trades.forEach(t => {
    const d = parseTradeDate(t.date);
    if (!d || isNaN(d.getTime())) return;
    d.setHours(0,0,0,0);
    const iso = toIso(d);
    dailyPnlIso[iso] = (dailyPnlIso[iso] || 0) + toNumber(t.pnl);
    dailyCountIso[iso] = (dailyCountIso[iso] || 0) + 1;
    if (!dailyTradesIso[iso]) dailyTradesIso[iso] = [];
    dailyTradesIso[iso].push(t);
  });
  const maxAbsDailyPnl = Math.max(1, ...Object.values(dailyPnlIso).map((value) => Math.abs(value)));

  const getDayPnl = (d) => dailyPnlIso[toIso(d)];
  const getDayCount = (d) => dailyCountIso[toIso(d)] || 0;

  const startOfWeekFor = (d) => {
    const s = new Date(d); s.setHours(0,0,0,0);
    const mondayIndex = (s.getDay() + 6) % 7;
    s.setDate(s.getDate() - mondayIndex);
    return s;
  };
  const sumRange = (start, endExclusive) => {
    let total = 0, tradeDays = 0;
    const d = new Date(start);
    while (d < endExclusive) {
      const v = getDayPnl(d);
      if (v !== undefined) { total += v; tradeDays++; }
      d.setDate(d.getDate() + 1);
    }
    return { total, tradeDays };
  };

  const viewedMonthStart = new Date(viewYear, viewMonth, 1);
  const viewedMonthEnd   = new Date(viewYear, viewMonth + 1, 1);
  const viewedYearStart  = new Date(viewYear, 0, 1);
  const viewedYearEnd    = new Date(viewYear + 1, 0, 1);
  const viewedMonth = sumRange(viewedMonthStart, viewedMonthEnd);
  const viewedYear  = sumRange(viewedYearStart,  viewedYearEnd);

  const weekAnchor = selectedDate || new Date(viewYear, viewMonth, 1);
  const viewedWeekStart = startOfWeekFor(weekAnchor);
  const viewedWeekEnd   = new Date(viewedWeekStart);
  viewedWeekEnd.setDate(viewedWeekStart.getDate() + 7);
  const viewedWeek = sumRange(viewedWeekStart, viewedWeekEnd);

  const selectedDayTrades = selectedDate ? (dailyTradesIso[toIso(selectedDate)] || []) : [];
  const selectedDayPnl    = selectedDate ? getDayPnl(selectedDate) : undefined;

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); } else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); } else setViewMonth(m => m + 1);
  };
  const goToday = () => { setViewMonth(today.getMonth()); setViewYear(today.getFullYear()); setSelectedDate(null); };

  const monthPnl = viewedMonth.total;

  // Sunday-first grid
  const sunFirstDay = new Date(viewYear, viewMonth, 1).getDay();
  const gridDays = [];
  for (let i = 0; i < sunFirstDay; i++) gridDays.push(null);
  for (let i = 1; i <= daysInMonth; i++) gridDays.push(i);
  while (gridDays.length % 7 !== 0) gridDays.push(null);

  // Split into week rows
  const weeks = [];
  for (let i = 0; i < gridDays.length; i += 7) weeks.push(gridDays.slice(i, i + 7));

  const getWeekStats = (weekDays) => {
    let total = 0, tradeDays = 0;
    weekDays.forEach(day => {
      if (!day) return;
      const v = getDayPnl(new Date(viewYear, viewMonth, day));
      if (v !== undefined) { total += v; tradeDays++; }
    });
    return { total, tradeDays };
  };

  const fmtPnl = (v) => {
    const abs = Math.abs(v);
    const sign = v < 0 ? '-' : '';
    if (abs >= 1000000) return `${sign}$${(abs / 1000000).toFixed(1)}M`;
    if (abs >= 100000)  return `${sign}$${(abs / 1000).toFixed(0)}K`;
    if (abs >= 10000)   return `${sign}$${(abs / 1000).toFixed(1)}K`;
    if (abs >= 1000)    return `${sign}$${(abs / 1000).toFixed(2)}K`;
    return `${sign}$${abs.toFixed(2)}`;
  };

  const fmtPnlSigned = (v) => (v >= 0 ? '' : '-') + fmtPnl(v).replace(/^-/, '');

  // Solid cell colors matching the screenshot
  const CAL_GREEN_BG     = themeMode === 'day' ? '#c8edd8' : '#1a4030';
  const CAL_GREEN_BORDER = themeMode === 'day' ? '#7dcca0' : '#265c40';
  const CAL_RED_BG       = themeMode === 'day' ? '#f0cccc' : '#4a1818';
  const CAL_RED_BORDER   = themeMode === 'day' ? '#cc7d7d' : '#7a2828';
  const CELL_BORDER      = themeMode === 'day' ? C.border : '#1c2d3f';
  const HEADER_BG        = themeMode === 'day' ? C.card2 : '#0b1522';

  const btnBase = {
    border: `1px solid ${C.border}`, borderRadius: 8, background: C.card2,
    cursor: 'pointer', color: C.text, fontWeight: 700, fontSize: 13,
  };

  return (
    <div style={{ fontFamily: C.font, paddingBottom: 28 }}>

      {/* ── Header bar ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={prevMonth} style={{ ...btnBase, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>‹</button>
          <span style={{ color: C.text, fontSize: 18, fontWeight: 800, minWidth: 160, textAlign: 'center' }}>{monthNames[viewMonth]} {viewYear}</span>
          <button onClick={nextMonth} style={{ ...btnBase, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>›</button>
          <button onClick={goToday} style={{ ...btnBase, padding: '6px 16px', marginLeft: 6, fontSize: 12 }}>This month</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ color: C.sub, fontSize: 13, fontWeight: 600 }}>Monthly stats:</span>
          <span style={{
            background: viewedMonth.tradeDays === 0 ? C.card2 : monthPnl >= 0 ? C.green : C.red,
            color: '#fff', fontWeight: 900, fontSize: 14, borderRadius: 20, padding: '5px 16px',
            letterSpacing: '-0.3px',
          }}>
            {viewedMonth.tradeDays === 0 ? '$0' : fmtPnlSigned(monthPnl)}
          </span>
          <span style={{ color: C.sub, fontSize: 13, fontWeight: 600 }}>{viewedMonth.tradeDays} day{viewedMonth.tradeDays !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* ── Calendar ── */}
      <div style={{ ...cardStyle, padding: 0, overflow: 'hidden', border: `1px solid ${CELL_BORDER}` }}>
        {/* Day headers row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr) 148px', background: HEADER_BG, borderBottom: `1px solid ${CELL_BORDER}` }}>
          {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
            <div key={d} style={{ textAlign: 'center', padding: '11px 0', color: C.sub, fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', borderRight: `1px solid ${CELL_BORDER}` }}>{d}</div>
          ))}
          <div style={{ padding: '11px 0', textAlign: 'center', color: C.sub, fontSize: 11, fontWeight: 700, letterSpacing: '0.6px' }}></div>
        </div>

        {/* Week rows */}
        {weeks.map((weekDays, wi) => {
          const ws = getWeekStats(weekDays);
          return (
            <div key={wi} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr) 148px', borderBottom: wi < weeks.length - 1 ? `1px solid ${CELL_BORDER}` : 'none' }}>
              {weekDays.map((day, di) => {
                if (!day) return (
                  <div key={`e-${wi}-${di}`} style={{ minHeight: 100, borderRight: `1px solid ${CELL_BORDER}`, background: themeMode === 'day' ? C.bg : 'transparent' }} />
                );
                const cellDate = new Date(viewYear, viewMonth, day);
                const pnl = getDayPnl(cellDate);
                const count = getDayCount(cellDate);
                const hasTrades = pnl !== undefined;
                const isToday = day === today.getDate() && viewMonth === today.getMonth() && viewYear === today.getFullYear();
                const isSelected = !!selectedDate && selectedDate.getDate() === day && selectedDate.getMonth() === viewMonth && selectedDate.getFullYear() === viewYear;

                let bg = 'transparent';
                let borderL = `1px solid ${CELL_BORDER}`;
                if (hasTrades && pnl >= 0) { bg = CAL_GREEN_BG; }
                if (hasTrades && pnl < 0)  { bg = CAL_RED_BG;   }
                if (isSelected) bg = 'rgba(56,139,253,0.18)';

                return (
                  <div key={day}
                    onClick={() => setSelectedDate(isSelected ? null : cellDate)}
                    style={{ background: bg, borderRight: `1px solid ${CELL_BORDER}`, minHeight: 100, padding: '8px 10px', cursor: 'pointer', position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', outline: isSelected ? `2px solid ${C.blue}` : 'none', outlineOffset: -2 }}>
                    {/* Day number — top right */}
                    <div style={{ position: 'absolute', top: 8, right: 9 }}>
                      {isToday ? (
                        <span style={{ background: '#6b5dd3', color: '#fff', width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800 }}>{day}</span>
                      ) : (
                        <span style={{ color: C.sub, fontSize: 11, fontWeight: 600 }}>{day}</span>
                      )}
                    </div>
                    {/* P&L + trade count centered */}
                    {hasTrades && (
                      <>
                        <div style={{ color: themeMode === 'day' ? (pnl >= 0 ? '#0a6b40' : '#8b1c1c') : '#ffffff', fontSize: 15, fontWeight: 800, letterSpacing: '-0.3px' }}>
                          {fmtPnlSigned(pnl)}
                        </div>
                        <div style={{ color: themeMode === 'day' ? (pnl >= 0 ? '#3a9e72' : '#b55555') : 'rgba(255,255,255,0.5)', fontSize: 11, marginTop: 3, fontWeight: 500 }}>
                          {count} trade{count !== 1 ? 's' : ''}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}

              {/* Week summary card */}
              <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 3, background: themeMode === 'day' ? C.card2 : '#0b1420' }}>
                <div style={{ color: C.sub, fontSize: 11, fontWeight: 700 }}>Week {wi + 1}</div>
                <div style={{ color: ws.tradeDays > 0 ? (ws.total >= 0 ? C.greenBright : C.redBright) : C.sub, fontSize: 19, fontWeight: 900, letterSpacing: '-0.5px' }}>
                  {ws.tradeDays > 0 ? fmtPnlSigned(ws.total) : '$0'}
                </div>
                <div style={{ color: C.sub, fontSize: 11 }}>{ws.tradeDays} day{ws.tradeDays !== 1 ? 's' : ''}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Selected day detail ── */}
      {selectedDate && (
        <div style={{ marginTop: 14, ...cardStyle, padding: '16px 20px' }}>
          {/* Day header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>
              {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              {selectedDayTrades.length > 0 && (
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: C.sub, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Commission</div>
                  <div style={{ color: C.orange, fontSize: 13, fontWeight: 800 }}>
                    -${selectedDayTrades.reduce((s, t) => s + toNumber(t.commission), 0).toFixed(2)}
                  </div>
                </div>
              )}
              <div style={{ color: selectedDayPnl === undefined ? C.sub : selectedDayPnl >= 0 ? C.greenBright : C.redBright, fontSize: 15, fontWeight: 800 }}>
                {selectedDayPnl === undefined ? 'No trades' : fmtPnlSigned(selectedDayPnl)}
              </div>
            </div>
          </div>
          {selectedDayTrades.length === 0 ? (
            <div style={{ color: C.sub, fontSize: 12 }}>No trades on this day.</div>
          ) : (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 70px 80px 80px', gap: 8, padding: '4px 0 8px', borderBottom: `1px solid ${C.border}`, marginBottom: 4 }}>
                {['Asset / Strategy','Direction','Result','Commission','P&L'].map(h => (
                  <div key={h} style={{ color: C.sub, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px' }}>{h}</div>
                ))}
              </div>
              {selectedDayTrades.map((t, idx) => {
                const p = toNumber(t.pnl);
                const comm = toNumber(t.commission);
                const outcome = p > 0 ? 'Win' : p < 0 ? 'Loss' : 'BE';
                const oc = p > 0 ? C.greenBright : p < 0 ? C.redBright : C.sub;
                return (
                  <div key={t.id || idx} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 70px 80px 80px', gap: 8, padding: '8px 0', borderBottom: idx < selectedDayTrades.length - 1 ? `1px solid ${C.border}` : 'none', fontSize: 12, alignItems: 'center' }}>
                    <div style={{ color: C.text, fontWeight: 600 }}>
                      {t.asset}<span style={{ color: C.sub, fontWeight: 400, marginLeft: 5 }}>· {t.strategy || 'No strategy'}</span>
                    </div>
                    <div style={{ color: C.sub }}>{t.type}</div>
                    <div><span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: `${oc}18`, color: oc, border: `1px solid ${oc}44` }}>{outcome}</span></div>
                    <div style={{ color: comm > 0 ? C.orange : C.sub, fontWeight: 700 }}>{comm > 0 ? `-$${comm.toFixed(2)}` : '—'}</div>
                    <div style={{ color: oc, fontWeight: 800 }}>{p >= 0 ? '+' : ''}${p.toFixed(2)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AdvancedCharts({ trades }) {
  const POS_BRIGHT = '#24f08a';
  const NEG_BRIGHT = '#ff5e7a';
  const parseTradeDate = (raw) => {
    const parts = raw ? raw.split('/') : [];
    if (parts.length === 3) return new Date(parts[2], parts[0] - 1, parts[1]);
    return new Date(raw);
  };
  const keyForDate = (date) => `${date.getFullYear()}-${(date.getMonth()+1).toString().padStart(2,'0')}-${date.getDate().toString().padStart(2,'0')}`;
  const dayName = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const dailyPnlMap = {};
  let maxAbsPnl = 0;
  trades.forEach((t) => {
    const d = parseTradeDate(t.date);
    if (Number.isNaN(d.getTime())) return;
    d.setHours(0, 0, 0, 0);
    const k = keyForDate(d);
    const pnl = toNumber(t.pnl);
    dailyPnlMap[k] = (dailyPnlMap[k] || 0) + pnl;
  });
  Object.values(dailyPnlMap).forEach((v) => { maxAbsPnl = Math.max(maxAbsPnl, Math.abs(v)); });
  const totalWinningDays = Object.values(dailyPnlMap).filter((v) => v > 0).length;
  const totalLosingDays = Object.values(dailyPnlMap).filter((v) => v < 0).length;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tradeDates = Object.keys(dailyPnlMap)
    .map((k) => new Date(k))
    .filter((d) => !Number.isNaN(d.getTime()))
    .sort((a, b) => a - b);
  const latestTradeDate = tradeDates.length ? new Date(tradeDates[tradeDates.length - 1]) : new Date(today);
  latestTradeDate.setHours(0, 0, 0, 0);
  const [rangeMonths, setRangeMonths] = useState(3);
  const [viewMonthKey, setViewMonthKey] = useState(() => `${latestTradeDate.getFullYear()}-${(latestTradeDate.getMonth()+1).toString().padStart(2,'0')}-01`);
  const viewMonthStart = new Date(viewMonthKey);
  const startDate = new Date(viewMonthStart.getFullYear(), viewMonthStart.getMonth(), 1);
  const endExclusive = new Date(viewMonthStart.getFullYear(), viewMonthStart.getMonth() + rangeMonths, 1);
  const rangeEnd = new Date(endExclusive);
  rangeEnd.setDate(rangeEnd.getDate() - 1);
  const firstGridDate = new Date(startDate);
  firstGridDate.setDate(startDate.getDate() - startDate.getDay());
  const lastGridDate = new Date(rangeEnd);
  lastGridDate.setDate(rangeEnd.getDate() + (6 - rangeEnd.getDay()));
  const totalDays = Math.floor((lastGridDate - firstGridDate) / 86400000) + 1;
  const totalWeeks = Math.ceil(totalDays / 7);
  const [selectedKey, setSelectedKey] = useState(keyForDate(today));
  const weeks = [];
  const monthLabels = {};

  for (let w = 0; w < totalWeeks; w++) {
    const week = [];
    for (let dIdx = 0; dIdx < 7; dIdx++) {
      const index = (w * 7) + dIdx;
      const d = new Date(firstGridDate);
      d.setDate(firstGridDate.getDate() + index);
      const key = keyForDate(d);
      const pnl = dailyPnlMap[key];
      const inRange = d >= startDate && d <= rangeEnd;
      const isToday = key === keyForDate(rangeEnd);
      const intensity = pnl ? Math.min(Math.abs(pnl) / (maxAbsPnl || 1), 1) : 0;
      const bg = !inRange
        ? 'transparent'
        : pnl > 0
          ? `rgba(36,240,138,${0.2 + intensity * 0.7})`
          : pnl < 0
            ? `rgba(255,94,122,${0.2 + intensity * 0.7})`
            : C.card2;
      const border = isToday ? C.blue : (pnl > 0 ? '#24f08a66' : pnl < 0 ? '#ff5e7a66' : C.border);
      if (d.getDate() === 1 || (w === 0 && dIdx === 0)) monthLabels[w] = `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
      week.push({ key, pnl, inRange, label: d.toDateString(), bg, border, dayNum: d.getDate(), isToday });
    }
    weeks.push(week);
  }

  const selectedPnl = dailyPnlMap[selectedKey];
  const selectedDateObj = new Date(selectedKey);
  const monthAnchors = [];
  const seenMonths = new Set();
  weeks.flat().forEach((cell) => {
    if (!cell.inRange) return;
    const d = new Date(cell.key);
    const mKey = `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}`;
    if (seenMonths.has(mKey)) return;
    seenMonths.add(mKey);
    monthAnchors.push({ label: `${monthNames[d.getMonth()]} ${d.getFullYear()}`, key: cell.key, monthKey: mKey });
  });
  const organizedMonthAnchors = monthAnchors;
  const selectedMonthKey = Number.isNaN(selectedDateObj.getTime())
    ? ''
    : `${selectedDateObj.getFullYear()}-${(selectedDateObj.getMonth() + 1).toString().padStart(2, '0')}`;
  const monthStats = Object.entries(dailyPnlMap).reduce((acc, [key, pnl]) => {
    const monthKey = key.slice(0, 7);
    if (!acc[monthKey]) acc[monthKey] = { pnl: 0, green: 0, red: 0, trades: 0 };
    acc[monthKey].pnl += pnl;
    acc[monthKey].trades += 1;
    if (pnl > 0) acc[monthKey].green += 1;
    if (pnl < 0) acc[monthKey].red += 1;
    return acc;
  }, {});
  const selectedMonthStatRaw = monthStats[selectedMonthKey] || { pnl: 0, green: 0, red: 0, trades: 0 };
  const selectedMonthLabel = Number.isNaN(selectedDateObj.getTime())
    ? `${monthNames[startDate.getMonth()]} ${startDate.getFullYear()}`
    : `${monthNames[selectedDateObj.getMonth()]} ${selectedDateObj.getFullYear()}`;
  const weekDayStats = dayName.map((day, idx) => {
    let pnl = 0;
    let days = 0;
    Object.entries(dailyPnlMap).forEach(([key, value]) => {
      const d = new Date(key);
      if (!Number.isNaN(d.getTime()) && d.getDay() === idx) {
        pnl += value;
        days += 1;
      }
    });
    return { day, pnl, days };
  });
  const bestDay = [...weekDayStats].sort((a, b) => b.pnl - a.pnl)[0];
  const worstDay = [...weekDayStats].sort((a, b) => a.pnl - b.pnl)[0];

  return (
    <div style={{...cardStyle,padding:'20px',marginBottom:'16px'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:'10px',flexWrap:'wrap',marginBottom:'12px'}}>
        <div>
          <div style={{color:C.text,fontSize:'15px',fontWeight:'800',marginBottom:'4px'}}>2. Advanced Charts</div>
          <div style={{color:C.sub,fontSize:'12px'}}>More visual insights.</div>
        </div>
        <div style={{display:'flex',gap:'10px',alignItems:'center',fontSize:'12px',color:C.sub}}>
          <span style={{display:'inline-flex',alignItems:'center',gap:'6px'}}><span style={{width:'10px',height:'10px',borderRadius:'2px',background:'rgba(36,240,138,0.85)',display:'inline-block'}}/> Green = profitable day</span>
          <span style={{display:'inline-flex',alignItems:'center',gap:'6px'}}><span style={{width:'10px',height:'10px',borderRadius:'2px',background:'rgba(255,94,122,0.85)',display:'inline-block'}}/> Red = losing day</span>
        </div>
      </div>
      <div style={{color:C.sub,fontSize:'12px',marginBottom:'10px'}}>Equity Heatmap Calendar showing profit/loss days. Click any day.</div>
      <div style={{display:'flex',gap:'10px',flexWrap:'wrap',marginBottom:'10px'}}>
        <div style={{background:C.card2,border:`1px solid ${C.border}`,borderRadius:'8px',padding:'8px 12px'}}>
          <div style={{color:C.sub,fontSize:'12px',fontWeight:'700',textTransform:'uppercase',marginBottom:'2px'}}>Total Winning Days</div>
          <div style={{color:POS_BRIGHT,fontSize:'18px',fontWeight:'800'}}>{totalWinningDays}</div>
        </div>
        <div style={{background:C.card2,border:`1px solid ${C.border}`,borderRadius:'8px',padding:'8px 12px'}}>
          <div style={{color:C.sub,fontSize:'12px',fontWeight:'700',textTransform:'uppercase',marginBottom:'2px'}}>Total Losing Days</div>
          <div style={{color:NEG_BRIGHT,fontSize:'18px',fontWeight:'800'}}>{totalLosingDays}</div>
        </div>
      </div>
      <div style={{display:'flex',gap:'6px',flexWrap:'wrap',marginBottom:'10px'}}>
        <button onClick={() => {
          const d = new Date(viewMonthStart);
          d.setMonth(d.getMonth() - rangeMonths);
          setViewMonthKey(`${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-01`);
        }} style={{padding:'4px 10px',borderRadius:'12px',border:`1px solid ${C.border}`,background:C.card2,color:C.text,fontSize:'12px',fontWeight:'700',cursor:'pointer'}}>◀ Prev</button>
        <select value={rangeMonths} onChange={(e)=>setRangeMonths(parseInt(e.target.value,10))} style={{...inp,width:'96px',padding:'4px 8px',fontSize:'12px'}}>
          <option value={1}>1 Month</option>
          <option value={2}>2 Months</option>
          <option value={3}>3 Months</option>
          <option value={6}>6 Months</option>
        </select>
        <button onClick={() => {
          const d = new Date(viewMonthStart);
          d.setMonth(d.getMonth() + rangeMonths);
          setViewMonthKey(`${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-01`);
        }} style={{padding:'4px 10px',borderRadius:'12px',border:`1px solid ${C.border}`,background:C.card2,color:C.text,fontSize:'12px',fontWeight:'700',cursor:'pointer'}}>Next ▶</button>
        {organizedMonthAnchors.map((m) => {
          const mKey = m.key.slice(0, 7);
          const active = mKey === selectedMonthKey;
          return (
            <button
              key={m.key}
              onClick={() => setSelectedKey(m.key)}
              style={{
                padding:'4px 8px',
                borderRadius:'12px',
                border:`1px solid ${active ? C.blueBright : C.border}`,
                background:active?'rgba(56,139,253,0.16)':C.card2,
                color:active?C.blueBright:C.sub,
                fontSize:'12px',
                fontWeight:'700',
                cursor:'pointer',
              }}
            >
              {m.label}
            </button>
          );
        })}
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(320px, 1fr))',gap:'16px',alignItems:'start'}}>
        <div style={{overflowX:'auto',paddingBottom:'2px'}}>
          <div style={{display:'grid',gridTemplateColumns:`24px repeat(${totalWeeks}, 14px)`,gap:'4px',alignItems:'center'}}>
            <div />
            {Array.from({length: totalWeeks}).map((_, w) => (
              <div key={`m-${w}`} style={{gridRow:'1',color:C.sub,fontSize:'11px',whiteSpace:'nowrap',transform:'translateY(-2px)'}}>
                {monthLabels[w] || ''}
              </div>
            ))}
            {[0,1,2,3,4,5,6].map((d) => (
              <React.Fragment key={`row-${d}`}>
                <div style={{color:C.sub,fontSize:'11px',textAlign:'right',paddingRight:'2px'}}>{d%2===1?dayName[d]:''}</div>
                {weeks.map((week, wIdx) => {
                  const cell = week[d];
                  const selected = cell.key === selectedKey;
                  return (
                    <button
                      key={`${cell.key}-${wIdx}`}
                      title={cell.pnl === undefined ? `${cell.label}: No trades` : `${cell.label}: ${cell.pnl >= 0 ? '+' : ''}$${cell.pnl.toFixed(2)}`}
                      onClick={() => setSelectedKey(cell.key)}
                      style={{
                        width:'16px',
                        height:'16px',
                        borderRadius:'4px',
                        border:`1px solid ${selected ? C.blueBright : (cell.inRange ? cell.border : 'transparent')}`,
                        background: cell.bg,
                        cursor: cell.inRange ? 'pointer' : 'default',
                        padding:0,
                        outline:'none',
                      }}
                      disabled={!cell.inRange}
                    />
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
        <div style={{background:C.card2,border:`1px solid ${C.border}`,borderRadius:'10px',padding:'10px',display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{color:C.sub,fontSize:'12px',fontWeight:'700',textTransform:'uppercase'}}>Selected Month Stats</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr auto',gap:'8px',borderBottom:`1px solid ${C.border}`,paddingBottom:'8px'}}>
            <div>
              <div style={{color:C.text,fontSize:'14px',fontWeight:'700'}}>{selectedMonthLabel}</div>
              <div style={{color:C.sub,fontSize:'12px'}}>{selectedMonthStatRaw.green} green / {selectedMonthStatRaw.red} red days</div>
              <div style={{color:C.sub,fontSize:'12px'}}>{selectedMonthStatRaw.trades} active days</div>
            </div>
            <div style={{color:selectedMonthStatRaw.pnl>=0?POS_BRIGHT:NEG_BRIGHT,fontWeight:'800',fontSize:'15px'}}>{selectedMonthStatRaw.pnl>=0?'+':''}${selectedMonthStatRaw.pnl.toFixed(2)}</div>
          </div>
          <div style={{color:C.sub,fontSize:'12px',fontWeight:'700',textTransform:'uppercase',marginTop:'2px'}}>Weekday Edge</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px'}}>
            <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:'8px',padding:'8px'}}>
              <div style={{color:C.sub,fontSize:'11px',marginBottom:'3px'}}>Best Day</div>
              <div style={{color:POS_BRIGHT,fontSize:'14px',fontWeight:'700'}}>{bestDay?.day || '—'}</div>
              <div style={{color:C.sub,fontSize:'12px'}}>{bestDay ? `${bestDay.pnl>=0?'+':''}$${bestDay.pnl.toFixed(0)}` : '—'}</div>
            </div>
            <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:'8px',padding:'8px'}}>
              <div style={{color:C.sub,fontSize:'11px',marginBottom:'3px'}}>Weak Day</div>
              <div style={{color:NEG_BRIGHT,fontSize:'14px',fontWeight:'700'}}>{worstDay?.day || '—'}</div>
              <div style={{color:C.sub,fontSize:'12px'}}>{worstDay ? `${worstDay.pnl>=0?'+':''}$${worstDay.pnl.toFixed(0)}` : '—'}</div>
            </div>
          </div>
        </div>
      </div>
      <div style={{marginTop:'12px',background:C.card2,border:`1px solid ${C.border}`,borderRadius:'8px',padding:'10px 12px',display:'flex',justifyContent:'space-between',gap:'10px',flexWrap:'wrap'}}>
        <div style={{color:C.text,fontSize:'14px',fontWeight:'700'}}>
          {Number.isNaN(selectedDateObj.getTime()) ? 'Selected Day' : selectedDateObj.toDateString()}
        </div>
        <div style={{color:selectedPnl>=0?POS_BRIGHT:NEG_BRIGHT,fontSize:'15px',fontWeight:'800'}}>
          {selectedPnl === undefined ? 'No trades' : `${selectedPnl >= 0 ? '+' : ''}$${selectedPnl.toFixed(2)}`}
        </div>
      </div>
    </div>
  );
}

function AddTradeAdvancedChart({ asset, market, themeMode, interval, setInterval, resolveTvSymbol, isMobile }) {
  const hostRef = useRef(null);
  const containerIdRef = useRef(`tv_add_${Math.random().toString(36).slice(2, 9)}`);
  const mappedSymbol = resolveTvSymbol(asset, market);

  useEffect(() => {
    if (!hostRef.current) return;
    hostRef.current.innerHTML = '';
    const container = document.createElement('div');
    container.className = 'tradingview-widget-container__widget';
    container.style.height = `${isMobile ? 420 : 560}px`;
    container.style.width = '100%';
    container.id = containerIdRef.current;
    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.type = 'text/javascript';
    script.async = true;
    script.innerHTML = JSON.stringify({
      autosize: false,
      width: '100%',
      height: isMobile ? 420 : 560,
      symbol: mappedSymbol,
      interval,
      timezone: 'America/Toronto',
      theme: themeMode === 'day' ? 'light' : 'dark',
      style: '1',
      locale: 'en',
      allow_symbol_change: true,
      support_host: 'https://www.tradingview.com',
      hide_side_toolbar: false,
      withdateranges: true,
      save_image: false,
      details: true,
      hotlist: false,
      calendar: false,
      studies: ['Volume@tv-basicstudies', 'VWAP@tv-basicstudies'],
      container_id: containerIdRef.current,
    });
    hostRef.current.appendChild(container);
    hostRef.current.appendChild(script);
  }, [mappedSymbol, interval, themeMode, isMobile]);

  return (
    <div style={{ ...cardStyle, padding:'12px', marginBottom:'12px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:'8px', flexWrap:'wrap', marginBottom:'8px' }}>
        <div style={{ color:C.sub, fontSize:'11px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'1px' }}>
          Advanced Chart Widget
        </div>
        <select value={interval} onChange={(e) => setInterval(e.target.value)} style={{ ...inp, width:'120px', padding:'6px 8px', fontSize:'12px' }}>
          <option value="1">1m</option>
          <option value="5">5m</option>
          <option value="15">15m</option>
          <option value="60">1h</option>
          <option value="240">4h</option>
          <option value="D">1D</option>
        </select>
      </div>
      <div style={{ color:C.sub, fontSize:'11px', marginBottom:'8px' }}>
        Pulling market data for: <span style={{ color:C.text, fontWeight:'700' }}>{mappedSymbol}</span>
      </div>
      <div ref={hostRef} className="tradingview-widget-container" style={{ width:'100%', minHeight:isMobile ? '420px' : '560px' }} />
    </div>
  );
}

function ImportTrades({ trades, setTrades, activeAccount, userId, onImportDone }) {
  const [importing,setImporting]=useState(false);
  const [result,setResult]=useState(null);
  const [importMode, setImportMode] = useState('replaceImported');
  const [importBroker, setImportBroker] = useState('auto');

  // IBKR Auto Sync state
  const [ibkrToken, setIbkrToken] = useState(() => localStorage.getItem('ibkr_flex_token') || '');
  const [ibkrQueryId, setIbkrQueryId] = useState(() => localStorage.getItem('ibkr_flex_query_id') || '');
  const [ibkrSyncing, setIbkrSyncing] = useState(false);
  const [ibkrResult, setIbkrResult] = useState(null);
  const [ibkrShowSetup, setIbkrShowSetup] = useState(false);

  const saveIbkrCreds = () => {
    localStorage.setItem('ibkr_flex_token', ibkrToken);
    localStorage.setItem('ibkr_flex_query_id', ibkrQueryId);
  };

  const handleIbkrSync = async () => {
    if (!ibkrToken.trim() || !ibkrQueryId.trim()) {
      setIbkrResult({ success: false, error: 'Enter your Flex Token and Query ID first.' });
      return;
    }
    saveIbkrCreds();
    setIbkrSyncing(true);
    setIbkrResult(null);
    try {
      const res = await fetch('/api/ibkr-sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: ibkrToken.trim(), queryId: ibkrQueryId.trim() }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setIbkrResult({ success: false, error: data.error || 'Sync failed' });
        setIbkrSyncing(false);
        return;
      }
      const rawTrades = (data.trades || []).map(t => ({ ...t, account: activeAccount, user_id: userId }));
      if (rawTrades.length === 0) {
        setIbkrResult({ success: false, error: 'No closed trades found in the Flex report.' });
        setIbkrSyncing(false);
        return;
      }
      // Always do a full replace: query DB directly for IBKR trades, delete them, then insert fresh
      const { data: existingIbkr } = await supabase
        .from('trades')
        .select('id')
        .eq('user_id', userId)
        .ilike('notes', '%Imported from IBKR%');
      if (existingIbkr && existingIbkr.length > 0) {
        const ids = existingIbkr.map(t => t.id);
        await supabase.from('trades').delete().in('id', ids);
        setTrades(prev => prev.filter(t => !ids.includes(t.id)));
      }
      const { data: inserted, error: insertErr } = await supabase.from('trades').insert(rawTrades).select();
      if (insertErr) {
        setIbkrResult({ success: false, error: insertErr.message });
      } else {
        setTrades(prev => [...prev.filter(t => !(t.notes||'').toLowerCase().includes('imported from ibkr')), ...inserted]);
        setIbkrResult({ success: true, count: inserted.length });
        if (onImportDone) onImportDone({ account: activeAccount });
      }
    } catch (err) {
      setIbkrResult({ success: false, error: err.message });
    }
    setIbkrSyncing(false);
  };
  const [importAudit, setImportAudit] = useState(() => {
    if (typeof window === 'undefined') return null;
    const raw = localStorage.getItem('lastImportAudit');
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
  });
  const fileRef=useRef();
  const isImportedTrade = (t) => ((t.notes || '').toLowerCase().startsWith('imported from'));
  const tradeFingerprint = (t) => [
    t.date || '',
    (t.asset || '').toUpperCase(),
    `${t.entry || ''}`,
    `${t.exit || ''}`,
    `${t.size || ''}`,
    (t.type || '').toLowerCase(),
    t.account || '',
  ].join('|');
  const parseCSVLine=(line)=>{
    const result=[];let current='',inQuotes=false;
    for(let i=0;i<line.length;i++){if(line[i]==='"'){inQuotes=!inQuotes;}else if(line[i]===','&&!inQuotes){result.push(current.trim());current='';}else{current+=line[i];}}
    result.push(current.trim());return result;
  };
  const parseIBKRCSV=(text)=>{
    const lines=text.split('\n').filter(l=>l.trim());if(lines.length<2)return[];
    const headers=parseCSVLine(lines[0]);
    const idx=(name)=>headers.indexOf(name);
    const symIdx=idx('Symbol'),dateIdx=idx('TradeDate'),qtyIdx=idx('Quantity'),priceIdx=idx('TradePrice'),pnlIdx=idx('FifoPnlRealized'),commIdx=idx('IBCommission'),bsIdx=idx('Buy/Sell');
    const levelIdx=idx('LevelOfDetail'),txnIdx=idx('TransactionType'),ocIdx=idx('Open/CloseIndicator'),multIdx=idx('Multiplier'),costIdx=idx('CostBasis');
    const parsedRows = lines.slice(1).map(parseCSVLine);
    const hasClosedLots = parsedRows.some((cols) => ((cols[levelIdx] || '').toUpperCase() === 'CLOSED_LOT'));
    const newTrades=[];
    for(let i=0;i<parsedRows.length;i++){
      const cols=parsedRows[i];if(cols.length<10)continue;
      const level=(cols[levelIdx] || '').toUpperCase();
      const txn=(cols[txnIdx] || '');
      const oc=(cols[ocIdx] || '').toUpperCase();
      if (hasClosedLots) {
        if (level !== 'CLOSED_LOT') continue;
      } else {
        const isExecution = txn === 'ExchTrade' || level === 'EXECUTION';
        if (!isExecution || oc !== 'C') continue;
      }
      const symbol=cols[symIdx],dateRaw=cols[dateIdx],qty=Math.abs(toNumber(cols[qtyIdx])),price=toNumber(cols[priceIdx]),pnl=toNumber(cols[pnlIdx]),commission=toNumber(cols[commIdx]),buySell=(cols[bsIdx] || '').toUpperCase();
      if(!symbol||!qty||Number.isNaN(price))continue;
      const _parsedIbkrDate = dateRaw && dateRaw.length === 8
        ? new Date(+dateRaw.slice(0,4), +dateRaw.slice(4,6)-1, +dateRaw.slice(6,8))
        : parseTradeDate(dateRaw);
      const date = (_parsedIbkrDate && !isNaN(_parsedIbkrDate.getTime()))
        ? _parsedIbkrDate.toLocaleDateString()
        : new Date().toLocaleDateString();
      const adjPnl=(pnl+commission).toFixed(2);
      const multiplier = Math.max(1, toNumber(cols[multIdx], 1));
      const denom = qty * multiplier;
      const costBasis = toNumber(cols[costIdx], 0);
      const entryUnit = hasClosedLots && denom > 0 && costBasis
        ? (Math.abs(costBasis) / denom)
        : price;
      const tradeType = buySell === 'SELL' ? 'Buy' : 'Sell';
      const exitUnit = tradeType === 'Buy'
        ? (entryUnit + (toNumber(adjPnl) / denom))
        : (entryUnit - (toNumber(adjPnl) / denom));
      const inferred = inferTradeMeta(symbol, '', 'imported from ibkr');
      newTrades.push({
        asset:symbol,
        date,
        type:tradeType,
        entry:entryUnit.toFixed(4),
        exit:exitUnit.toFixed(4),
        size:qty.toString(),
        pnl:adjPnl,
        strategy:inferred.strategy,
        market:inferred.market,
        notes:`Imported from IBKR${hasClosedLots ? ' · Closed Lot' : ' · Closing Execution'} · Session: ${inferred.session}`,
        account:activeAccount,
        emotion:'',
        tags:`IBKR, ${inferred.tags}`,
        user_id:userId,
      });
    }
    return newTrades;
  };
  const BROKER_LABELS = {
    auto: 'Auto Detect',
    ibkr: 'IBKR',
    robinhood: 'Robinhood',
    webull: 'Webull',
    schwab: 'Charles Schwab',
    fidelity: 'Fidelity',
    thinkorswim: 'Thinkorswim',
    tradestation: 'TradeStation',
    binance: 'Binance',
    coinbase: 'Coinbase',
    generic: 'Generic CSV',
  };
  const normalizeHeader = (h) => `${h || ''}`.toLowerCase().replace(/[^a-z0-9]/g, '');
  const toDisplayDate = (raw) => {
    const d = parseTradeDate(raw);
    return Number.isNaN(d.getTime()) ? new Date().toLocaleDateString() : d.toLocaleDateString();
  };
  const findHeaderIndex = (headers, aliases) => {
    const normalized = headers.map(normalizeHeader);
    for (const alias of aliases) {
      const hit = normalized.indexOf(normalizeHeader(alias));
      if (hit !== -1) return hit;
    }
    for (const alias of aliases) {
      const target = normalizeHeader(alias);
      const hit = normalized.findIndex((h) => h.includes(target) || target.includes(h));
      if (hit !== -1) return hit;
    }
    return -1;
  };
  const detectBroker = (headers) => {
    const h = headers.map(normalizeHeader);
    if (h.includes('fifopnlrealized') || h.includes('levelofdetail')) return 'ibkr';
    if (h.includes('productid') || h.includes('realizedpnl')) return 'binance';
    if (h.includes('filledat') || h.includes('executedat')) return 'robinhood';
    if (h.includes('gainloss') || h.includes('realizedgainloss')) return 'schwab';
    if (h.includes('securitydescription') || h.includes('proceeds')) return 'fidelity';
    return 'generic';
  };
  const parseBrokerCSV = (text, brokerHint = 'auto') => {
    const lines = text.split('\n').filter((l) => l.trim());
    if (lines.length < 2) return [];
    const headers = parseCSVLine(lines[0]);
    const rows = lines.slice(1).map(parseCSVLine);
    const detectedBroker = brokerHint === 'auto' ? detectBroker(headers) : brokerHint;
    if (detectedBroker === 'ibkr') return parseIBKRCSV(text);
    const brokerLabel = BROKER_LABELS[detectedBroker] || 'CSV';

    const idx = {
      symbol: findHeaderIndex(headers, ['Symbol', 'Asset', 'Ticker', 'Instrument', 'Product', 'Product ID']),
      date: findHeaderIndex(headers, ['Date', 'Trade Date', 'TradeDate', 'Timestamp', 'Time', 'Filled At', 'Executed At']),
      side: findHeaderIndex(headers, ['Side', 'Action', 'Buy/Sell', 'Type']),
      qty: findHeaderIndex(headers, ['Quantity', 'Qty', 'Size', 'Shares', 'Filled', 'Amount']),
      price: findHeaderIndex(headers, ['Price', 'Trade Price', 'Average Price', 'Avg Price', 'Execution Price', 'Fill Price']),
      entry: findHeaderIndex(headers, ['Entry', 'Entry Price', 'Open Price', 'Buy Price']),
      exit: findHeaderIndex(headers, ['Exit', 'Exit Price', 'Close Price', 'Sell Price']),
      pnl: findHeaderIndex(headers, ['PnL', 'P&L', 'Realized PnL', 'Realized_PnL', 'Gain/Loss', 'Gain Loss', 'Profit']),
      commission: findHeaderIndex(headers, ['Commission', 'Fee', 'Fees']),
      strategy: findHeaderIndex(headers, ['Strategy', 'Setup']),
      market: findHeaderIndex(headers, ['Market', 'Asset Class', 'Security Type']),
    };

    const newTrades = [];
    rows.forEach((cols) => {
      const symbol = idx.symbol !== -1 ? `${cols[idx.symbol] || ''}`.trim() : '';
      if (!symbol) return;
      const qty = Math.abs(toNumber(idx.qty !== -1 ? cols[idx.qty] : 1, 1));
      const sideRaw = idx.side !== -1 ? `${cols[idx.side] || ''}`.toUpperCase() : '';
      const type = sideRaw.includes('SELL') || sideRaw === 'S' ? 'Sell' : 'Buy';

      const entryRaw = idx.entry !== -1 ? toNumber(cols[idx.entry], NaN) : NaN;
      const exitRaw = idx.exit !== -1 ? toNumber(cols[idx.exit], NaN) : NaN;
      const priceRaw = idx.price !== -1 ? toNumber(cols[idx.price], NaN) : NaN;
      const commission = idx.commission !== -1 ? toNumber(cols[idx.commission], 0) : 0;
      let entry = Number.isNaN(entryRaw) ? priceRaw : entryRaw;
      let exit = Number.isNaN(exitRaw) ? priceRaw : exitRaw;
      if (Number.isNaN(entry)) entry = 0;
      if (Number.isNaN(exit)) exit = entry;

      let pnl = idx.pnl !== -1 ? toNumber(cols[idx.pnl], NaN) : NaN;
      if (Number.isNaN(pnl) && qty > 0) pnl = calculateTradePnl(type, entry, exit, qty);
      if (Number.isNaN(pnl)) pnl = -Math.abs(commission);
      else pnl += commission;

      const strategyRaw = idx.strategy !== -1 ? cols[idx.strategy] : '';
      const inferred = inferTradeMeta(symbol, strategyRaw, `imported from ${brokerLabel.toLowerCase()}`);
      const marketRaw = idx.market !== -1 ? `${cols[idx.market] || ''}`.trim() : '';
      const market = marketRaw || inferred.market;

      newTrades.push({
        date: toDisplayDate(idx.date !== -1 ? cols[idx.date] : ''),
        asset: symbol,
        entry: entry.toFixed(4),
        exit: exit.toFixed(4),
        size: qty.toString(),
        pnl: pnl.toFixed(2),
        type,
        strategy: inferred.strategy,
        market,
        notes: `Imported from ${brokerLabel} CSV · Session: ${inferred.session}`,
        account: activeAccount,
        emotion: '',
        tags: `${brokerLabel} Import, ${inferred.tags}`,
        user_id: userId,
      });
    });
    return newTrades;
  };
  const parseJSONImport = (text, brokerHint = 'auto') => {
    let payload = null;
    try { payload = JSON.parse(text); } catch { return []; }
    const rows = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.trades)
        ? payload.trades
        : Array.isArray(payload?.data)
          ? payload.data
          : [];
    if (rows.length === 0) return [];
    const brokerLabel = BROKER_LABELS[brokerHint] && brokerHint !== 'auto' ? BROKER_LABELS[brokerHint] : 'JSON';
    return rows.map((r) => {
      const symbol = `${r.symbol || r.asset || r.ticker || r.instrument || ''}`.toUpperCase().trim();
      if (!symbol) return null;
      const qty = Math.abs(toNumber(r.quantity ?? r.qty ?? r.size ?? 1, 1));
      const sideRaw = `${r.side || r.action || r.type || ''}`.toUpperCase();
      const type = sideRaw.includes('SELL') || sideRaw === 'S' ? 'Sell' : 'Buy';
      const entry = toNumber(r.entry ?? r.entryPrice ?? r.price, 0);
      const exit = toNumber(r.exit ?? r.exitPrice ?? r.price, entry);
      const commission = toNumber(r.commission ?? r.fee, 0);
      let pnl = toNumber(r.pnl ?? r.realizedPnl ?? r.profit, NaN);
      if (Number.isNaN(pnl)) pnl = calculateTradePnl(type, entry, exit, qty);
      if (Number.isNaN(pnl)) pnl = -Math.abs(commission);
      else pnl += commission;
      const inferred = inferTradeMeta(symbol, r.strategy || '', `imported from ${brokerLabel.toLowerCase()}`);
      return {
        date: toDisplayDate(r.date || r.time || r.timestamp || r.tradeDate),
        asset: symbol,
        entry: entry.toFixed(4),
        exit: exit.toFixed(4),
        size: qty.toString(),
        pnl: pnl.toFixed(2),
        type,
        strategy: inferred.strategy,
        market: r.market || inferred.market,
        notes: `Imported from ${brokerLabel} JSON · Session: ${inferred.session}`,
        account: activeAccount,
        emotion: '',
        tags: `${brokerLabel} Import, ${inferred.tags}`,
        user_id: userId,
      };
    }).filter(Boolean);
  };
  const handleFile=async(e)=>{
    const file=e.target.files[0];if(!file)return;
    setImporting(true);setResult(null);
    const reader=new FileReader();
    reader.onload=async(ev)=>{
      const text = `${ev.target.result || ''}`;
      const trimmed = text.trim();
      const isJsonLike = file.name.toLowerCase().endsWith('.json') || trimmed.startsWith('{') || trimmed.startsWith('[');
      let newTrades = [];
      try {
        newTrades = isJsonLike
          ? parseJSONImport(text, importBroker)
          : parseBrokerCSV(text, importBroker);
      } catch (_err) {
        newTrades = [];
      }
      if(newTrades.length>0){
        let deletedCount = 0;
        let existingTrades = trades;
        if (importMode !== 'append') {
          deletedCount = importMode === 'replaceAll'
            ? trades.filter((t) => t.account === activeAccount).length
            : trades.filter((t) => t.account === activeAccount && isImportedTrade(t)).length;
          let deleteQuery = supabase
            .from('trades')
            .delete()
            .eq('user_id', userId)
            .eq('account', activeAccount);
          if (importMode === 'replaceImported') deleteQuery = deleteQuery.ilike('notes', 'Imported from%');
          const { error: deleteErr } = await deleteQuery;
          if (deleteErr) {
            setResult({ success:false, error:deleteErr.message || 'Failed to clear previous imports.' });
            setImporting(false);
            return;
          }
          existingTrades = importMode === 'replaceAll'
            ? trades.filter((t) => t.account !== activeAccount)
            : trades.filter((t) => !(t.account === activeAccount && isImportedTrade(t)));
          setTrades(existingTrades);
        }
        const seen = new Set(existingTrades.map(tradeFingerprint));
        const uniqueTrades = [];
        let skipped = 0;
        newTrades.forEach((t) => {
          const fp = tradeFingerprint(t);
          if (seen.has(fp)) {
            skipped += 1;
            return;
          }
          seen.add(fp);
          uniqueTrades.push(t);
        });
        if (uniqueTrades.length === 0) {
          setResult({ success:false, error:'All rows are duplicates. Nothing imported.', skipped, deleted:deletedCount });
          setImporting(false);
          return;
        }
        const {data,error}=await supabase.from('trades').insert(uniqueTrades).select();
        if(!error&&data){
          setTrades(prev=>[...prev,...data]);
          const importedDates = data
            .map((trade) => parseTradeDate(trade.date))
            .filter((date) => !Number.isNaN(date.getTime()))
            .sort((a, b) => a.getTime() - b.getTime());
          const latestImportedTradeDate = importedDates.length
            ? importedDates[importedDates.length - 1].toISOString()
            : null;
          const audit = {
            when: new Date().toISOString(),
            account: activeAccount,
            broker: BROKER_LABELS[importBroker] || importBroker,
            mode: importMode,
            file: file.name,
            imported: data.length,
            skipped,
            deleted: deletedCount,
            latestTradeDate: latestImportedTradeDate,
          };
          setImportAudit(audit);
          if (typeof window !== 'undefined') localStorage.setItem('lastImportAudit', JSON.stringify(audit));
          setResult({success:true,count:data.length,skipped,deleted:deletedCount,latestTradeDate:latestImportedTradeDate});
          if (onImportDone) onImportDone({ latestTradeDate: latestImportedTradeDate, account: activeAccount });
        } else {
          setResult({success:false,error:error?.message || 'Import failed.'});
        }
      }else{setResult({success:false,error:'No rows parsed. Choose the correct broker mode or check file format.'});}
      setImporting(false);
    };
    reader.readAsText(file);
  };
  return (
    <div>
      {/* ── IBKR Auto Sync ── */}
      <div style={{...cardStyle,padding:'24px',marginBottom:'16px'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:8}}>
          <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
            <div style={{width:38,height:38,borderRadius:10,background:'linear-gradient(135deg,#1a3a6b 0%,#2a1a6b 100%)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,border:'1px solid rgba(56,139,253,0.25)'}}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#388bfd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
            </div>
            <div>
              <div style={{color:C.text,fontWeight:'800',fontSize:'15px',letterSpacing:'-0.2px'}}>IBKR Auto Sync</div>
              <div style={{color:C.sub,fontSize:'11px',marginTop:1}}>Pull closed trades directly from Interactive Brokers via Flex Web Service</div>
            </div>
          </div>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            {(ibkrToken && ibkrQueryId) && <span style={{fontSize:'10px',fontWeight:'700',background:'rgba(14,203,129,0.1)',color:C.greenBright,border:`1px solid rgba(14,203,129,0.25)`,borderRadius:5,padding:'3px 9px',letterSpacing:'0.5px'}}>CONFIGURED</span>}
            <button onClick={()=>setIbkrShowSetup(s=>!s)} style={{padding:'7px 14px',borderRadius:7,border:`1px solid ${C.border}`,background:C.card2,color:C.text,cursor:'pointer',fontWeight:'700',fontSize:'12px'}}>
              {ibkrShowSetup ? 'Hide Setup' : (ibkrToken ? 'Edit Setup' : 'Setup')}
            </button>
          </div>
        </div>

        {ibkrShowSetup && (
          <div style={{marginTop:16,padding:'16px',background:C.card2,border:`1px solid ${C.border}`,borderRadius:10}}>
            <div style={{color:C.sub,fontSize:'10px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'1px',marginBottom:12}}>How to get your credentials</div>
            {[
              ['1', 'Log in to IBKR Account Management (www.ibkr.com)'],
              ['2', 'Go to: Reports → Flex Queries → Create / Edit Activity Flex Query'],
              ['3', 'Select Trades section → enable all fields → set Date Range to "Last 365 Days"'],
              ['4', 'Save the query → note the Query ID shown next to it'],
              ['5', 'Go to: Reports → Flex Queries → Generate Tokens → copy your Flex Token'],
            ].map(([n,t])=>(
              <div key={n} style={{display:'flex',gap:10,marginBottom:8,alignItems:'flex-start'}}>
                <span style={{width:20,height:20,borderRadius:'50%',background:`${C.blue}22`,border:`1px solid ${C.blue}44`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'10px',fontWeight:'800',color:C.blue,flexShrink:0}}>{n}</span>
                <span style={{color:C.sub,fontSize:'12px',lineHeight:'1.5'}}>{t}</span>
              </div>
            ))}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginTop:16}}>
              <div>
                <div style={{color:C.sub,fontSize:'11px',fontWeight:'700',marginBottom:4}}>Flex Token</div>
                <input value={ibkrToken} onChange={e=>{setIbkrToken(e.target.value);}} placeholder="e.g. 123456789012345678901234" style={{...inp,width:'100%',boxSizing:'border-box',fontFamily:'monospace',fontSize:'12px'}}/>
              </div>
              <div>
                <div style={{color:C.sub,fontSize:'11px',fontWeight:'700',marginBottom:4}}>Query ID</div>
                <input value={ibkrQueryId} onChange={e=>{setIbkrQueryId(e.target.value);}} placeholder="e.g. 123456" style={{...inp,width:'100%',boxSizing:'border-box',fontFamily:'monospace',fontSize:'12px'}}/>
              </div>
            </div>
            <div style={{display:'flex',gap:10,marginTop:12,flexWrap:'wrap'}}>
              <button onClick={()=>{saveIbkrCreds();setIbkrShowSetup(false);}} style={{padding:'8px 18px',borderRadius:7,border:`1px solid ${C.border}`,background:C.card2,color:C.text,cursor:'pointer',fontWeight:'700',fontSize:'13px'}}>Save Only</button>
              <button onClick={()=>{saveIbkrCreds();setIbkrShowSetup(false);handleIbkrSync();}} style={{padding:'8px 20px',borderRadius:7,border:'none',background:C.green,color:'#fff',cursor:'pointer',fontWeight:'800',fontSize:'13px'}}>Save &amp; Sync Now →</button>
            </div>
          </div>
        )}

        <div style={{marginTop:16,display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
          <button
            onClick={handleIbkrSync}
            disabled={ibkrSyncing}
            style={{padding:'10px 22px',borderRadius:8,border:'none',background:ibkrSyncing?C.card2:C.green,color:ibkrSyncing?C.sub:'#fff',cursor:ibkrSyncing?'not-allowed':'pointer',fontWeight:'800',fontSize:'13px',display:'flex',alignItems:'center',gap:7}}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
            {ibkrSyncing ? 'Syncing from IBKR...' : 'Sync Trades from IBKR'}
          </button>
          <button
            onClick={() => {
              if (!window.confirm('This will delete all IBKR-imported trades and re-sync from scratch. Continue?')) return;
              handleIbkrSync();
            }}
            disabled={ibkrSyncing}
            style={{padding:'10px 18px',borderRadius:8,border:`1px solid rgba(239,68,68,0.3)`,background:'rgba(239,68,68,0.06)',color:C.redBright,cursor:ibkrSyncing?'not-allowed':'pointer',fontWeight:'700',fontSize:'13px',display:'flex',alignItems:'center',gap:7}}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
            Clear &amp; Re-Sync
          </button>
          {ibkrSyncing && <span style={{color:C.sub,fontSize:'11px'}}>This may take 10–30 seconds…</span>}
        </div>

        {ibkrResult && (
          <div style={{marginTop:12,padding:'11px 15px',borderRadius:8,background:ibkrResult.success?'rgba(14,203,129,0.07)':'rgba(239,68,68,0.07)',border:`1px solid ${ibkrResult.success?'rgba(14,203,129,0.25)':'rgba(239,68,68,0.25)'}`}}>
            {ibkrResult.success
              ? <span style={{color:C.greenBright,fontWeight:'700',fontSize:'13px'}}>✓ Synced {ibkrResult.count} trade{ibkrResult.count!==1?'s':''}{ ibkrResult.skipped ? ` · ${ibkrResult.skipped} skipped` : '' }</span>
              : <span style={{color:C.redBright,fontWeight:'700',fontSize:'13px'}}>✕ {ibkrResult.error}</span>}
          </div>
        )}
      </div>

      <div style={{...cardStyle,padding:'28px',marginBottom:'20px'}}>
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}>
          <div style={{width:38,height:38,borderRadius:10,background:'linear-gradient(135deg,#0d2a4a 0%,#0d1f3c 100%)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,border:'1px solid rgba(56,139,253,0.2)'}}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#388bfd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          </div>
          <h3 style={{margin:0,fontSize:'15px',fontWeight:'800',color:C.text,letterSpacing:'-0.2px'}}>Import Trades (CSV / JSON)</h3>
        </div>
        <p style={{color:C.sub,fontSize:'13px',margin:'0 0 24px',lineHeight:'1.6'}}>Upload broker CSV/JSON files to load trading stats into Tradournal. Use Auto Detect for most files.</p>
        {[['1','Choose broker mode (or Auto Detect)',C.blue],['2','Export your trade history from broker',C.blue],['3','Upload CSV or JSON file',C.blue],['4','Review Import Audit and stats totals',C.green]].map(([num,text,color])=>(
          <div key={num} style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'10px'}}>
            <div style={{width:'24px',height:'24px',borderRadius:'50%',background:`${color}22`,border:`1px solid ${color}55`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'11px',fontWeight:'800',color,flexShrink:0}}>{num}</div>
            <span style={{color:C.text,fontSize:'13px'}}>{text}</span>
          </div>
        ))}
        <div style={{marginTop:'10px',marginBottom:'12px',background:C.card2,border:`1px solid ${C.border}`,borderRadius:'8px',padding:'10px 12px',display:'flex',flexDirection:'column',alignItems:'flex-start',gap:'8px'}}>
          <div style={{color:C.sub,fontSize:'10px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'0.8px'}}>BROKER MODE</div>
          <select value={importBroker} onChange={(e)=>setImportBroker(e.target.value)} style={{...inp,width:'100%',maxWidth:'420px'}}>
            <option value="auto">Auto Detect (Recommended)</option>
            <option value="ibkr">IBKR</option>
            <option value="robinhood">Robinhood</option>
            <option value="webull">Webull</option>
            <option value="schwab">Charles Schwab</option>
            <option value="fidelity">Fidelity</option>
            <option value="thinkorswim">Thinkorswim</option>
            <option value="tradestation">TradeStation</option>
            <option value="binance">Binance</option>
            <option value="coinbase">Coinbase</option>
            <option value="generic">Generic CSV</option>
          </select>
          <span style={{color:C.sub,fontSize:'11px'}}>If parsing misses rows, switch from Auto Detect to the specific broker.</span>
        </div>
        <div style={{marginTop:'10px',marginBottom:'12px',background:C.card2,border:`1px solid ${C.border}`,borderRadius:'8px',padding:'10px 12px',display:'flex',flexDirection:'column',alignItems:'flex-start',gap:'8px'}}>
          <div style={{color:C.sub,fontSize:'10px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'0.8px'}}>IMPORT MODE</div>
          <select value={importMode} onChange={(e)=>setImportMode(e.target.value)} style={{...inp,width:'100%',maxWidth:'420px'}}>
            <option value="append">Append (keep everything, skip exact duplicates)</option>
            <option value="replaceImported">Replace imported-only trades in this account</option>
            <option value="replaceAll">Replace all trades in this account (full reset)</option>
          </select>
          <span style={{color:C.sub,fontSize:'11px'}}>Use Replace Imported as the default to avoid overlap on re-upload.</span>
        </div>
        <input ref={fileRef} type="file" accept=".csv,.json,text/csv,application/json" onChange={handleFile} style={{display:'none'}}/>
        <button onClick={()=>fileRef.current.click()} style={{padding:'12px 24px',background:C.blue,color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontWeight:'800',fontSize:'13px',marginTop:'16px',display:'flex',alignItems:'center',gap:8}}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
          Upload Broker File
        </button>
        {importing&&<div style={{color:C.sub,fontSize:'13px',marginTop:'12px'}}>⏳ Importing trades...</div>}
        {result&&(
          <div style={{marginTop:'16px',padding:'14px',background:result.success?'rgba(46,160,67,0.1)':'rgba(218,54,51,0.1)',borderRadius:'8px',border:`1px solid ${result.success?C.green:C.red}55`}}>
            {result.success
              ? <span style={{color:C.greenBright,fontWeight:'700'}}>✅ Imported {result.count} trades{result.skipped?`, skipped ${result.skipped} duplicates`:''}{result.deleted?`, removed ${result.deleted} previous imports`:''}.</span>
              : <span style={{color:C.redBright,fontWeight:'700'}}>❌ {result.error || 'No trades found. Check the CSV format.'}</span>}
          </div>
        )}
      </div>
      <div style={{...cardStyle,padding:'18px',marginBottom:'20px'}}>
        <h3 style={{margin:'0 0 10px',fontSize:'14px',fontWeight:'700',color:C.text}}>🧾 Import Audit</h3>
        {!importAudit && <div style={{color:C.sub,fontSize:'12px'}}>No imports logged yet on this browser.</div>}
        {importAudit && (
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(170px, 1fr))',gap:'8px'}}>
            {[
              ['Last Import', new Date(importAudit.when).toLocaleString()],
              ['Account', importAudit.account || '—'],
              ['Broker', importAudit.broker || '—'],
              ['Mode', importAudit.mode || '—'],
              ['File', importAudit.file || '—'],
              ['Imported', `${importAudit.imported || 0}`],
              ['Skipped', `${importAudit.skipped || 0}`],
              ['Deleted', `${importAudit.deleted || 0}`],
            ].map(([label, value]) => (
              <div key={label} style={{background:C.card2,border:`1px solid ${C.border}`,borderRadius:'8px',padding:'8px 10px'}}>
                <div style={{color:C.sub,fontSize:'10px',fontWeight:'700',textTransform:'uppercase',marginBottom:'4px'}}>{label}</div>
                <div style={{color:C.text,fontSize:'12px',fontWeight:'700',wordBreak:'break-word'}}>{value}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={{...cardStyle,padding:'22px'}}>
        <h3 style={{margin:'0 0 12px',fontSize:'15px',fontWeight:'700',color:C.text}}>📋 Supported Import Formats</h3>
        <div style={{background:C.card2,padding:'14px',borderRadius:'8px',fontFamily:'monospace',fontSize:'12px',color:C.greenBright,border:`1px solid ${C.border}`}}>
          Date, Asset, Entry, Exit, Size, Strategy<br/>
          01/15/2024, AAPL, 150.00, 155.00, 100, Breakout<br/>
          <br/>
          Also supported (Auto Detect): IBKR Flex, Robinhood/Webull/Schwab/Fidelity-style CSV, Binance/Coinbase exports, and JSON arrays with symbol/date/qty/price/pnl fields.
        </div>
      </div>
    </div>
  );
}

function JournalInsights({ trades }) {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dayMap = days.reduce((acc, day) => ({ ...acc, [day]: { pnl: 0, trades: 0, wins: 0 } }), {});
  const emotionMap = {};
  const tagMap = {};
  let totalPsych = 0;
  let psychCount = 0;
  let ruleScoreTotal = 0;
  let ruleScoreCount = 0;

  trades.forEach((t) => {
    const pnl = toNumber(t.pnl);
    const d = new Date(t.date);
    const day = !Number.isNaN(d.getTime()) ? days[d.getDay()] : null;
    if (day) {
      dayMap[day].pnl += pnl;
      dayMap[day].trades += 1;
      if (pnl > 0) dayMap[day].wins += 1;
    }

    const emotion = t.emotion || 'Unspecified';
    if (!emotionMap[emotion]) emotionMap[emotion] = { pnl: 0, trades: 0, wins: 0 };
    emotionMap[emotion].pnl += pnl;
    emotionMap[emotion].trades += 1;
    if (pnl > 0) emotionMap[emotion].wins += 1;

    const psych = parseFloat(t.psychScore);
    if (!Number.isNaN(psych)) {
      totalPsych += psych;
      psychCount += 1;
    }

    const checks = Array.isArray(t.rulesChecked)
      ? t.rulesChecked
      : typeof t.rulesChecked === 'string'
        ? t.rulesChecked.split(',').map((x) => x.trim()).filter(Boolean)
        : [];
    if (checks.length > 0) {
      ruleScoreTotal += (checks.length / RULES.length) * 100;
      ruleScoreCount += 1;
    }

    (t.tags || '')
      .split(',')
      .map((tag) => tag.trim().toLowerCase())
      .filter(Boolean)
      .forEach((tag) => {
        tagMap[tag] = (tagMap[tag] || 0) + 1;
      });
  });

  const weekdayData = days.map((day) => ({
    day,
    pnl: parseFloat(dayMap[day].pnl.toFixed(2)),
    winRate: dayMap[day].trades ? (dayMap[day].wins / dayMap[day].trades) * 100 : 0,
  }));
  const emotions = Object.entries(emotionMap)
    .map(([emotion, stats]) => ({
      emotion,
      pnl: stats.pnl,
      trades: stats.trades,
      winRate: stats.trades ? (stats.wins / stats.trades) * 100 : 0,
    }))
    .sort((a, b) => b.trades - a.trades)
    .slice(0, 6);
  const topTags = Object.entries(tagMap).sort((a, b) => b[1] - a[1]).slice(0, 8);

  const activeDays = weekdayData.filter((d) => Math.abs(d.pnl) > 0);
  const greenDays = activeDays.filter((d) => d.pnl > 0).length;
  const consistency = activeDays.length ? (greenDays / activeDays.length) * 100 : 0;
  const expectancy = trades.length ? trades.reduce((s, t) => s + toNumber(t.pnl), 0) / trades.length : 0;
  const avgPsych = psychCount ? totalPsych / psychCount : 0;
  const avgRuleScore = ruleScoreCount ? ruleScoreTotal / ruleScoreCount : 0;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
        {[
          ['Expectancy / Trade', `$${expectancy.toFixed(2)}`, expectancy >= 0 ? C.greenBright : C.redBright],
          ['Consistency Score', `${consistency.toFixed(1)}%`, C.blueBright],
          ['Avg Psychology', `${avgPsych.toFixed(1)}/10`, C.purple],
          ['Rule Adherence', `${avgRuleScore.toFixed(1)}%`, C.orange],
        ].map(([label, value, color]) => (
          <div key={label} style={{ ...cardStyle, padding: '16px' }}>
            <div style={{ color: C.sub, fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '6px' }}>{label}</div>
            <div style={{ color, fontSize: '20px', fontWeight: '800' }}>{value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '14px', marginBottom: '16px' }}>
        <div style={{ ...cardStyle, padding: '20px' }}>
          <div style={{ color: C.sub, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '14px' }}>Weekday Edge</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={weekdayData}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="day" stroke={C.sub} tick={{ fontSize: 10 }} />
              <YAxis stroke={C.sub} tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '12px', color: C.text }} labelStyle={{ color: C.text }} itemStyle={{ color: C.text }} formatter={(v, name) => [name === 'pnl' ? `$${v}` : `${parseFloat(v).toFixed(1)}%`, name === 'pnl' ? 'PnL' : 'Win Rate']} />
              <Bar dataKey="pnl" fill={C.blue} radius={[4, 4, 0, 0]} maxBarSize={36} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={{ ...cardStyle, padding: '20px' }}>
          <div style={{ color: C.sub, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>Emotion Performance</div>
          {emotions.length === 0 && <div style={{ color: C.sub, fontSize: '12px' }}>No emotion data yet.</div>}
          {emotions.map((e) => (
            <div key={e.emotion} style={{ marginBottom: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ color: C.text, fontSize: '12px' }}>{e.emotion}</span>
                <span style={{ color: e.pnl >= 0 ? C.greenBright : C.redBright, fontSize: '12px', fontWeight: '700' }}>{e.winRate.toFixed(1)}%</span>
              </div>
              <div style={{ background: C.card2, borderRadius: '4px', height: '5px' }}>
                <div style={{ width: `${Math.min(e.winRate, 100)}%`, height: '5px', borderRadius: '4px', background: e.pnl >= 0 ? C.green : C.red }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ ...cardStyle, padding: '20px' }}>
        <div style={{ color: C.sub, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '14px' }}>Tag Leaderboard</div>
        {topTags.length === 0 && <div style={{ color: C.sub, fontSize: '12px' }}>No tags added yet.</div>}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {topTags.map(([tag, count]) => (
            <div key={tag} style={{ padding: '6px 10px', borderRadius: '20px', border: `1px solid ${C.border}`, background: C.card2, fontSize: '12px', color: C.text }}>
              #{tag} <span style={{ color: C.sub }}>({count})</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PremiumCoach({ trades, openReplay }) {
  const sorted = [...trades].sort((a, b) => (a.id || 0) - (b.id || 0));
  const wins = sorted.filter((t) => toNumber(t.pnl) > 0);
  const losses = sorted.filter((t) => toNumber(t.pnl) < 0);
  const winRate = sorted.length ? (wins.length / sorted.length) * 100 : 0;
  const lossRate = 100 - winRate;
  const avgWin = wins.length ? wins.reduce((s, t) => s + toNumber(t.pnl), 0) / wins.length : 0;
  const avgLossAbs = losses.length ? Math.abs(losses.reduce((s, t) => s + toNumber(t.pnl), 0) / losses.length) : 0;
  const expectancy = (winRate / 100) * avgWin - (lossRate / 100) * avgLossAbs;

  const strategyMap = {};
  const emotionMap = {};
  const dayMap = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].reduce((acc, day) => ({ ...acc, [day]: { pnl: 0, trades: 0, wins: 0 } }), {});
  const hourMap = Array.from({ length: 24 }, (_, i) => ({ hour: i, pnl: 0, trades: 0 }));
  const sizes = [];
  let maxDrawdown = 0;
  let drawdownTotal = 0;
  let drawdownCount = 0;
  let running = 0;
  let peak = 0;
  let currentWinStreak = 0;
  let currentLossStreak = 0;
  let longestWinStreak = 0;
  let longestLossStreak = 0;
  let revengeTrades = 0;

  sorted.forEach((t) => {
    const pnl = toNumber(t.pnl);
    const strategy = t.strategy || 'Other';
    if (!strategyMap[strategy]) strategyMap[strategy] = { trades: 0, wins: 0, pnl: 0 };
    strategyMap[strategy].trades += 1;
    strategyMap[strategy].pnl += pnl;
    if (pnl > 0) strategyMap[strategy].wins += 1;

    const emotion = t.emotion || 'Unspecified';
    if (!emotionMap[emotion]) emotionMap[emotion] = { trades: 0, pnl: 0 };
    emotionMap[emotion].trades += 1;
    emotionMap[emotion].pnl += pnl;

    const d = new Date(t.date);
    if (!Number.isNaN(d.getTime())) {
      const dayKey = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
      dayMap[dayKey].trades += 1;
      dayMap[dayKey].pnl += pnl;
      if (pnl > 0) dayMap[dayKey].wins += 1;
    }

    const h = new Date(t.created_at || t.date).getHours();
    if (!Number.isNaN(h) && hourMap[h]) {
      hourMap[h].trades += 1;
      hourMap[h].pnl += pnl;
    }

    const size = toNumber(t.size);
    if (!Number.isNaN(size) && size > 0) sizes.push(size);

    running += pnl;
    if (running > peak) peak = running;
    const dd = peak - running;
    if (dd > 0) {
      drawdownTotal += dd;
      drawdownCount += 1;
    }
    if (dd > maxDrawdown) maxDrawdown = dd;

    if (pnl > 0) {
      currentWinStreak += 1;
      currentLossStreak = 0;
    } else if (pnl < 0) {
      currentLossStreak += 1;
      currentWinStreak = 0;
    }
    if (currentWinStreak > longestWinStreak) longestWinStreak = currentWinStreak;
    if (currentLossStreak > longestLossStreak) longestLossStreak = currentLossStreak;

    if ((t.tags || '').toLowerCase().includes('revenge') || (t.notes || '').toLowerCase().includes('revenge')) revengeTrades += 1;
  });

  const medianSize = sizes.length ? [...sizes].sort((a, b) => a - b)[Math.floor(sizes.length / 2)] : 0;
  const avgSize = sizes.length ? sizes.reduce((s, v) => s + v, 0) / sizes.length : 0;
  const disciplineScore = medianSize > 0 ? Math.max(0, 100 - ((Math.max(avgSize - medianSize, 0) / medianSize) * 100)) : 100;

  const strategyRows = Object.entries(strategyMap)
    .map(([strategy, v]) => ({ strategy, trades: v.trades, winRate: v.trades ? (v.wins / v.trades) * 100 : 0, profit: v.pnl }))
    .sort((a, b) => b.profit - a.profit);
  const emotionRows = Object.entries(emotionMap).map(([emotion, v]) => ({ emotion, trades: v.trades, pnl: v.pnl })).sort((a, b) => b.trades - a.trades);
  const dayRows = Object.entries(dayMap).map(([day, v]) => ({ day, trades: v.trades, winRate: v.trades ? (v.wins / v.trades) * 100 : 0, pnl: v.pnl }));
  const bestHours = hourMap.filter((x) => x.trades > 0).sort((a, b) => b.pnl - a.pnl).slice(0, 6);

  const weekStart = new Date();
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  weekStart.setHours(0, 0, 0, 0);
  const weeklyTrades = sorted.filter((t) => {
    const d = new Date(t.date);
    return !Number.isNaN(d.getTime()) && d >= weekStart;
  });
  const weeklyPnl = weeklyTrades.reduce((s, t) => s + toNumber(t.pnl), 0);
  const weeklyWins = weeklyTrades.filter((t) => toNumber(t.pnl) > 0).length;
  const weeklyBest = weeklyTrades.length ? Math.max(...weeklyTrades.map((t) => toNumber(t.pnl))) : 0;
  const weeklyWorst = weeklyTrades.length ? Math.min(...weeklyTrades.map((t) => toNumber(t.pnl))) : 0;

  const reviews = sorted.slice(-5).reverse().map((t) => {
    const pnl = toNumber(t.pnl);
    const checks = Array.isArray(t.rulesChecked)
      ? t.rulesChecked
      : typeof t.rulesChecked === 'string'
        ? t.rulesChecked.split(',').map((x) => x.trim()).filter(Boolean)
        : [];
    const hasStop = checks.includes('Set stop loss before entry');
    const size = toNumber(t.size);
    const riskStatus = medianSize > 0 && size > medianSize * 1.35 ? 'Too large' : 'Controlled';
    const entryStatus = parseFloat(t.psychScore || 0) < 6 || pnl < 0 ? 'Early' : 'Patient';
    const score = Math.max(1, Math.min(10, Math.round((checks.length / RULES.length) * 5 + (pnl > 0 ? 3 : 1) + (riskStatus === 'Controlled' ? 2 : 0))));
    const suggestion = !hasStop
      ? 'Set stop loss before entry on every trade.'
      : riskStatus === 'Too large'
        ? 'Reduce position size to keep risk consistent.'
        : entryStatus === 'Early'
          ? 'Wait for confirmation candle before entry.'
          : 'Execution is solid. Keep journaling this setup.';
    return { id: t.id, asset: t.asset, entryStatus, hasStop, riskStatus, score, suggestion };
  });

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:'12px', marginBottom:'16px' }}>
        {[
          ['Expectancy', `$${expectancy.toFixed(2)} / trade`, expectancy >= 0 ? C.greenBright : C.redBright],
          ['Risk Discipline', `${disciplineScore.toFixed(1)}%`, disciplineScore >= 70 ? C.greenBright : C.orange],
          ['Max Drawdown', `$${maxDrawdown.toFixed(2)}`, C.redBright],
          ['Longest Losing Streak', `${longestLossStreak}`, C.orange],
          ['Longest Winning Streak', `${longestWinStreak}`, C.greenBright],
        ].map(([l, v, c]) => (
          <div key={l} style={{ ...cardStyle, padding:'16px' }}>
            <div style={{ color:C.sub, fontSize:'10px', textTransform:'uppercase', fontWeight:'700', marginBottom:'6px' }}>{l}</div>
            <div style={{ color:c, fontSize:'20px', fontWeight:'800' }}>{v}</div>
          </div>
        ))}
      </div>

      <div style={{ ...cardStyle, padding:'18px', marginBottom:'16px' }}>
        <div style={{ color:C.sub, fontSize:'11px', textTransform:'uppercase', letterSpacing:'1px', fontWeight:'700', marginBottom:'10px' }}>AI Trade Review (Last 5)</div>
        {reviews.length === 0 && <div style={{ color:C.sub, fontSize:'12px' }}>No trades to review yet.</div>}
        {reviews.map((r) => (
          <div key={r.id} style={{ background:C.card2, border:`1px solid ${C.border}`, borderRadius:'8px', padding:'12px', marginBottom:'8px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'6px' }}>
              <div style={{ color:C.text, fontWeight:'700', fontSize:'13px' }}>{r.asset || 'Asset'}</div>
              <div style={{ color:C.blueBright, fontWeight:'700', fontSize:'12px' }}>Execution Score: {r.score}/10</div>
            </div>
            <div style={{ color:C.sub, fontSize:'12px', marginBottom:'4px' }}>Entry: <span style={{ color:C.text }}>{r.entryStatus}</span> · Stop: <span style={{ color:C.text }}>{r.hasStop ? 'Correct' : 'Missing'}</span> · Risk: <span style={{ color:r.riskStatus === 'Too large' ? C.redBright : C.greenBright }}>{r.riskStatus}</span></div>
            <div style={{ color:C.text, fontSize:'12px' }}>Suggestion: {r.suggestion}</div>
          </div>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))', gap:'14px', marginBottom:'16px' }}>
        <div style={{ ...cardStyle, padding:'18px' }}>
          <div style={{ color:C.sub, fontSize:'11px', textTransform:'uppercase', letterSpacing:'1px', fontWeight:'700', marginBottom:'10px' }}>Setup Performance</div>
          {strategyRows.length === 0 && <div style={{ color:C.sub, fontSize:'12px' }}>No strategy data yet.</div>}
          {strategyRows.slice(0, 6).map((row) => (
            <div key={row.strategy} style={{ display:'grid', gridTemplateColumns:'1.2fr 0.6fr 0.7fr 0.8fr', gap:'8px', padding:'7px 0', borderBottom:`1px solid ${C.border}`, fontSize:'12px' }}>
              <div style={{ color:C.text }}>{row.strategy}</div>
              <div style={{ color:C.sub }}>{row.trades}</div>
              <div style={{ color:C.blueBright }}>{row.winRate.toFixed(1)}%</div>
              <div style={{ color:row.profit >= 0 ? C.greenBright : C.redBright, fontWeight:'700' }}>{row.profit >= 0 ? '+' : ''}${row.profit.toFixed(0)}</div>
            </div>
          ))}
        </div>
        <div style={{ ...cardStyle, padding:'18px' }}>
          <div style={{ color:C.sub, fontSize:'11px', textTransform:'uppercase', letterSpacing:'1px', fontWeight:'700', marginBottom:'10px' }}>Emotional Analytics</div>
          {emotionRows.length === 0 && <div style={{ color:C.sub, fontSize:'12px' }}>No emotion data yet.</div>}
          {emotionRows.slice(0, 6).map((row) => (
            <div key={row.emotion} style={{ display:'grid', gridTemplateColumns:'1fr 0.5fr 0.7fr', gap:'8px', padding:'7px 0', borderBottom:`1px solid ${C.border}`, fontSize:'12px' }}>
              <div style={{ color:C.text }}>{row.emotion}</div>
              <div style={{ color:C.sub }}>{row.trades}</div>
              <div style={{ color:row.pnl >= 0 ? C.greenBright : C.redBright, fontWeight:'700' }}>{row.pnl >= 0 ? '+' : ''}${row.pnl.toFixed(0)}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))', gap:'14px', marginBottom:'16px' }}>
        <div style={{ ...cardStyle, padding:'18px' }}>
          <div style={{ color:C.sub, fontSize:'11px', textTransform:'uppercase', letterSpacing:'1px', fontWeight:'700', marginBottom:'10px' }}>Day Of Week Performance</div>
          {dayRows.map((row) => (
            <div key={row.day} style={{ display:'grid', gridTemplateColumns:'0.9fr 0.7fr 0.7fr 0.8fr', gap:'8px', padding:'7px 0', borderBottom:`1px solid ${C.border}`, fontSize:'12px' }}>
              <div style={{ color:C.text }}>{row.day}</div>
              <div style={{ color:C.sub }}>{row.trades}</div>
              <div style={{ color:C.blueBright }}>{row.winRate.toFixed(1)}%</div>
              <div style={{ color:row.pnl >= 0 ? C.greenBright : C.redBright, fontWeight:'700' }}>{row.pnl >= 0 ? '+' : ''}${row.pnl.toFixed(0)}</div>
            </div>
          ))}
        </div>
        <div style={{ ...cardStyle, padding:'18px' }}>
          <div style={{ color:C.sub, fontSize:'11px', textTransform:'uppercase', letterSpacing:'1px', fontWeight:'700', marginBottom:'10px' }}>Best Trading Hours</div>
          {bestHours.length === 0 && <div style={{ color:C.sub, fontSize:'12px' }}>Add more trades to unlock hourly analytics.</div>}
          {bestHours.map((h) => (
            <div key={h.hour} style={{ display:'grid', gridTemplateColumns:'0.9fr 0.6fr 0.8fr', gap:'8px', padding:'7px 0', borderBottom:`1px solid ${C.border}`, fontSize:'12px' }}>
              <div style={{ color:C.text }}>{h.hour}:00-{(h.hour + 1) % 24}:00</div>
              <div style={{ color:C.sub }}>{h.trades}</div>
              <div style={{ color:h.pnl >= 0 ? C.greenBright : C.redBright, fontWeight:'700' }}>{h.pnl >= 0 ? '+' : ''}${h.pnl.toFixed(0)}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ ...cardStyle, padding:'18px', marginBottom:'16px' }}>
        <div style={{ color:C.sub, fontSize:'11px', textTransform:'uppercase', letterSpacing:'1px', fontWeight:'700', marginBottom:'10px' }}>Weekly Performance Report</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:'10px', marginBottom:'8px' }}>
          {[
            ['Trades', weeklyTrades.length, C.text],
            ['Win Rate', `${weeklyTrades.length ? ((weeklyWins / weeklyTrades.length) * 100).toFixed(1) : '0.0'}%`, C.blueBright],
            ['Profit', `${weeklyPnl >= 0 ? '+' : ''}$${weeklyPnl.toFixed(2)}`, weeklyPnl >= 0 ? C.greenBright : C.redBright],
            ['Best Trade', `${weeklyBest >= 0 ? '+' : ''}$${weeklyBest.toFixed(2)}`, C.greenBright],
            ['Worst Trade', `${weeklyWorst >= 0 ? '+' : ''}$${weeklyWorst.toFixed(2)}`, C.redBright],
            ['Revenge Trades', revengeTrades, revengeTrades > 0 ? C.orange : C.greenBright],
            ['Avg Drawdown', `$${(drawdownCount ? drawdownTotal / drawdownCount : 0).toFixed(2)}`, C.orange],
          ].map(([l, v, c]) => (
            <div key={l} style={{ background:C.card2, border:`1px solid ${C.border}`, borderRadius:'8px', padding:'12px' }}>
              <div style={{ color:C.sub, fontSize:'10px', textTransform:'uppercase', fontWeight:'700', marginBottom:'4px' }}>{l}</div>
              <div style={{ color:c, fontSize:'16px', fontWeight:'800' }}>{v}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ ...cardStyle, padding:'18px' }}>
        <div style={{ color:C.sub, fontSize:'11px', textTransform:'uppercase', letterSpacing:'1px', fontWeight:'700', marginBottom:'10px' }}>Trade Replay + Screenshot Analysis</div>
        <div style={{ color:C.sub, fontSize:'12px', marginBottom:'10px' }}>
          Screenshots uploaded: <span style={{ color:C.text, fontWeight:'700' }}>{sorted.filter((t) => !!t.screenshot).length}</span>
        </div>
        <button onClick={() => openReplay()} style={{ padding:'8px 14px', background:C.blue, color:'#fff', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'700' }}>
          Open Replay Workspace
        </button>
      </div>
    </div>
  );
}

function VolumeProfileChart({ trades }) {
  const [filterSymbol, setFilterSymbol] = useState('');
  const BINS = 24;
  const eligible = trades.filter(t => {
    const e = toNumber(t.entry, NaN), x = toNumber(t.exit, NaN), s = toNumber(t.size, 0);
    return Number.isFinite(e) && Number.isFinite(x) && s > 0;
  });
  const filtered = filterSymbol ? eligible.filter(t => (t.asset || '').toUpperCase() === filterSymbol) : eligible;
  const uniqueSymbols = [...new Set(eligible.map(t => t.asset).filter(Boolean))].sort();

  if (filtered.length === 0) {
    return (
      <div style={{ ...cardStyle, padding: '20px', marginTop: '16px' }}>
        <div style={{ color: C.sub, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>Session Volume Profile</div>
        <div style={{ color: C.sub, fontSize: '12px' }}>Add trades with entry/exit prices and position size to see the volume profile.</div>
      </div>
    );
  }

  const allPrices = filtered.flatMap(t => [toNumber(t.entry), toNumber(t.exit)]).filter(p => p > 0);
  const minP = Math.min(...allPrices), maxP = Math.max(...allPrices), range = maxP - minP;
  const safeRange = range > 0 ? range : 1;
  const binSize = safeRange / BINS;
  const bins = Array.from({ length: BINS }, (_, i) => {
    const price = minP + (i + 0.5) * binSize;
    return { price, priceLabel: price.toFixed(2), volume: 0, buyVol: 0, sellVol: 0, isPoc: false, inVA: false };
  });

  filtered.forEach(t => {
    const entry = toNumber(t.entry), exit = toNumber(t.exit), size = toNumber(t.size, 0);
    const low = Math.min(entry, exit), high = Math.max(entry, exit);
    const overlapping = range > 0
      ? bins.filter(b => (b.price + binSize / 2) > low && (b.price - binSize / 2) < high)
      : bins;
    const target = overlapping.length
      ? overlapping
      : [bins[Math.max(0, Math.min(BINS - 1, Math.floor((entry - minP) / binSize)))]];
    const volPer = size / target.length;
    target.forEach(b => {
      b.volume += volPer;
      if (t.type === 'Buy') b.buyVol += volPer; else b.sellVol += volPer;
    });
  });

  const totalVol = bins.reduce((s, b) => s + b.volume, 0);
  const pocIdx = bins.reduce((mi, b, i, a) => b.volume > a[mi].volume ? i : mi, 0);
  bins[pocIdx].isPoc = true;
  const sortedByVol = [...bins].sort((a, b) => b.volume - a.volume);
  let accumulated = 0;
  const vaSet = new Set();
  for (const b of sortedByVol) {
    if (accumulated >= totalVol * 0.7) break;
    accumulated += b.volume;
    vaSet.add(b.price);
  }
  bins.forEach(b => { b.inVA = vaSet.has(b.price); });
  const poc = bins[pocIdx].price;
  const vaBins = bins.filter(b => b.inVA);
  const vah = vaBins.length ? Math.max(...vaBins.map(b => b.price)) : maxP;
  const val = vaBins.length ? Math.min(...vaBins.map(b => b.price)) : minP;
  const profileData = [...bins].reverse();

  return (
    <div style={{ ...cardStyle, padding: '20px', marginTop: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
        <div>
          <div style={{ color: C.sub, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px' }}>Session Volume Profile</div>
          <div style={{ color: C.sub, fontSize: '11px', marginTop: '2px' }}>{filtered.length} trades · Price levels with most activity</div>
        </div>
        <select value={filterSymbol} onChange={e => setFilterSymbol(e.target.value)} style={{ ...inp, width: '150px', padding: '6px 8px', fontSize: '12px' }}>
          <option value="">All Symbols</option>
          {uniqueSymbols.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
        {[['POC', `$${poc.toFixed(2)}`, 'Highest Vol', C.orange], ['VAH', `$${vah.toFixed(2)}`, 'Value Area High', C.greenBright], ['VAL', `$${val.toFixed(2)}`, 'Value Area Low', C.redBright], ['VA Range', `$${(vah - val).toFixed(2)}`, '70% of Volume', C.blueBright]].map(([l, v, d, c]) => (
          <div key={l} style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 12px' }}>
            <div style={{ color: C.sub, fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '2px' }}>{l}</div>
            <div style={{ color: c, fontSize: '14px', fontWeight: '800' }}>{v}</div>
            <div style={{ color: C.sub, fontSize: '10px' }}>{d}</div>
          </div>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={Math.max(320, BINS * 14)}>
        <BarChart data={profileData} layout="vertical" margin={{ top: 0, right: 20, bottom: 0, left: 60 }}>
          <XAxis type="number" stroke={C.sub} tick={{ fontSize: 10 }} tickFormatter={v => v.toFixed(0)} />
          <YAxis type="category" dataKey="priceLabel" stroke={C.sub} tick={{ fontSize: 9, fill: C.sub }} width={60} />
          <Tooltip contentStyle={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '11px', color: C.text }} labelStyle={{ color: C.text }} itemStyle={{ color: C.text }} formatter={v => [v.toFixed(1), 'Volume']} labelFormatter={l => `Price: $${l}`} />
          <Bar dataKey="volume" maxBarSize={14} radius={[0, 3, 3, 0]}>
            {profileData.map((entry, i) => (
              <Cell key={i} fill={entry.isPoc ? C.orange : entry.inVA ? `${C.blue}cc` : `${C.border}88`} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div style={{ marginTop: '8px', display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
        {[[C.orange, 'POC – Highest Volume Price'], [`${C.blue}cc`, '70% Value Area'], [`${C.border}88`, 'Low Activity']].map(([bg, label]) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: C.sub }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '2px', background: bg, flexShrink: 0 }} />
            {label}
          </div>
        ))}
      </div>
    </div>
  );
}

function QuantLabPanel({ trades, themeMode }) {
  const [simulations, setSimulations] = useState(300);
  const [horizon, setHorizon] = useState(50);
  const [startingBalance, setStartingBalance] = useState('25000');
  const [riskPct, setRiskPct] = useState('1');
  const [entryPrice, setEntryPrice] = useState('');
  const [stopPrice, setStopPrice] = useState('');

  const pnlSeries = useMemo(
    () => trades
      .map((trade) => toNumber(trade.pnl, NaN))
      .filter((value) => Number.isFinite(value)),
    [trades]
  );

  const strategyMatrix = useMemo(() => {
    const matrix = {};
    trades.forEach((trade) => {
      const strategy = (trade.strategy || 'Unlabeled').trim() || 'Unlabeled';
      const market = (trade.market || 'Unknown').trim() || 'Unknown';
      const key = `${strategy}__${market}`;
      if (!matrix[key]) matrix[key] = { strategy, market, trades: 0, pnl: 0, wins: 0, losses: 0 };
      const pnl = toNumber(trade.pnl);
      matrix[key].trades += 1;
      matrix[key].pnl += pnl;
      if (pnl > 0) matrix[key].wins += 1;
      if (pnl < 0) matrix[key].losses += 1;
    });

    return Object.values(matrix)
      .map((row) => {
        const avgPnl = row.trades ? row.pnl / row.trades : 0;
        const winRate = row.trades ? (row.wins / row.trades) * 100 : 0;
        const profitFactor = row.losses === 0
          ? (row.wins > 0 ? Infinity : 0)
          : Math.abs(
              trades
                .filter((trade) => (trade.strategy || 'Unlabeled').trim() === row.strategy && (trade.market || 'Unknown').trim() === row.market && toNumber(trade.pnl) > 0)
                .reduce((sum, trade) => sum + toNumber(trade.pnl), 0) /
              trades
                .filter((trade) => (trade.strategy || 'Unlabeled').trim() === row.strategy && (trade.market || 'Unknown').trim() === row.market && toNumber(trade.pnl) < 0)
                .reduce((sum, trade) => sum + toNumber(trade.pnl), 0)
            );
        return {
          ...row,
          avgPnl,
          winRate,
          profitFactor,
        };
      })
      .sort((a, b) => b.avgPnl - a.avgPnl)
      .slice(0, 8);
  }, [trades]);

  const simulationOutput = useMemo(() => {
    if (pnlSeries.length < 5) return null;
    const pathLength = Math.max(10, Math.min(250, Number(horizon) || 50));
    const simCount = Math.max(100, Math.min(2000, Number(simulations) || 300));
    const startEquity = Math.max(0, toNumber(startingBalance, 25000));
    const percentile = (sorted, ratio) => {
      if (!sorted.length) return 0;
      const index = (sorted.length - 1) * ratio;
      const lower = Math.floor(index);
      const upper = Math.ceil(index);
      if (lower === upper) return sorted[lower];
      const weight = index - lower;
      return (sorted[lower] * (1 - weight)) + (sorted[upper] * weight);
    };

    const paths = [];
    const finals = [];
    const drawdowns = [];

    for (let sim = 0; sim < simCount; sim += 1) {
      let equity = startEquity;
      let peak = startEquity;
      let maxDrawdown = 0;
      const path = [];

      for (let step = 0; step < pathLength; step += 1) {
        const sample = pnlSeries[Math.floor(Math.random() * pnlSeries.length)];
        equity += sample;
        peak = Math.max(peak, equity);
        maxDrawdown = Math.max(maxDrawdown, peak - equity);
        path.push(equity);
      }

      paths.push(path);
      finals.push(equity);
      drawdowns.push(maxDrawdown);
    }

    finals.sort((a, b) => a - b);
    drawdowns.sort((a, b) => a - b);

    const percentilePath = Array.from({ length: pathLength }, (_, step) => {
      const values = paths.map((path) => path[step]).sort((a, b) => a - b);
      return {
        step: step + 1,
        p10: percentile(values, 0.1),
        median: percentile(values, 0.5),
        p90: percentile(values, 0.9),
      };
    });

    const profitableRuns = finals.filter((value) => value > startEquity).length;

    return {
      pathLength,
      simCount,
      startEquity,
      percentilePath,
      medianFinal: percentile(finals, 0.5),
      p10Final: percentile(finals, 0.1),
      p90Final: percentile(finals, 0.9),
      profitablePct: (profitableRuns / finals.length) * 100,
      medianDrawdown: percentile(drawdowns, 0.5),
      p90Drawdown: percentile(drawdowns, 0.9),
    };
  }, [horizon, pnlSeries, simulations, startingBalance]);

  const planner = useMemo(() => {
    const balance = Math.max(0, toNumber(startingBalance, 0));
    const pct = Math.max(0, toNumber(riskPct, 0));
    const entry = toNumber(entryPrice, NaN);
    const stop = toNumber(stopPrice, NaN);
    const riskAmount = balance * (pct / 100);
    const riskPerUnit = Number.isNaN(entry) || Number.isNaN(stop) ? NaN : Math.abs(entry - stop);
    const positionSize = Number.isNaN(riskPerUnit) || riskPerUnit <= 0 ? 0 : Math.floor(riskAmount / riskPerUnit);
    const capitalAtWork = Number.isNaN(entry) ? 0 : positionSize * entry;
    return { balance, pct, entry, stop, riskAmount, riskPerUnit, positionSize, capitalAtWork };
  }, [entryPrice, riskPct, startingBalance, stopPrice]);

  const averageTrade = pnlSeries.length ? pnlSeries.reduce((sum, value) => sum + value, 0) / pnlSeries.length : 0;
  const pnlStdDev = pnlSeries.length > 1
    ? Math.sqrt(pnlSeries.reduce((sum, value) => sum + ((value - averageTrade) ** 2), 0) / pnlSeries.length)
    : 0;
  const stabilityScore = pnlStdDev > 0 ? (averageTrade / pnlStdDev) * Math.sqrt(Math.max(pnlSeries.length, 1)) : 0;

  if (pnlSeries.length === 0) return null;

  return (
    <div style={{ ...cardStyle, padding: '22px', marginTop: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap', marginBottom: '18px' }}>
        <div>
          <div style={{ color: C.text, fontSize: '17px', fontWeight: '800' }}>Quant Lab</div>
          <div style={{ color: C.sub, fontSize: '12px', marginTop: '4px' }}>Forward-looking risk simulation, edge ranking, and position sizing from your actual journal history.</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(140px, 1fr))', gap: '8px', width: 'min(100%, 420px)' }}>
          <div>
            <div style={{ color: C.sub, fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '5px' }}>Start Balance</div>
            <input value={startingBalance} onChange={(e) => setStartingBalance(e.target.value)} style={{...inp}} inputMode="decimal" />
          </div>
          <div>
            <div style={{ color: C.sub, fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '5px' }}>Risk % / Trade</div>
            <input value={riskPct} onChange={(e) => setRiskPct(e.target.value)} style={{...inp}} inputMode="decimal" />
          </div>
          <div>
            <div style={{ color: C.sub, fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '5px' }}>Simulation Runs</div>
            <select value={simulations} onChange={(e) => setSimulations(Number(e.target.value))} style={{...inp}}>
              {[200, 300, 500, 1000].map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
          </div>
          <div>
            <div style={{ color: C.sub, fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '5px' }}>Trade Horizon</div>
            <select value={horizon} onChange={(e) => setHorizon(Number(e.target.value))} style={{...inp}}>
              {[20, 30, 50, 75, 100].map((value) => <option key={value} value={value}>{value} trades</option>)}
            </select>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '10px', marginBottom: '18px' }}>
        {[
          ['Avg Trade', `${averageTrade >= 0 ? '+' : ''}$${averageTrade.toFixed(2)}`, averageTrade >= 0 ? C.greenBright : C.redBright],
          ['Trade Volatility', `$${pnlStdDev.toFixed(2)}`, C.orange],
          ['Stability Score', stabilityScore.toFixed(2), stabilityScore >= 0 ? C.blueBright : C.redBright],
          ['Planner Risk $', `$${planner.riskAmount.toFixed(2)}`, C.blueBright],
        ].map(([label, value, color]) => (
          <div key={label} style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '14px' }}>
            <div style={{ color: C.sub, fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '6px' }}>{label}</div>
            <div style={{ color, fontSize: '18px', fontWeight: '800' }}>{value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.6fr) minmax(320px, 1fr)', gap: '16px', alignItems: 'start' }}>
        <div style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
            <div>
              <div style={{ color: C.sub, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px' }}>Monte Carlo Equity Paths</div>
              <div style={{ color: C.sub, fontSize: '11px', marginTop: '3px' }}>{simulationOutput ? `${simulationOutput.simCount} runs across ${simulationOutput.pathLength} trades` : 'Need more trades to simulate.'}</div>
            </div>
          </div>
          {simulationOutput ? (
            <>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={simulationOutput.percentilePath}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                  <XAxis dataKey="step" stroke={C.sub} tick={{ fontSize: 10 }} />
                  <YAxis stroke={C.sub} tick={{ fontSize: 10 }} tickFormatter={(value) => `$${Math.round(value)}`} />
                  <Tooltip
                    contentStyle={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '12px', color: C.text }} labelStyle={{ color: C.text }} itemStyle={{ color: C.text }}
                    formatter={(value, name) => [`$${toNumber(value).toFixed(2)}`, name === 'median' ? 'Median' : name === 'p10' ? '10th %ile' : '90th %ile']}
                    labelFormatter={(label) => `Trade ${label}`}
                  />
                  <Line type="monotone" dataKey="p10" stroke={C.redBright} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="median" stroke={C.blueBright} strokeWidth={2.5} dot={false} />
                  <Line type="monotone" dataKey="p90" stroke={C.greenBright} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginTop: '14px' }}>
                {[
                  ['Median Finish', simulationOutput.medianFinal, C.blueBright],
                  ['10th %ile', simulationOutput.p10Final, C.redBright],
                  ['90th %ile', simulationOutput.p90Final, C.greenBright],
                  ['Profitable Runs', simulationOutput.profitablePct, C.text],
                  ['Median Max DD', simulationOutput.medianDrawdown, C.orange],
                  ['90th %ile DD', simulationOutput.p90Drawdown, C.redBright],
                ].map(([label, value, color]) => (
                  <div key={label} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px' }}>
                    <div style={{ color: C.sub, fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '4px' }}>{label}</div>
                    <div style={{ color, fontSize: '15px', fontWeight: '800' }}>
                      {label === 'Profitable Runs' ? `${toNumber(value).toFixed(1)}%` : `$${toNumber(value).toFixed(2)}`}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ color: C.sub, fontSize: '12px' }}>Add at least 5 trades to unlock Monte Carlo simulation.</div>
          )}
        </div>

        <div style={{ display: 'grid', gap: '16px' }}>
          <div style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '16px' }}>
            <div style={{ color: C.sub, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>Risk Planner</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
              <div>
                <div style={{ color: C.sub, fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '5px' }}>Entry</div>
                <input value={entryPrice} onChange={(e) => setEntryPrice(e.target.value)} style={{...inp}} inputMode="decimal" placeholder="185.50" />
              </div>
              <div>
                <div style={{ color: C.sub, fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '5px' }}>Stop</div>
                <input value={stopPrice} onChange={(e) => setStopPrice(e.target.value)} style={{...inp}} inputMode="decimal" placeholder="182.90" />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px' }}>
              {[
                ['Risk Amount', `$${planner.riskAmount.toFixed(2)}`, C.blueBright],
                ['Risk / Unit', planner.riskPerUnit > 0 ? `$${planner.riskPerUnit.toFixed(2)}` : '—', C.orange],
                ['Suggested Size', planner.positionSize ? `${planner.positionSize} units` : '—', C.greenBright],
                ['Capital Used', planner.capitalAtWork ? `$${planner.capitalAtWork.toFixed(2)}` : '—', C.text],
              ].map(([label, value, color]) => (
                <div key={label} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px' }}>
                  <div style={{ color: C.sub, fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '4px' }}>{label}</div>
                  <div style={{ color, fontSize: '15px', fontWeight: '800' }}>{value}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '16px' }}>
            <div style={{ color: C.sub, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>Top Strategy Edges</div>
            {strategyMatrix.length === 0 && <div style={{ color: C.sub, fontSize: '12px' }}>Add strategy and market tags to unlock the edge matrix.</div>}
            {strategyMatrix.map((row) => (
              <div key={`${row.strategy}-${row.market}`} style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.7fr 0.8fr 0.8fr', gap: '8px', padding: '8px 0', borderBottom: `1px solid ${C.border}`, fontSize: '12px', alignItems: 'center' }}>
                <div>
                  <div style={{ color: C.text, fontWeight: '700' }}>{row.strategy}</div>
                  <div style={{ color: C.sub, fontSize: '10px' }}>{row.market}</div>
                </div>
                <div style={{ color: C.sub }}>{row.trades} trades</div>
                <div style={{ color: C.blueBright }}>{row.winRate.toFixed(1)}%</div>
                <div style={{ color: row.avgPnl >= 0 ? C.greenBright : C.redBright, fontWeight: '800' }}>
                  {row.avgPnl >= 0 ? '+' : ''}${row.avgPnl.toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function SymbolBreakdown({ trades }) {
  const symbolMap = {};
  trades.forEach(t => {
    const sym = t.asset || 'Unknown';
    if (!symbolMap[sym]) symbolMap[sym] = { pnl: 0, trades: 0, wins: 0 };
    const pnl = toNumber(t.pnl);
    symbolMap[sym].pnl += pnl;
    symbolMap[sym].trades += 1;
    if (pnl > 0) symbolMap[sym].wins += 1;
  });
  const data = Object.entries(symbolMap)
    .map(([symbol, v]) => ({ symbol, pnl: parseFloat(v.pnl.toFixed(2)), trades: v.trades, winRate: v.trades ? parseFloat(((v.wins / v.trades) * 100).toFixed(1)) : 0 }))
    .sort((a, b) => Math.abs(b.pnl) - Math.abs(a.pnl))
    .slice(0, 10);
  if (data.length === 0) return null;
  return (
    <div style={{ ...cardStyle, padding: '20px', marginTop: '16px' }}>
      <div style={{ color: C.sub, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '14px' }}>Symbol P&L Breakdown</div>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
          <XAxis dataKey="symbol" stroke={C.sub} tick={{ fontSize: 9 }} angle={-35} textAnchor="end" height={60} tickFormatter={v => v.length > 12 ? v.slice(0, 12) + '…' : v} />
          <YAxis stroke={C.sub} tick={{ fontSize: 10 }} tickFormatter={v => `$${v}`} />
          <Tooltip contentStyle={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '12px', color: C.text }} labelStyle={{ color: C.text }} itemStyle={{ color: C.text }} formatter={(v, name) => name === 'pnl' ? [`$${v.toFixed(2)}`, 'P&L'] : [`${v}%`, 'Win Rate']} labelFormatter={l => l} />
          <ReferenceLine y={0} stroke={C.border} />
          <Bar dataKey="pnl" maxBarSize={40} radius={[4, 4, 0, 0]}>
            {data.map((e, i) => <Cell key={i} fill={e.pnl >= 0 ? C.greenBright : C.redBright} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function RMultipleHistogram({ trades }) {
  const rValues = trades.map(t => {
    const entry = toNumber(t.entry, NaN), exit = toNumber(t.exit, NaN), stop = toNumber(t.stopLoss, NaN);
    if (Number.isNaN(entry) || Number.isNaN(exit) || Number.isNaN(stop) || stop === 0 || entry === stop) return null;
    const risk = Math.abs(entry - stop);
    const move = t.type === 'Sell' ? (entry - exit) : (exit - entry);
    return move / risk;
  }).filter(r => r !== null && Number.isFinite(r));
  if (rValues.length < 2) return null;
  const step = 0.5;
  const buckets = [];
  for (let r = -4; r < 8; r += step) {
    buckets.push({ label: `${r >= 0 ? '+' : ''}${r.toFixed(1)}R`, rVal: r, count: rValues.filter(v => v >= r && v < r + step).length });
  }
  const avgR = rValues.reduce((s, v) => s + v, 0) / rValues.length;
  return (
    <div style={{ ...cardStyle, padding: '20px', marginTop: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
        <div>
          <div style={{ color: C.sub, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px' }}>R-Multiple Distribution</div>
          <div style={{ color: C.sub, fontSize: '11px', marginTop: '2px' }}>{rValues.length} trades with stop loss set</div>
        </div>
        <div style={{ background: avgR >= 0 ? 'rgba(22,199,132,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${avgR >= 0 ? C.green : C.red}55`, borderRadius: '8px', padding: '6px 12px' }}>
          <span style={{ color: avgR >= 0 ? C.greenBright : C.redBright, fontWeight: '800', fontSize: '13px' }}>Avg {avgR >= 0 ? '+' : ''}{avgR.toFixed(2)}R</span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={buckets}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
          <XAxis dataKey="label" stroke={C.sub} tick={{ fontSize: 9 }} interval={1} />
          <YAxis stroke={C.sub} tick={{ fontSize: 10 }} allowDecimals={false} />
          <Tooltip contentStyle={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '12px', color: C.text }} labelStyle={{ color: C.text }} itemStyle={{ color: C.text }} formatter={v => [v, 'Trades']} />
          <ReferenceLine x="+0.0R" stroke={C.border} />
          <Bar dataKey="count" maxBarSize={22} radius={[3, 3, 0, 0]}>
            {buckets.map((e, i) => <Cell key={i} fill={e.rVal >= 0 ? `${C.green}cc` : `${C.red}cc`} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Confetti ────────────────────────────────────────────────────────────────
function fireConfetti(canvas) {
  const ctx = canvas.getContext('2d');
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
  const pieces = Array.from({ length: 120 }, () => ({
    x: Math.random() * canvas.width,
    y: Math.random() * canvas.height - canvas.height,
    r: Math.random() * 7 + 4,
    d: Math.random() * 120 + 20,
    color: ['#0ecb81','#2f8cff','#8b7bff','#f59e0b','#39e5a5','#7cb6ff'][Math.floor(Math.random()*6)],
    tilt: Math.random() * 10 - 10,
    tiltAngleIncremental: (Math.random() * 0.07) + 0.05,
    tiltAngle: 0,
  }));
  let angle = 0;
  let frame;
  let ttl = 180;
  function draw() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    angle += 0.01;
    ttl--;
    pieces.forEach(p => {
      p.tiltAngle += p.tiltAngleIncremental;
      p.y += (Math.cos(angle + p.d) + 3 + p.r / 2) * 0.7;
      p.tilt = Math.sin(p.tiltAngle) * 15;
      ctx.beginPath();
      ctx.lineWidth = p.r / 2;
      ctx.strokeStyle = p.color;
      ctx.moveTo(p.x + p.tilt + p.r / 4, p.y);
      ctx.lineTo(p.x + p.tilt, p.y + p.tilt + p.r / 4);
      ctx.stroke();
    });
    if (ttl > 0) frame = requestAnimationFrame(draw);
    else ctx.clearRect(0, 0, canvas.width, canvas.height);
  }
  draw();
  return () => cancelAnimationFrame(frame);
}

// ── AI Assistant ─────────────────────────────────────────────────────────────
function AIAssistant({ trades }) {
  const [open, setOpen] = React.useState(false);
  const [input, setInput] = React.useState('');
  const [history, setHistory] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const bottomRef = React.useRef(null);

  React.useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [history, open]);

  const buildContext = () => {
    if (!trades || trades.length === 0) return 'No trades logged yet.';
    const toNum = v => { const n = parseFloat(v); return isFinite(n) ? n : 0; };
    const wins = trades.filter(t => toNum(t.pnl) > 0);
    const losses = trades.filter(t => toNum(t.pnl) < 0);
    const totalPnl = trades.reduce((s, t) => s + toNum(t.pnl), 0);
    const winRate = trades.length ? (wins.length / trades.length * 100).toFixed(1) : 0;
    const avgWin = wins.length ? (wins.reduce((s,t) => s+toNum(t.pnl),0)/wins.length).toFixed(2) : 0;
    const avgLoss = losses.length ? (Math.abs(losses.reduce((s,t) => s+toNum(t.pnl),0)/losses.length)).toFixed(2) : 0;
    const symbols = [...new Set(trades.map(t => t.asset).filter(Boolean))].slice(0, 8).join(', ');
    const recent = [...trades].slice(-5).map(t => `${t.asset} ${toNum(t.pnl)>=0?'+':''}$${toNum(t.pnl).toFixed(2)}`).join(', ');
    return `Total trades: ${trades.length} | Win rate: ${winRate}% | Total P&L: $${totalPnl.toFixed(2)} | Avg win: $${avgWin} | Avg loss: $${avgLoss} | Traded symbols: ${symbols} | Recent: ${recent}`;
  };

  const SUGGESTIONS = ['How is my win rate?','What are my weaknesses?','Which symbol performs best?','How can I improve my psychology?'];

  const send = async (msg) => {
    const text = msg || input.trim();
    if (!text || loading) return;
    setInput('');
    const userMsg = { role: 'user', content: text };
    const newHistory = [...history, userMsg];
    setHistory(newHistory);
    setLoading(true);
    try {
      const r = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, tradesContext: buildContext(), history: history.slice(-8) }),
      });
      const data = await r.json();
      setHistory(h => [...h, { role: 'assistant', content: data.reply || data.error || 'No response.' }]);
    } catch (e) {
      setHistory(h => [...h, { role: 'assistant', content: 'Connection error. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          width: 56, height: 56, borderRadius: '50%',
          background: 'linear-gradient(135deg,#388bfd,#8957e5)',
          border: 'none', cursor: 'pointer', boxShadow: '0 4px 20px rgba(56,139,253,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24,
          transition: 'transform 0.2s',
        }}
        title="AI Trading Assistant"
      >
        {open ? '✕' : '🤖'}
      </button>

      {/* Chat panel */}
      {open && (
        <div style={{
          position: 'fixed', bottom: 90, right: 24, zIndex: 9998,
          width: 360, height: 500, borderRadius: 16,
          background: 'linear-gradient(160deg,#0f1725,#08101d)',
          border: '1px solid #22314a', boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: C.font,
        }}>
          {/* Header */}
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #22314a', background: 'linear-gradient(90deg,#1a2640,#0f1725)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg,#388bfd,#8957e5)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🤖</div>
            <div>
              <div style={{ color: '#e8eefb', fontWeight: 800, fontSize: 14 }}>AI Trading Coach</div>
              <div style={{ color: '#95a5c0', fontSize: 10 }}>Powered by Claude · Knows your trades</div>
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {history.length === 0 && (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>📊</div>
                <div style={{ color: '#e8eefb', fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Your AI Trading Coach</div>
                <div style={{ color: '#95a5c0', fontSize: 12, marginBottom: 16 }}>Ask me anything about your trading performance</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {SUGGESTIONS.map(s => (
                    <button key={s} onClick={() => send(s)} style={{ padding: '8px 12px', background: 'rgba(47,140,255,0.1)', border: '1px solid rgba(47,140,255,0.3)', borderRadius: 8, color: '#7cb6ff', fontSize: 12, cursor: 'pointer', textAlign: 'left' }}>{s}</button>
                  ))}
                </div>
              </div>
            )}
            {history.map((m, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  maxWidth: '80%', padding: '9px 13px', borderRadius: m.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                  background: m.role === 'user' ? 'linear-gradient(135deg,#388bfd,#2563eb)' : '#131d2d',
                  border: m.role === 'assistant' ? '1px solid #22314a' : 'none',
                  color: '#e8eefb', fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap',
                }}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{ padding: '9px 13px', background: '#131d2d', border: '1px solid #22314a', borderRadius: '14px 14px 14px 4px', color: '#95a5c0', fontSize: 13 }}>
                  <span style={{ animation: 'pulse 1s infinite' }}>Thinking…</span>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div style={{ padding: '10px 12px', borderTop: '1px solid #22314a', display: 'flex', gap: 8 }}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
              placeholder="Ask about your trading…"
              style={{ flex: 1, padding: '9px 12px', background: '#0b1424', border: '1px solid #22314a', borderRadius: 10, color: '#e8eefb', fontSize: 13, outline: 'none' }}
            />
            <button onClick={() => send()} disabled={loading || !input.trim()} style={{ padding: '9px 14px', background: input.trim() ? '#2f8cff' : '#131d2d', border: 'none', borderRadius: 10, color: input.trim() ? '#fff' : '#95a5c0', cursor: input.trim() ? 'pointer' : 'default', fontWeight: 700, fontSize: 13 }}>
              ↑
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ── News Panel ────────────────────────────────────────────────────────────────
function NewsPanel({ trades }) {
  const [articles, setArticles] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [filter, setFilter] = React.useState('all');
  const [watchInput, setWatchInput] = React.useState('');
  const [watchList, setWatchList] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem('newsWatchList') || '[]'); } catch { return []; }
  });

  const tradedSymbols = React.useMemo(() => {
    return [...new Set(trades.map(t => (t.asset || '').split(' ')[0].toUpperCase()).filter(Boolean))].slice(0, 6);
  }, [trades]);

  const allSymbols = React.useMemo(() => {
    return [...new Set([...tradedSymbols, ...watchList])].slice(0, 12);
  }, [tradedSymbols, watchList]);

  const symbolsKey = allSymbols.join(',');

  React.useEffect(() => {
    if (!symbolsKey) return;
    setLoading(true);
    setError('');
    fetch(`/api/news?symbols=${symbolsKey}`)
      .then(r => r.json())
      .then(d => { setArticles(d.articles || []); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [symbolsKey]);

  const addToWatchList = () => {
    const sym = watchInput.trim().toUpperCase().replace(/[^A-Z]/g, '');
    if (!sym || watchList.includes(sym)) { setWatchInput(''); return; }
    const updated = [...watchList, sym];
    setWatchList(updated);
    localStorage.setItem('newsWatchList', JSON.stringify(updated));
    setWatchInput('');
  };

  const removeFromWatchList = (sym) => {
    const updated = watchList.filter(s => s !== sym);
    setWatchList(updated);
    localStorage.setItem('newsWatchList', JSON.stringify(updated));
    if (filter === sym) setFilter('all');
  };

  const filtered = filter === 'all' ? articles : articles.filter(a => a.symbol === filter);
  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr);
    const h = Math.floor(diff / 3600000);
    if (h < 1) return `${Math.floor(diff / 60000)}m ago`;
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  return (
    <div style={{ fontFamily: C.font }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ color: C.text, fontSize: 18, fontWeight: 800 }}>Market News</div>
          <div style={{ color: C.sub, fontSize: 12 }}>Trusted sources: Yahoo Finance, CNBC, Bloomberg, Reuters, WSJ, FT & more</div>
        </div>
        {/* Watchlist input */}
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            value={watchInput}
            onChange={e => setWatchInput(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === 'Enter' && addToWatchList()}
            placeholder="Add symbol, e.g. NVDA"
            style={{ ...inp, width: 160, fontSize: 12, padding: '7px 10px' }}
          />
          <button onClick={addToWatchList} style={{ padding: '7px 14px', background: C.blue, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>+ Add</button>
        </div>
      </div>

      {/* Watchlist badges */}
      {watchList.length > 0 && (
        <div style={{ ...cardStyle, padding: '12px 16px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ color: C.sub, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px' }}>Watchlist:</span>
          {watchList.map(sym => (
            <span key={sym} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 10px', background: `${C.purple}22`, border: `1px solid ${C.purple}55`, borderRadius: 20 }}>
              <span style={{ color: C.purple, fontSize: 11, fontWeight: 700 }}>{sym}</span>
              <button onClick={() => removeFromWatchList(sym)} style={{ background: 'none', border: 'none', color: C.sub, cursor: 'pointer', fontSize: 12, padding: 0, lineHeight: 1 }}>✕</button>
            </span>
          ))}
        </div>
      )}

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {['all', ...allSymbols].map(sym => (
          <button key={sym} onClick={() => setFilter(sym)} style={{
            padding: '5px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: 'pointer',
            border: `1px solid ${filter === sym ? C.blue : C.border}`,
            background: filter === sym ? `${C.blue}22` : C.card2,
            color: filter === sym ? C.blueBright : C.sub,
          }}>{sym === 'all' ? 'All' : sym}</button>
        ))}
      </div>

      {allSymbols.length === 0 && (
        <div style={{ ...cardStyle, padding: 40, textAlign: 'center', color: C.sub }}>Add a symbol above or log trades to see news.</div>
      )}
      {loading && <div style={{ color: C.sub, textAlign: 'center', padding: 40 }}>Loading news…</div>}
      {error && <div style={{ color: C.redBright, padding: 16 }}>Failed to load news: {error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 12 }}>
        {filtered.map((a, i) => (
          <a key={i} href={a.link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
            <div style={{ ...cardStyle, padding: '16px 18px', cursor: 'pointer', transition: 'border-color 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = C.blue}
              onMouseLeave={e => e.currentTarget.style.borderColor = '#22314a'}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ padding: '2px 8px', background: watchList.includes(a.symbol) ? `${C.purple}22` : `${C.blue}22`, border: `1px solid ${watchList.includes(a.symbol) ? C.purple : C.blue}55`, borderRadius: 20, color: watchList.includes(a.symbol) ? C.purple : C.blueBright, fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{a.symbol}</span>
                  {a.source && <span style={{ padding: '2px 8px', background: `${C.orange}18`, border: `1px solid ${C.orange}44`, borderRadius: 20, color: C.orange, fontSize: 9, fontWeight: 700, flexShrink: 0, letterSpacing: '0.3px' }}>{a.source}</span>}
                </div>
                <span style={{ color: C.sub, fontSize: 10, flexShrink: 0 }}>{a.pubDate ? timeAgo(a.pubDate) : ''}</span>
              </div>
              <div style={{ color: C.text, fontWeight: 700, fontSize: 13, lineHeight: 1.4, marginBottom: 6 }}>{a.title}</div>
              {a.description && <div style={{ color: C.sub, fontSize: 11, lineHeight: 1.5 }}>{a.description}…</div>}
            </div>
          </a>
        ))}
      </div>

      {!loading && filtered.length === 0 && allSymbols.length > 0 && !error && (
        <div style={{ color: C.sub, textAlign: 'center', padding: 40 }}>No articles found for selected filter.</div>
      )}
    </div>
  );
}

function PreMarketGainersPanel() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [minPct, setMinPct] = useState(1);
  const [dirFilter, setDirFilter] = useState('gainers');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [selectedStock, setSelectedStock] = useState(null);

  const fetchPreMarket = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const BATCH = 40;
      let all = [];
      for (let i = 0; i < GAP_SCANNER_LARGE_CAPS.length; i += BATCH) {
        const syms = GAP_SCANNER_LARGE_CAPS.slice(i, i + BATCH).join(',');
        const res = await fetch(`/api/gap-scanner?symbols=${encodeURIComponent(syms)}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        all = [...all, ...(json?.quoteResponse?.result || [])];
        if (i + BATCH < GAP_SCANNER_LARGE_CAPS.length) await new Promise(r => setTimeout(r, 250));
      }
      setQuotes(all);
      setLastRefresh(new Date());
    } catch (err) {
      setError(`Unable to fetch pre-market data: ${err.message}`);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPreMarket();
    const iv = setInterval(fetchPreMarket, 5 * 60 * 1000);
    return () => clearInterval(iv);
  }, [fetchPreMarket]);

  const fmtVol = v => v >= 1e9 ? `${(v / 1e9).toFixed(2)}B` : v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(0)}K` : `${v || '—'}`;

  const movers = quotes.map(q => {
    const pmPrice = q.preMarketPrice;
    const pmChangePct = q.preMarketChangePercent;
    const pmChange = q.preMarketChange;
    const prevClose = q.regularMarketPreviousClose;
    const pmVol = q.preMarketVolume;
    const high = q.regularMarketDayHigh;
    const low = q.regularMarketDayLow;
    if (!pmPrice || pmChangePct == null || Math.abs(pmChangePct) < minPct) return null;
    if (dirFilter === 'gainers' && pmChangePct <= 0) return null;
    if (dirFilter === 'decliners' && pmChangePct >= 0) return null;
    return {
      symbol: q.symbol,
      name: (q.shortName || q.symbol || '').slice(0, 22),
      pmPrice: pmPrice.toFixed(2),
      prevClose: prevClose != null ? prevClose.toFixed(2) : null,
      pmChange: pmChange != null ? pmChange : 0,
      pmChangePct: parseFloat(pmChangePct.toFixed(2)),
      up: pmChangePct > 0,
      vol: fmtVol(pmVol),
      high: high != null ? high.toFixed(2) : null,
      low: low != null ? low.toFixed(2) : null,
      chartData: prevClose && pmPrice ? [
        { name: 'Prev Close', price: parseFloat(prevClose.toFixed(2)), fill: C.sub },
        { name: 'Pre-Mkt', price: parseFloat(pmPrice.toFixed(2)), fill: pmChangePct > 0 ? C.greenBright : C.redBright },
      ] : [],
    };
  }).filter(Boolean).sort((a, b) =>
    dirFilter === 'decliners'
      ? a.pmChangePct - b.pmChangePct
      : b.pmChangePct - a.pmChangePct
  );

  const barChartData = movers.slice(0, 12).map(g => ({ symbol: g.symbol, pct: g.pmChangePct }));

  return (
    <div>
      <div style={{ ...cardStyle, padding: '20px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
          <div>
            <div style={{ color: C.text, fontSize: '16px', fontWeight: '800' }}>🌅 Pre-Market Movers</div>
            <div style={{ color: C.sub, fontSize: '11px', marginTop: '3px' }}>
              {GAP_SCANNER_LARGE_CAPS.length} stocks tracked · Auto-refreshes every 5 min{lastRefresh ? ` · Updated ${lastRefresh.toLocaleTimeString()}` : ''}
            </div>
          </div>
          <button onClick={fetchPreMarket} disabled={loading} style={{ padding: '9px 18px', background: loading ? C.card2 : C.blue, color: loading ? C.sub : '#fff', border: 'none', borderRadius: '8px', cursor: loading ? 'default' : 'pointer', fontWeight: '700', fontSize: '13px' }}>
            {loading ? '⏳ Scanning...' : '🔄 Refresh Now'}
          </button>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {[
            ['Min Change %', <select key="pct" value={minPct} onChange={e => setMinPct(+e.target.value)} style={{ ...inp, width: '120px', padding: '7px 8px', fontSize: '12px' }}>
              {[0.5, 1, 2, 3, 5].map(v => <option key={v} value={v}>±{v}%</option>)}
            </select>],
            ['Show', <select key="dir" value={dirFilter} onChange={e => setDirFilter(e.target.value)} style={{ ...inp, width: '140px', padding: '7px 8px', fontSize: '12px' }}>
              <option value="gainers">Gainers ↑ Only</option>
              <option value="decliners">Decliners ↓ Only</option>
              <option value="both">Both ↑↓</option>
            </select>],
          ].map(([label, ctrl]) => (
            <div key={label}>
              <div style={{ color: C.sub, fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '5px' }}>{label}</div>
              {ctrl}
            </div>
          ))}
        </div>
      </div>

      {error && (
        <div style={{ ...cardStyle, padding: '14px', marginBottom: '16px', borderColor: `${C.red}66` }}>
          <div style={{ color: C.redBright, fontSize: '12px' }}>⚠ {error}</div>
        </div>
      )}

      {loading && quotes.length === 0 && (
        <div style={{ ...cardStyle, padding: '60px', textAlign: 'center', marginBottom: '16px' }}>
          <div style={{ color: C.sub, fontSize: '14px', marginBottom: '8px' }}>⏳ Fetching pre-market data for {GAP_SCANNER_LARGE_CAPS.length} stocks...</div>
          <div style={{ color: C.sub, fontSize: '12px' }}>This may take a few seconds</div>
        </div>
      )}

      {movers.length > 0 && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '16px' }}>
            {[
              ['Total Found', movers.length, C.text],
              ['Gainers ↑', movers.filter(g => g.up).length, C.greenBright],
              ['Decliners ↓', movers.filter(g => !g.up).length, C.redBright],
              ['Top Gainer', movers.filter(g=>g.up)[0] ? `${movers.filter(g=>g.up)[0].symbol} +${movers.filter(g=>g.up)[0].pmChangePct}%` : '—', C.orange],
              ['Top Decliner', movers.filter(g=>!g.up).slice(-1)[0] ? `${movers.filter(g=>!g.up).slice(-1)[0].symbol} ${movers.filter(g=>!g.up).slice(-1)[0].pmChangePct}%` : '—', C.redBright],
            ].map(([l, v, c]) => (
              <div key={l} style={{ ...cardStyle, padding: '14px' }}>
                <div style={{ color: C.sub, fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '4px' }}>{l}</div>
                <div style={{ color: c, fontSize: l === 'Total Found' ? '22px' : '14px', fontWeight: '800', lineHeight: '1.2' }}>{v}</div>
              </div>
            ))}
          </div>

          {barChartData.length > 0 && (
            <div style={{ ...cardStyle, padding: '20px', marginBottom: '16px' }}>
              <div style={{ color: C.text, fontSize: '13px', fontWeight: '700', marginBottom: '14px' }}>Top Pre-Market Movers — % Change vs Previous Close</div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={barChartData} margin={{ top: 4, right: 10, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={`${C.border}55`} vertical={false} />
                  <XAxis dataKey="symbol" tick={{ fill: C.sub, fontSize: 11, fontWeight: 700 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: C.sub, fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `${v > 0 ? '+' : ''}${v}%`} width={48} />
                  <Tooltip
                    contentStyle={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '12px', color: C.text }}
                    formatter={v => [`${v > 0 ? '+' : ''}${v.toFixed(2)}%`, 'Pre-Market Change']}
                    labelStyle={{ color: C.text, fontWeight: 700 }}
                  />
                  <ReferenceLine y={0} stroke={C.border} strokeWidth={1} />
                  <Bar dataKey="pct" radius={[4, 4, 0, 0]}>
                    {barChartData.map((entry, idx) => (
                      <Cell key={idx} fill={entry.pct >= 0 ? C.greenBright : C.redBright} fillOpacity={0.9} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: '12px' }}>
            {movers.map(g => (
              <div
                key={g.symbol}
                onClick={() => setSelectedStock(selectedStock === g.symbol ? null : g.symbol)}
                style={{
                  ...cardStyle,
                  padding: '16px',
                  cursor: 'pointer',
                  transition: 'transform 0.15s, border-color 0.15s',
                  borderColor: selectedStock === g.symbol ? (g.up ? C.greenBright : C.redBright) : C.border,
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                  <div>
                    <div style={{ color: C.text, fontSize: '15px', fontWeight: '800' }}>{g.symbol}</div>
                    <div style={{ color: C.sub, fontSize: '10px', marginTop: '1px' }}>{g.name}</div>
                  </div>
                  <span style={{
                    padding: '4px 10px', borderRadius: '20px', fontSize: '13px', fontWeight: '800',
                    background: g.up ? `${C.green}22` : `${C.red}22`,
                    color: g.up ? C.greenBright : C.redBright,
                  }}>
                    {g.up ? '▲' : '▼'} {g.up ? '+' : ''}{g.pmChangePct}%
                  </span>
                </div>

                <div style={{ display: 'flex', gap: '14px', marginBottom: '10px', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ color: C.sub, fontSize: '9px', fontWeight: '700', textTransform: 'uppercase' }}>PM Price</div>
                    <div style={{ color: C.text, fontSize: '15px', fontWeight: '800' }}>${g.pmPrice}</div>
                  </div>
                  <div>
                    <div style={{ color: C.sub, fontSize: '9px', fontWeight: '700', textTransform: 'uppercase' }}>Change</div>
                    <div style={{ color: g.up ? C.greenBright : C.redBright, fontSize: '13px', fontWeight: '700' }}>
                      {g.pmChange >= 0 ? '+' : ''}{g.pmChange.toFixed ? g.pmChange.toFixed(2) : g.pmChange}
                    </div>
                  </div>
                  {g.prevClose && (
                    <div>
                      <div style={{ color: C.sub, fontSize: '9px', fontWeight: '700', textTransform: 'uppercase' }}>Prev Close</div>
                      <div style={{ color: C.sub, fontSize: '12px', fontWeight: '600' }}>${g.prevClose}</div>
                    </div>
                  )}
                  <div>
                    <div style={{ color: C.sub, fontSize: '9px', fontWeight: '700', textTransform: 'uppercase' }}>Vol</div>
                    <div style={{ color: C.sub, fontSize: '12px', fontWeight: '600' }}>{g.vol}</div>
                  </div>
                </div>

                {g.chartData.length === 2 && (
                  <div style={{ marginTop: '4px' }}>
                    <ResponsiveContainer width="100%" height={70}>
                      <BarChart data={g.chartData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }} barSize={40}>
                        <XAxis dataKey="name" tick={{ fill: C.sub, fontSize: 9, fontWeight: 600 }} axisLine={false} tickLine={false} />
                        <Tooltip
                          contentStyle={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: '6px', fontSize: '11px' }}
                          formatter={v => [`$${v}`, '']}
                          labelStyle={{ color: C.sub, fontSize: '10px' }}
                        />
                        <Bar dataKey="price" radius={[3, 3, 0, 0]}>
                          {g.chartData.map((entry, idx) => (
                            <Cell key={idx} fill={entry.fill} fillOpacity={0.85} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {g.high && g.low && (
                  <div style={{ display: 'flex', gap: '12px', marginTop: '6px', paddingTop: '8px', borderTop: `1px solid ${C.border}` }}>
                    <span style={{ color: C.sub, fontSize: '10px' }}>
                      Day H: <span style={{ color: C.greenBright, fontWeight: '700' }}>${g.high}</span>
                    </span>
                    <span style={{ color: C.sub, fontSize: '10px' }}>
                      Day L: <span style={{ color: C.redBright, fontWeight: '700' }}>${g.low}</span>
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {!loading && movers.length === 0 && quotes.length > 0 && (
        <div style={{ ...cardStyle, padding: '40px', textAlign: 'center' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>🌙</div>
          <div style={{ color: C.text, fontSize: '14px', fontWeight: '700', marginBottom: '8px' }}>No pre-market movers found</div>
          <div style={{ color: C.sub, fontSize: '12px' }}>
            Pre-market data may not be available yet (markets open 4:00 AM–9:30 AM ET)<br/>
            Try lowering the Min Change % filter or selecting "Both ↑↓"
          </div>
        </div>
      )}
    </div>
  );
}

function GapScannerPanel() {
  const [rawQuotes, setRawQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [minGap, setMinGap] = useState(2);
  const [minVolRatio, setMinVolRatio] = useState(2);
  const [direction, setDirection] = useState('both');
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const BATCH = 40;
      let all = [];
      for (let i = 0; i < GAP_SCANNER_LARGE_CAPS.length; i += BATCH) {
        const syms = GAP_SCANNER_LARGE_CAPS.slice(i, i + BATCH).join(',');
        const res = await fetch(`/api/gap-scanner?symbols=${encodeURIComponent(syms)}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        all = [...all, ...(json?.quoteResponse?.result || [])];
        if (i + BATCH < GAP_SCANNER_LARGE_CAPS.length) await new Promise(r => setTimeout(r, 250));
      }
      setRawQuotes(all);
      setLastRefresh(new Date());
    } catch (err) {
      setError(`Unable to fetch market data: ${err.message}. Market data may be temporarily unavailable.`);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchQuotes();
    const iv = setInterval(fetchQuotes, 5 * 60 * 1000);
    return () => clearInterval(iv);
  }, [fetchQuotes]);

  const fmtVol = v => v >= 1e9 ? `${(v / 1e9).toFixed(2)}B` : v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(0)}K` : `${v || '—'}`;

  const results = rawQuotes.map(q => {
    const open = q.regularMarketOpen;
    const prev = q.regularMarketPreviousClose;
    const vol = q.regularMarketVolume;
    const avg = q.averageDailyVolume10Day || q.averageDailyVolume3Month || 1;
    if (!open || !prev || prev === 0 || !vol) return null;
    const gapPct = ((open - prev) / prev) * 100;
    const volRatio = vol / avg;
    return {
      symbol: q.symbol,
      name: (q.shortName || q.symbol || '').slice(0, 24),
      price: q.regularMarketPrice != null ? q.regularMarketPrice.toFixed(2) : '—',
      open: open.toFixed(2),
      prev: prev.toFixed(2),
      high: q.regularMarketDayHigh != null ? q.regularMarketDayHigh.toFixed(2) : '—',
      low: q.regularMarketDayLow != null ? q.regularMarketDayLow.toFixed(2) : '—',
      gapPct: gapPct.toFixed(2),
      vol: fmtVol(vol),
      volRatio: volRatio.toFixed(1),
      up: gapPct > 0,
    };
  }).filter(q => {
    if (!q) return false;
    const g = Math.abs(parseFloat(q.gapPct));
    const r = parseFloat(q.volRatio);
    if (g < minGap || r < minVolRatio) return false;
    if (direction === 'up' && !q.up) return false;
    if (direction === 'down' && q.up) return false;
    return true;
  }).sort((a, b) => Math.abs(parseFloat(b.gapPct)) - Math.abs(parseFloat(a.gapPct)));

  const gapUp = results.filter(r => r.up);
  const gapDown = results.filter(r => !r.up);

  return (
    <div>
      <div style={{ ...cardStyle, padding: '20px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
          <div>
            <div style={{ color: C.text, fontSize: '16px', fontWeight: '800' }}>Large Cap Gap Scanner</div>
            <div style={{ color: C.sub, fontSize: '11px', marginTop: '3px' }}>
              {GAP_SCANNER_LARGE_CAPS.length} stocks scanned · Auto-refreshes every 5 min{lastRefresh ? ` · Updated ${lastRefresh.toLocaleTimeString()}` : ''}
            </div>
          </div>
          <button onClick={fetchQuotes} disabled={loading} style={{ padding: '9px 18px', background: loading ? C.card2 : C.blue, color: loading ? C.sub : '#fff', border: 'none', borderRadius: '8px', cursor: loading ? 'default' : 'pointer', fontWeight: '700', fontSize: '13px' }}>
            {loading ? '⏳ Scanning...' : '🔄 Refresh Now'}
          </button>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {[
            ['Min Gap %', <select key="g" value={minGap} onChange={e => setMinGap(+e.target.value)} style={{ ...inp, width: '110px', padding: '7px 8px', fontSize: '12px' }}>
              {[0.5, 1, 2, 3, 5, 8].map(v => <option key={v} value={v}>{v}%</option>)}
            </select>],
            ['Min Volume Ratio', <select key="v" value={minVolRatio} onChange={e => setMinVolRatio(+e.target.value)} style={{ ...inp, width: '140px', padding: '7px 8px', fontSize: '12px' }}>
              {[1.5, 2, 3, 4, 5].map(v => <option key={v} value={v}>{v}× avg vol</option>)}
            </select>],
            ['Direction', <select key="d" value={direction} onChange={e => setDirection(e.target.value)} style={{ ...inp, width: '150px', padding: '7px 8px', fontSize: '12px' }}>
              <option value="both">Both ↑↓</option>
              <option value="up">Gap Up ↑ Only</option>
              <option value="down">Gap Down ↓ Only</option>
            </select>],
          ].map(([label, control]) => (
            <div key={label}>
              <div style={{ color: C.sub, fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '5px' }}>{label}</div>
              {control}
            </div>
          ))}
        </div>
      </div>

      {error && (
        <div style={{ ...cardStyle, padding: '14px', marginBottom: '16px', borderColor: `${C.red}66` }}>
          <div style={{ color: C.redBright, fontSize: '12px' }}>⚠ {error}</div>
        </div>
      )}

      {loading && rawQuotes.length === 0 && (
        <div style={{ ...cardStyle, padding: '60px', textAlign: 'center', marginBottom: '16px' }}>
          <div style={{ color: C.sub, fontSize: '14px', marginBottom: '8px' }}>⏳ Scanning {GAP_SCANNER_LARGE_CAPS.length} large cap stocks...</div>
          <div style={{ color: C.sub, fontSize: '12px' }}>Fetching gap & volume data from market</div>
        </div>
      )}

      {!loading && results.length === 0 && rawQuotes.length > 0 && (
        <div style={{ ...cardStyle, padding: '40px', textAlign: 'center', marginBottom: '16px' }}>
          <div style={{ color: C.text, fontSize: '14px', fontWeight: '700', marginBottom: '8px' }}>No stocks match current filters</div>
          <div style={{ color: C.sub, fontSize: '12px' }}>Try lowering Min Gap % (currently {minGap}%) or Min Volume Ratio (currently {minVolRatio}×)</div>
        </div>
      )}

      {results.length > 0 && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginBottom: '16px' }}>
            {[
              ['Stocks Found', results.length, C.text],
              ['Gap Up ↑', gapUp.length, C.greenBright],
              ['Gap Down ↓', gapDown.length, C.redBright],
              ['Avg Gap', `${(results.reduce((s, r) => s + Math.abs(parseFloat(r.gapPct)), 0) / results.length).toFixed(2)}%`, C.orange],
              ['Avg Vol Ratio', `${(results.reduce((s, r) => s + parseFloat(r.volRatio), 0) / results.length).toFixed(1)}×`, C.blueBright],
            ].map(([l, v, c]) => (
              <div key={l} style={{ ...cardStyle, padding: '14px' }}>
                <div style={{ color: C.sub, fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '4px' }}>{l}</div>
                <div style={{ color: c, fontSize: '22px', fontWeight: '800' }}>{v}</div>
              </div>
            ))}
          </div>
          <div style={{ ...cardStyle, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: C.card2 }}>
                    {['Symbol', 'Name', 'Price', 'Gap %', 'Open', 'Prev Close', 'Day High', 'Day Low', 'Volume', 'Vol Ratio'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', color: C.sub, fontSize: '10px', fontWeight: '700', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap', borderBottom: `1px solid ${C.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {results.map((r, i) => (
                    <tr key={r.symbol} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? 'transparent' : `${C.card2}44` }}>
                      <td style={{ padding: '11px 14px', fontWeight: '800', color: C.text }}>{r.symbol}</td>
                      <td style={{ padding: '11px 14px', color: C.sub, maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</td>
                      <td style={{ padding: '11px 14px', color: C.text, fontWeight: '700' }}>${r.price}</td>
                      <td style={{ padding: '11px 14px' }}>
                        <span style={{ color: r.up ? C.greenBright : C.redBright, fontWeight: '800', fontSize: '13px' }}>
                          {r.up ? '↑ +' : '↓ '}{r.gapPct}%
                        </span>
                      </td>
                      <td style={{ padding: '11px 14px', color: C.text }}>${r.open}</td>
                      <td style={{ padding: '11px 14px', color: C.sub }}>${r.prev}</td>
                      <td style={{ padding: '11px 14px', color: C.greenBright }}>${r.high}</td>
                      <td style={{ padding: '11px 14px', color: C.redBright }}>${r.low}</td>
                      <td style={{ padding: '11px 14px', color: C.sub }}>{r.vol}</td>
                      <td style={{ padding: '11px 14px' }}>
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: '10px',
                          background: parseFloat(r.volRatio) >= 4 ? `${C.orange}22` : `${C.blue}22`,
                          color: parseFloat(r.volRatio) >= 4 ? C.orange : C.blueBright,
                          fontWeight: '700',
                        }}>
                          {r.volRatio}×
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function App() {
  const [accentColor, setAccentColorState] = useState(() => {
    return typeof window !== 'undefined' ? (localStorage.getItem('accentColor') || '#2f8cff') : '#2f8cff';
  });
  const [autoBrightness, setAutoBrightness] = useState(() => {
    return typeof window !== 'undefined' ? localStorage.getItem('autoBrightness') === 'true' : false;
  });
  const [brightnessLevel, setBrightnessLevel] = useState(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem('brightnessLevel') : null;
    return saved ? Number(saved) : 100;
  });
  const [themeMode, setThemeMode] = useState(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem('themeMode') : null;
    const accent = typeof window !== 'undefined' ? (localStorage.getItem('accentColor') || '#2f8cff') : '#2f8cff';
    const autoBright = typeof window !== 'undefined' ? localStorage.getItem('autoBrightness') === 'true' : false;
    let mode;
    if (autoBright && typeof window !== 'undefined') {
      mode = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'night' : 'day';
    } else {
      mode = saved === 'day' ? 'day' : 'night';
    }
    applyTheme(mode, accent);
    return mode;
  });
  const [user, setUser] = useState(null);
  const [authSession, setAuthSession] = useState(null);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [subStatus, setSubStatus] = useState(null); // null=loading, 'active'|'trialing'|'none'|'canceled' etc
  const [subCustomerId, setSubCustomerId] = useState(null);
  const [checkoutProcessing, setCheckoutProcessing] = useState(false);
  const [trades, setTrades] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [activeAccount, setActiveAccount] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [replayTradeId, setReplayTradeId] = useState(null);
  const [selectedTradeDetail, setSelectedTradeDetail] = useState(null);
  const [filterStrategy, setFilterStrategy] = useState('All');
  const [filterAsset, setFilterAsset] = useState('');
  const [globalFilters, setGlobalFilters] = useState(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem('globalFilters') : null;
    if (!saved) return { from:'', to:'', asset:'', strategy:'', account:'Current' };
    try { return JSON.parse(saved); } catch { return { from:'', to:'', asset:'', strategy:'', account:'Current' }; }
  });
  const [filterPresets, setFilterPresets] = useState(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem('filterPresets') : null;
    if (!saved) return [];
    try { return JSON.parse(saved); } catch { return []; }
  });
  const [riskControls] = useState(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem('riskControls') : null;
    if (!saved) return { dailyLossLimit:'300', maxTradesPerDay:'6' };
    try { return JSON.parse(saved); } catch { return { dailyLossLimit:'300', maxTradesPerDay:'6' }; }
  });
  const [toasts, setToasts] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState({asset:'',type:'Buy',entry:'',exit:'',size:'',stopLoss:'',target:'',commission:'',notes:'',strategy:'',market:'Stocks',emotion:'',psychScore:'7',tags:'',rulesChecked:[],screenshot:null});
  const [dbLoading, setDbLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 980 : false));
  const [addChartInterval, setAddChartInterval] = useState('15');
  const fileRef = useRef();

  useEffect(() => {
    if (SUPABASE_ENV_MISSING) {
      setAuthLoading(false);
      return;
    }
    const bootstrapRecoverySession = async () => {
      if (typeof window === 'undefined') return false;
      const searchParams = new URLSearchParams(window.location.search || '');
      const queryType = searchParams.get('type') || '';
      const tokenHash = searchParams.get('token_hash') || '';
      const code = searchParams.get('code') || '';
      const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';

      if (queryType === 'recovery' && tokenHash) {
        const { data, error } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: 'recovery',
        });
        if (!error) {
          setIsRecoveryMode(true);
          setAuthSession(data?.session ?? null);
          setUser(data?.user ?? data?.session?.user ?? null);
          window.history.replaceState({}, document.title, window.location.pathname);
          return true;
        }
      }

      if (code && typeof supabase.auth.exchangeCodeForSession === 'function') {
        const { data, error } = await supabase.auth.exchangeCodeForSession(code);
        if (!error && data?.session) {
          const pendingReset = localStorage.getItem('pendingPasswordReset');
          const isRecentReset = pendingReset && (Date.now() - parseInt(pendingReset, 10) < 3600000);
          const isRecovery = queryType === 'recovery' || !!isRecentReset;
          if (isRecovery) localStorage.removeItem('pendingPasswordReset');
          setIsRecoveryMode(isRecovery);
          setAuthSession(data.session);
          setUser(data.session.user ?? null);
          window.history.replaceState({}, document.title, window.location.pathname);
          return true;
        }
      }

      const params = new URLSearchParams(hash);
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');
      const type = params.get('type');
      if (!accessToken || !refreshToken || type !== 'recovery') return false;
      const { data, error } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });
      if (error) return false;
      setIsRecoveryMode(true);
      setAuthSession(data?.session ?? null);
      setUser(data?.session?.user ?? null);
      window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.search}`);
      return true;
    };

    bootstrapRecoverySession().catch(() => {});
    supabase.auth.getSession().then(({ data: { session } }) => {
      setAuthSession(session ?? null);
      setUser(session?.user ?? null);
      setAuthLoading(false);
    }).catch(() => {
      setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') setIsRecoveryMode(true);
      setAuthSession(session ?? null);
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Web Push setup
  const VAPID_PUBLIC_KEY = 'BGQYIWcoVYvc-0wQQ-ORJ_bEivPNIAyaXVMdtA4JWLLiyDSqSSVEtRz4KATtGXSkPyMqW8C8Oeu7uVI3lTmAbAg';
  const [pushStatus, setPushStatus] = useState('unknown'); // 'unknown'|'unsupported'|'denied'|'granted'|'error'

  const registerPush = useCallback(async () => {
    if (!user) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) { setPushStatus('unsupported'); return; }
    try {
      let reg = await navigator.serviceWorker.getRegistration('/sw.js');
      if (!reg) reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      if (Notification.permission === 'denied') { setPushStatus('denied'); return; }
      if (Notification.permission !== 'granted') {
        const perm = await Notification.requestPermission();
        if (perm !== 'granted') { setPushStatus('denied'); return; }
      }
      const padding = '='.repeat((4 - VAPID_PUBLIC_KEY.length % 4) % 4);
      const b64 = (VAPID_PUBLIC_KEY + padding).replace(/-/g, '+').replace(/_/g, '/');
      const raw = window.atob(b64);
      const appKey = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i++) appKey[i] = raw.charCodeAt(i);
      const existing = await reg.pushManager.getSubscription();
      const sub = existing || await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: appKey });
      await fetch('/api/push', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'subscribe', userId: user.id, subscription: sub.toJSON() }),
      });
      setPushStatus('granted');
    } catch { setPushStatus('error'); }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-register on login if already granted (no prompt needed)
  useEffect(() => {
    if (!user) return;
    if (!('Notification' in window)) { setPushStatus('unsupported'); return; }
    if (Notification.permission === 'granted') { registerPush(); return; }
    if (Notification.permission === 'denied') { setPushStatus('denied'); return; }
    setPushStatus('unknown'); // needs user gesture to request
  }, [user, registerPush]);

  // Fetch subscription status whenever user changes
  useEffect(() => {
    if (!user) { setSubStatus('none'); setSubCustomerId(null); return; }
    const fetchSub = async () => {
      try {
        const { data, error } = await supabase
          .from('subscriptions')
          .select('status, stripe_customer_id')
          .eq('user_id', user.id)
          .single();
        if (error || !data) {
          setSubStatus('none');
          setSubCustomerId(null);
        } else {
          setSubStatus(data.status || 'none');
          setSubCustomerId(data.stripe_customer_id || null);
        }
      } catch {
        setSubStatus('none');
      }
    };
    fetchSub();
  }, [user]);

  // Handle returning from Stripe checkout
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'success' && user) {
      setCheckoutProcessing(true);
      window.history.replaceState({}, document.title, window.location.pathname);
      // Poll for subscription for up to 20s
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const { data } = await supabase
            .from('subscriptions')
            .select('status, stripe_customer_id')
            .eq('user_id', user.id)
            .single();
          if (data && ['active', 'trialing'].includes(data.status)) {
            setSubStatus(data.status);
            setSubCustomerId(data.stripe_customer_id || null);
            setCheckoutProcessing(false);
            clearInterval(poll);
          }
        } catch { /* ignore */ }
        if (attempts >= 10) { setCheckoutProcessing(false); clearInterval(poll); }
      }, 2000);
    }
  }, [user]);

  useLayoutEffect(() => {
    applyTheme(themeMode, accentColor);
    if (typeof window !== 'undefined') {
      localStorage.setItem('themeMode', themeMode);
      localStorage.setItem('accentColor', accentColor);
      localStorage.setItem('autoBrightness', String(autoBrightness));
      localStorage.setItem('brightnessLevel', String(brightnessLevel));
    }
    if (typeof document !== 'undefined') document.body.style.background = C.bg;
  }, [themeMode, accentColor, autoBrightness, brightnessLevel]);

  // Auto brightness: follow system prefers-color-scheme
  useEffect(() => {
    if (!autoBrightness || typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
      const newMode = e.matches ? 'night' : 'day';
      applyTheme(newMode, accentColor);
      setThemeMode(newMode);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [autoBrightness, accentColor]);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 980);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') localStorage.setItem('globalFilters', JSON.stringify(globalFilters));
  }, [globalFilters]);
  useEffect(() => {
    if (typeof window !== 'undefined') localStorage.setItem('filterPresets', JSON.stringify(filterPresets));
  }, [filterPresets]);
  useEffect(() => {
    if (typeof window !== 'undefined') localStorage.setItem('riskControls', JSON.stringify(riskControls));
  }, [riskControls]);

  const setAccentColor = (color) => {
    setAccentColorState(color);
    applyTheme(themeMode, color);
  };

  const pushToast = (message, type='info') => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3500);
  };
  const removeToast = (id) => setToasts((prev) => prev.filter((t) => t.id !== id));

  const loadData = useCallback(async () => {
    if (SUPABASE_ENV_MISSING || !user) return;
    const cacheKey = `tradournal-cache:${user.id}`;
    const defaultAccountName = 'Main Account';
    try {
      setLoadError('');
      setDbLoading(true);
      let accs = [];
      let allTrades = [];
      let lastError = null;
      let bootstrapError = null;
      let sessionToken = authSession?.access_token || '';

      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          if (!sessionToken) {
            const { data: sessionData } = await supabase.auth.getSession();
            sessionToken = sessionData?.session?.access_token || '';
          }
          if (!sessionToken) throw new Error('Your session expired. Please sign in again.');

          accs = await fetchSupabaseRest(`accounts?select=*&user_id=eq.${encodeURIComponent(user.id)}&order=id.asc`, {
            token: sessionToken,
          }) || [];

          if (accs.length === 0) {
            try {
              const created = await fetchSupabaseRest('accounts', {
                method: 'POST',
                token: sessionToken,
                body: [{ name: defaultAccountName, broker: 'IBKR', type: 'Live', balance: '', user_id: user.id }],
              });
              accs = Array.isArray(created) ? created : [];
            } catch (err) {
              bootstrapError = err;
              accs = [];
            }
          }

          allTrades = [];
          const batchSize = 1000;
          let offset = 0;
          let keepGoing = true;
          while (keepGoing) {
            const rows = await fetchSupabaseRest(
              `trades?select=*&user_id=eq.${encodeURIComponent(user.id)}&order=id.asc&limit=${batchSize}&offset=${offset}`,
              { token: sessionToken }
            );
            const batch = Array.isArray(rows) ? rows : [];
            allTrades = [...allTrades, ...batch];
            keepGoing = batch.length === batchSize;
            offset += batchSize;
          }
          lastError = null;
          break;
        } catch (err) {
          lastError = err;
          if (!isTransientNetworkError(err) || attempt === 2) break;
          await sleep(700 * (attempt + 1));
        }
      }

      if (lastError) throw lastError;

      const resolvedAccounts = accs.length ? accs : [{ name: defaultAccountName, broker: 'IBKR', type: 'Live', balance: '', user_id: user.id, localOnly: true }];
      setAccounts(resolvedAccounts);
      setActiveAccount((prev) => prev || resolvedAccounts[0]?.name || defaultAccountName);
      setTrades(allTrades);
      if (typeof window !== 'undefined') {
        localStorage.setItem(cacheKey, JSON.stringify({
          when: new Date().toISOString(),
          accounts: resolvedAccounts,
          trades: allTrades,
        }));
      }
      if (bootstrapError) {
        pushToast('Your journal opened, but the default account could not be created yet. You can still look around and try again shortly.', 'info');
      }
    } catch (err) {
      let usedCache = false;
      if (typeof window !== 'undefined') {
        try {
          const cachedRaw = localStorage.getItem(cacheKey);
          if (cachedRaw) {
            const cached = JSON.parse(cachedRaw);
            const cachedAccounts = Array.isArray(cached?.accounts) ? cached.accounts : [];
            const cachedTrades = Array.isArray(cached?.trades) ? cached.trades : [];
            if (cachedAccounts.length || cachedTrades.length) {
              setAccounts(cachedAccounts);
              setActiveAccount((prev) => prev || cachedAccounts[0]?.name || 'Main Account');
              setTrades(cachedTrades);
              usedCache = true;
            }
          }
        } catch {
          // Ignore corrupt cache and fall through to the live error.
        }
      }
      const rawMessage = `${err?.message || 'Failed loading data.'}`;
      const friendlyMessage = rawMessage.toLowerCase().includes('load failed')
        ? 'The live journal connection failed for a moment. Please refresh and try again.'
        : rawMessage;
      const message = usedCache
        ? 'Live sync is temporarily unavailable. Showing your last saved journal data.'
        : friendlyMessage;
      setLoadError(message);
      pushToast(message, usedCache ? 'info' : 'error');
      if (!usedCache) {
        const fallbackAccounts = [{ name: defaultAccountName, broker: 'IBKR', type: 'Live', balance: '', user_id: user.id, localOnly: true }];
        setAccounts((prev) => (prev.length ? prev : fallbackAccounts));
        setActiveAccount((prev) => prev || defaultAccountName);
        setTrades((prev) => (Array.isArray(prev) ? prev : []));
      }
    } finally {
      setDbLoading(false);
    }
  }, [authSession, user]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleImportDone = useCallback(async () => {
    await loadData();
    setGlobalFilters({ from:'', to:'', asset:'', strategy:'', account:'Current' });
    setFilterAsset('');
    setFilterStrategy('All');
    setActiveTab('calendar');
  }, [loadData]);

  const handleSignOut = async () => {
    if (SUPABASE_ENV_MISSING) return;
    await supabase.auth.signOut();
    setIsRecoveryMode(false);
    setAuthSession(null);
    setSubStatus('none');
    setSubCustomerId(null);
    setTrades([]); setAccounts([]); setActiveAccount('');
  };
  const openReplay = (tradeId = null) => { setReplayTradeId(tradeId); setActiveTab('replay'); };

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });
  const handleScreenshot = e => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setForm(f => ({ ...f, screenshot: ev.target.result }));
    reader.readAsDataURL(file);
  };
  const toggleRule = rule => {
    const checked = form.rulesChecked.includes(rule) ? form.rulesChecked.filter(r => r !== rule) : [...form.rulesChecked, rule];
    setForm({ ...form, rulesChecked: checked });
  };

  const addTrade = async () => {
    if (!form.asset || !form.entry || !form.exit || !form.size || !activeAccount || !user?.id) {
      pushToast('Please complete the required trade fields first.', 'error');
      return;
    }
    const rawPnl = calculateTradePnl(form.type, form.entry, form.exit, form.size);
    const commission = parseFloat(form.commission) || 0;
    const pnl = (rawPnl - commission).toFixed(2);
    const newTrade = {
      asset: form.asset.trim().toUpperCase(),
      type: form.type,
      entry: form.entry,
      exit: form.exit,
      size: form.size,
      stopLoss: form.stopLoss,
      target: form.target,
      commission: commission || null,
      notes: form.notes,
      strategy: form.strategy,
      market: form.market,
      emotion: form.emotion,
      psychScore: form.psychScore,
      tags: form.tags,
      rulesChecked: form.rulesChecked,
      screenshot: form.screenshot,
      pnl,
      date: formatTradeDate(),
      account: activeAccount,
      user_id: user.id,
    };
    const { data, error } = await supabase.from('trades').insert([newTrade]).select().single();
    if (!error && data) {
      setTrades(prev => [...prev, data]);
      pushToast('Trade added', 'success');
      if (parseFloat(pnl) > 0) {
        const canvas = document.createElement('canvas');
        canvas.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:99999';
        document.body.appendChild(canvas);
        const cleanup = fireConfetti(canvas);
        setTimeout(() => { if (cleanup) cleanup(); document.body.removeChild(canvas); }, 4000);
      }
    }
    if (error) pushToast(error.message || 'Failed to add trade', 'error');
    setForm({ asset:'',type:'Buy',entry:'',exit:'',size:'',stopLoss:'',target:'',commission:'',notes:'',strategy:'',market:'Stocks',emotion:'',psychScore:'7',tags:'',rulesChecked:[],screenshot:null });
    setActiveTab('trades');
  };

  const deleteTrade = async id => {
    const { error } = await supabase.from('trades').delete().eq('id', id);
    if (error) { pushToast(error.message || 'Delete failed', 'error'); return; }
    setTrades(prev => prev.filter(t => t.id !== id));
    pushToast('Trade deleted', 'success');
  };

  const editTrade = async (id, updates) => {
    const { error } = await supabase.from('trades').update(updates).eq('id', id);
    if (error) { pushToast(error.message || 'Update failed', 'error'); return; }
    setTrades(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));
    pushToast('Trade updated', 'success');
  };

  const bulkDeleteTrades = async (ids) => {
    const { error } = await supabase.from('trades').delete().in('id', ids);
    if (error) { pushToast(error.message || 'Bulk delete failed', 'error'); return; }
    setTrades(prev => prev.filter(t => !ids.includes(t.id)));
    pushToast(`${ids.length} trade${ids.length > 1 ? 's' : ''} deleted`, 'success');
  };

  const exportCSV = () => {
    const headers = ['Date','Account','Asset','Market','Type','Strategy','Entry','Exit','Size','Stop Loss','Target','P&L','Tags','Emotion','Notes'];
    const rows = trades.map(t => [t.date,t.account,t.asset,t.market,t.type,t.strategy,t.entry,t.exit,t.size,t.stopLoss||'',t.target||'',t.pnl,t.tags,t.emotion,t.notes]);
    const csv = [headers,...rows].map(r=>r.join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
    a.download = 'trading-journal.csv'; a.click();
  };

  const livePathMatch = typeof window !== 'undefined'
    ? window.location.pathname.match(/^\/live\/([^/]+)$/)
    : null;
  const isPublicLiveRoute = Boolean(livePathMatch);
  const publicLiveToken = livePathMatch?.[1] || '';

  if (isPublicLiveRoute) {
    return (
      <div style={{minHeight:'100vh',background:C.bg,color:C.text,fontFamily:C.font,padding:'20px'}}>
        <div style={{maxWidth:'1100px',margin:'0 auto'}}>
          <div style={{...cardStyle,padding:'14px',marginBottom:'12px'}}>
            <div style={{color:C.text,fontSize:'18px',fontWeight:'800'}}>Tradournal · Live Trading</div>
            <div style={{color:C.sub,fontSize:'12px'}}>Public viewer mode</div>
          </div>
          <LiveTradingPanel
            backendUrl={BACKEND_URL}
            liveToken={publicLiveToken}
            C={C}
            cardStyle={cardStyle}
            compact
          />
        </div>
      </div>
    );
  }

  if (authLoading) return <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:C.bg,color:C.sub,fontFamily:C.font}}>Loading...</div>;
  if (isRecoveryMode) {
    return (
      <PasswordRecoveryScreen
        onComplete={() => setIsRecoveryMode(false)}
        onCancel={async () => {
          setIsRecoveryMode(false);
          await supabase.auth.signOut();
          setUser(null);
          setAuthSession(null);
        }}
      />
    );
  }
  if (!user) return <LandingPage onAuth={({ user: authUser, session }) => { setUser(authUser || null); if (session) setAuthSession(session); }} />;

  const handleSignOutAndClear = async () => {
    if (SUPABASE_ENV_MISSING) return;
    await supabase.auth.signOut();
    setIsRecoveryMode(false);
    setAuthSession(null);
    setSubStatus('none');
    setSubCustomerId(null);
    setTrades([]); setAccounts([]); setActiveAccount('');
  };

  if (checkoutProcessing) {
    return (
      <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: C.font }}>
        <div style={{ color: C.blue, fontSize: 20, fontWeight: 700, marginBottom: 12 }}>Processing your subscription…</div>
        <div style={{ color: C.sub, fontSize: 14 }}>This may take a few seconds.</div>
      </div>
    );
  }

  if (subStatus === null || (user && subStatus === null)) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: C.bg, color: C.sub, fontFamily: C.font }}>Loading…</div>;
  }

  if (!['active', 'trialing'].includes(subStatus)) {
    return <PaywallScreen user={user} onLogout={handleSignOutAndClear} customerId={subCustomerId} />;
  }

  const isImportedTradeRow = (t) => ((t.notes || '').toLowerCase().startsWith('imported from'));
  const normalizedTrades = trades.map((t) => {
    if (isImportedTradeRow(t)) return t;
    if ((t.type !== 'Buy' && t.type !== 'Sell') || t.entry === '' || t.exit === '' || t.size === '') return t;
    const computed = calculateTradePnl(t.type, t.entry, t.exit, t.size);
    if (Number.isNaN(computed)) return t;
    const computedStr = computed.toFixed(2);
    return `${t.pnl}` === computedStr ? t : { ...t, pnl: computedStr };
  });

  const scopedByAccount = globalFilters.account === 'All'
    ? normalizedTrades
    : globalFilters.account === 'Current'
      ? normalizedTrades.filter((t) => t.account === activeAccount)
      : normalizedTrades.filter((t) => t.account === globalFilters.account);
  const globallyFilteredTrades = scopedByAccount.filter((t) => {
    const d = parseTradeDate(t.date);
    if (globalFilters.from) {
      const from = new Date(globalFilters.from);
      from.setHours(0, 0, 0, 0);
      if (!Number.isNaN(d.getTime()) && d < from) return false;
    }
    if (globalFilters.to) {
      const to = new Date(globalFilters.to);
      to.setHours(23, 59, 59, 999);
      if (!Number.isNaN(d.getTime()) && d > to) return false;
    }
    if (globalFilters.asset && !(t.asset || '').toLowerCase().includes(globalFilters.asset.toLowerCase())) return false;
    if (globalFilters.strategy && !(t.strategy || '').toLowerCase().includes(globalFilters.strategy.toLowerCase())) return false;
    return true;
  });
  const accountTrades = globallyFilteredTrades;
  const getTradeRrValue = (trade) => {
    const entry = toNumber(trade.entry, NaN);
    const stop = toNumber(trade.stopLoss, NaN);
    if (Number.isNaN(entry) || Number.isNaN(stop)) return null;
    const riskPerUnit = Math.abs(entry - stop);
    if (!riskPerUnit) return null;
    const target = toNumber(trade.target, NaN);
    if (!Number.isNaN(target)) return Math.abs(target - entry) / riskPerUnit;
    const exit = toNumber(trade.exit, NaN);
    if (Number.isNaN(exit)) return null;
    const realizedMove = trade.type === 'Sell' ? (entry - exit) : (exit - entry);
    return realizedMove / riskPerUnit;
  };
  const formatTradeRr = (trade) => {
    const rr = getTradeRrValue(trade);
    if (rr === null) return '—';
    if (!Number.isNaN(toNumber(trade.target, NaN))) return `1:${rr.toFixed(2)}`;
    return `${rr >= 0 ? '+' : ''}${rr.toFixed(2)}R`;
  };
  const filteredTrades = accountTrades.filter(t => {
    const s = filterStrategy === 'All' || t.strategy === filterStrategy;
    const a = filterAsset === '' || t.asset.toLowerCase().includes(filterAsset.toLowerCase());
    return s && a;
  });
  const statsTrades = accountTrades;
  const totalPnl = statsTrades.reduce((s,t) => s + toNumber(t.pnl), 0);
  const wins = statsTrades.filter(t=>toNumber(t.pnl)>0);
  const losses = statsTrades.filter(t=>toNumber(t.pnl)<0);
  const breakEven = statsTrades.filter(t=>toNumber(t.pnl)===0);
  const winRate = statsTrades.length>0 ? ((wins.length/statsTrades.length)*100) : 0;
  const avgWin = wins.length>0 ? (wins.reduce((s,t)=>s + toNumber(t.pnl),0)/wins.length).toFixed(2) : 0;
  const avgLoss = losses.length>0 ? (losses.reduce((s,t)=>s + toNumber(t.pnl),0)/losses.length).toFixed(2) : 0;
  const bestTrade = statsTrades.length>0 ? Math.max(...statsTrades.map(t=>toNumber(t.pnl))) : 0;
  const worstTrade = statsTrades.length>0 ? Math.min(...statsTrades.map(t=>toNumber(t.pnl))) : 0;
  const profitFactor = losses.length>0&&avgLoss!==0 ? Math.abs(avgWin/avgLoss).toFixed(2) : '∞';
  let peak=0,eq=0,maxDD=0;
  statsTrades.forEach(t=>{eq += toNumber(t.pnl);if(eq>peak)peak=eq;const dd=peak-eq;if(dd>maxDD)maxDD=dd;});
  let running=0, eqPeak=0;
  const equityData = statsTrades.map((t,i)=>{
    running += toNumber(t.pnl);
    if (running > eqPeak) eqPeak = running;
    const dd = running < eqPeak ? parseFloat((running - eqPeak).toFixed(2)) : 0;
    return{name:`T${i+1}`,equity:parseFloat(running.toFixed(2)),drawdown:dd,pnl:toNumber(t.pnl),asset:t.asset};
  });
  const dailyMap = {};
  statsTrades.forEach(t=>{if(!dailyMap[t.date])dailyMap[t.date]=0;dailyMap[t.date]+=toNumber(t.pnl);});
  const dailyData = Object.entries(dailyMap).map(([date,pnl])=>{const d=new Date(date);const label=isNaN(d.getTime())?date:`${d.getMonth()+1}/${d.getDate()}`;return{date:label,pnl:parseFloat(pnl.toFixed(2))};});
  const saveFilterPreset = () => {
    const name = window.prompt('Preset name?');
    if (!name) return;
    setFilterPresets((prev) => {
      const without = prev.filter((p) => p.name !== name);
      return [...without, { name, filters: globalFilters }];
    });
    pushToast(`Saved preset: ${name}`, 'success');
  };
  const applyFilterPreset = (name) => {
    const p = filterPresets.find((x) => x.name === name);
    if (!p) return;
    setGlobalFilters(p.filters);
    pushToast(`Loaded preset: ${name}`, 'info');
  };
  const todayStr = new Date().toDateString();
  const todayTrades = statsTrades.filter((t) => parseTradeDate(t.date).toDateString() === todayStr);
  const todayPnl = todayTrades.reduce((s, t) => s + toNumber(t.pnl), 0);
  const maxTradesLimit = parseFloat(riskControls.maxTradesPerDay || 0);
  const dailyLossLimit = parseFloat(riskControls.dailyLossLimit || 0);
  const overTradeWarning = maxTradesLimit > 0 && todayTrades.length > maxTradesLimit;
  const dailyLossWarning = dailyLossLimit > 0 && todayPnl <= (-1 * dailyLossLimit);
  const TAB_TITLES = {dashboard:'Portfolio',insights:'Insights',calendar:'Profit Calendar',trades:'Trade Log',reports:'Reports',coach:'Premium Coach',replay:'Trade Replay',live:'Live Session',add:'Add Trade',accounts:'Accounts',import:'Import Trades',premarket:'Pre-Market Gainers',scanner:'Gap Scanner',news:'Market News',ai:'AI Assistant',futures:'Futures Market',maalerts:'MA Alert Scanner'};

  if (SUPABASE_ENV_MISSING) {
    return (
      <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:'#050912',color:'#e8eefb',fontFamily:'"Manrope","Sora","Segoe UI",sans-serif',padding:'24px'}}>
        <div style={{maxWidth:'640px',background:'#0f1725',border:'1px solid #22314a',borderRadius:'12px',padding:'20px'}}>
          <h2 style={{marginTop:0}}>Missing Supabase Environment Variables</h2>
          <p>Set these in your frontend environment and restart:</p>
          <ul>
            <li><code>REACT_APP_SUPABASE_URL</code></li>
            <li><code>REACT_APP_SUPABASE_ANON_KEY</code></li>
          </ul>
          <p style={{color:'#95a5c0',fontSize:'13px'}}>Use <code>.env</code> for local development and Vercel Project Environment Variables for production.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{display:'flex',height:'100vh',background:themeMode==='day'?'linear-gradient(160deg,#fdf8f0 0%,#f5efe2 100%)':'linear-gradient(160deg,#18130d 0%,#110e08 100%)',color:C.text,fontFamily:C.font,overflow:'hidden',filter:brightnessLevel!==100?`brightness(${brightnessLevel/100})`:'none'}}>
      {!isMobile && <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} accounts={accounts} activeAccount={activeAccount} setActiveAccount={setActiveAccount} onSignOut={handleSignOut} themeMode={themeMode} subStatus={subStatus} onManageSub={subCustomerId ? async () => {
        const res = await fetch('/api/stripe-portal', { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({ customerId: subCustomerId }) });
        const d = await res.json();
        if (d.url) window.location.href = d.url;
      } : null}/>}
      <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>
        {!isMobile && <TickerBar key={`ticker-${themeMode}`} trades={accountTrades} themeMode={themeMode}/>}
        <div style={{padding: isMobile ? '10px 14px' : '14px 24px', borderBottom:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',alignItems:'center',background:C.card,flexShrink:0,flexWrap:'wrap',gap:'8px'}}>
          <div>
            <h1 style={{margin:0,fontSize: isMobile ? '15px' : '17px',fontWeight:'800',color:C.text}}>{TAB_TITLES[activeTab]}</h1>
            {!isMobile && <div style={{color:C.sub,fontSize:'11px',marginTop:'2px'}}>{activeAccount} · {new Date().toDateString()}</div>}
          </div>
          <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
            <button onClick={() => { if (autoBrightness) { setAutoBrightness(false); } const next = themeMode === 'night' ? 'day' : 'night'; applyTheme(next, accentColor); setThemeMode(next); }} style={{padding: isMobile ? '7px 10px' : '8px 12px',background:autoBrightness?`${accentColor}22`:C.card2,color:autoBrightness?accentColor:C.text,border:`1px solid ${autoBrightness?accentColor+'55':C.border}`,borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'700'}} title={autoBrightness?'Auto brightness on — click to override':'Toggle day/night'}>
              {autoBrightness?'⬡':themeMode==='night'?'☀':'🌙'}
            </button>
            <button onClick={()=>setActiveTab('add')} style={{padding: isMobile ? '7px 12px' : '8px 16px',background:C.blue,color:'#fff',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'700',display:'flex',alignItems:'center',gap:5}}>
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
  Add Trade
</button>
            {!isMobile && <button onClick={()=>setActiveTab('import')} style={{padding:'8px 14px',background:C.card2,color:C.text,border:`1px solid ${C.border}`,borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'700',display:'flex',alignItems:'center',gap:5}}>
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
  Import
</button>}
            {!isMobile && <button onClick={exportCSV} style={{padding:'8px 14px',background:C.card2,color:C.text,border:`1px solid ${C.border}`,borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'700',display:'flex',alignItems:'center',gap:5}}>
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 5 17 10"/><line x1="12" y1="5" x2="12" y2="15"/></svg>
  Export
</button>}
          </div>
        </div>
        {['trades','insights','reports','coach'].includes(activeTab) && (
          <div style={{padding:'8px 24px',borderBottom:`1px solid ${C.border}`,display:'flex',gap:'8px',background:C.card,flexWrap:'wrap'}}>
            {[['dashboard','Overview'],['trades','Trade Log'],['insights','Insights'],['reports','Reports'],['coach','Coach']].map(([tab,label])=>(
              <button key={tab} onClick={()=>setActiveTab(tab)} style={{padding:'6px 12px',borderRadius:'16px',cursor:'pointer',fontSize:'11px',fontWeight:'700',border:`1px solid ${activeTab===tab?C.blue:C.border}`,background:activeTab===tab?'rgba(56,139,253,0.18)':C.card2,color:activeTab===tab?C.blueBright:C.sub}}>
                {label}
              </button>
            ))}
          </div>
        )}
        {dbLoading&&<div style={{padding:'10px 24px',background:'rgba(56,139,253,0.1)',borderBottom:`1px solid ${C.blue}33`,color:C.blueBright,fontSize:'12px',textAlign:'center'}}>⏳ Loading your data...</div>}
        {loadError && (
          <div style={{padding:'10px 24px',background:'rgba(218,54,51,0.12)',borderBottom:`1px solid ${C.red}44`,color:C.redBright,fontSize:'12px',display:'flex',justifyContent:'space-between',alignItems:'center',gap:'10px'}}>
            <span>{loadError}</span>
            <button onClick={loadData} style={{padding:'5px 10px',border:`1px solid ${C.red}`,borderRadius:'6px',background:'transparent',color:C.redBright,cursor:'pointer',fontSize:'11px'}}>Retry</button>
          </div>
        )}
        {(overTradeWarning || dailyLossWarning) && (
          <div style={{padding:'10px 24px',background:'rgba(245,158,11,0.14)',borderBottom:`1px solid ${C.orange}66`,color:C.orange,fontSize:'12px'}}>
            {overTradeWarning && `Risk warning: ${todayTrades.length} trades today exceeds max ${maxTradesLimit}. `}
            {dailyLossWarning && `Risk warning: today P&L ${todayPnl.toFixed(2)} breached daily loss limit -$${dailyLossLimit}.`}
          </div>
        )}
        <div style={{flex:1,overflow:'auto',padding: isMobile ? '12px 12px 80px' : '20px 24px', transition:'background 0.3s'}}>
          {['trades','reports','insights'].includes(activeTab) && (
            <GlobalFilterBar
              filters={globalFilters}
              setFilters={setGlobalFilters}
              accounts={accounts}
              savePreset={saveFilterPreset}
              applyPreset={applyFilterPreset}
              presets={filterPresets}
            />
          )}

          {activeTab==='dashboard'&&(
            <DashboardPanel
              accountTrades={statsTrades}
              totalPnl={totalPnl}
              winRate={winRate}
              profitFactor={profitFactor}
              avgWin={avgWin}
              avgLoss={avgLoss}
              wins={wins}
              losses={losses}
              breakEven={breakEven}
              setActiveTab={setActiveTab}
              themeMode={themeMode}
              C={C}
              cardStyle={cardStyle}
              DonutChart={DonutChart}
              Sparkline={Sparkline}
              AdvancedCharts={AdvancedCharts}
            />
          )}

          {activeTab==='calendar'&&<ProfitCalendar trades={accountTrades} themeMode={themeMode}/>}
          {activeTab==='insights'&&<JournalInsights trades={accountTrades}/>}
          {activeTab==='coach'&&<PremiumCoach trades={accountTrades} openReplay={openReplay}/>}
          {activeTab==='reports'&&(
            <>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(320px, 1fr))',gap:'14px',marginBottom:'16px'}}>
                <div style={{...cardStyle,padding:'20px'}}>
                  <div style={{color:C.sub,fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'1px',marginBottom:'14px'}}>Daily P&L</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={dailyData}><CartesianGrid strokeDasharray="3 3" stroke={C.border}/><XAxis dataKey="date" stroke={C.sub} tick={{fontSize:10}}/><YAxis stroke={C.sub} tick={{fontSize:10}}/><Tooltip contentStyle={{background:C.card2,border:`1px solid ${C.border}`,borderRadius:'8px',fontSize:'12px',color:C.text}} labelStyle={{color:C.text}} itemStyle={{color:C.text}} formatter={v=>[`$${v}`,'P&L']}/><Bar dataKey="pnl" radius={[3,3,0,0]} maxBarSize={30} fill={C.blue}/></BarChart>
                  </ResponsiveContainer>
                </div>
                <div style={{...cardStyle,padding:'20px',gridColumn:'1 / -1'}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'14px',flexWrap:'wrap',gap:8}}>
                    <div style={{color:C.sub,fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'1px'}}>Cumulative P&L &amp; Drawdown</div>
                    <div style={{display:'flex',gap:16,fontSize:'11px',fontWeight:'600'}}>
                      <span style={{display:'flex',alignItems:'center',gap:5}}><span style={{width:14,height:2,background:C.greenBright,display:'inline-block',borderRadius:2}}></span><span style={{color:C.sub}}>Equity</span></span>
                      <span style={{display:'flex',alignItems:'center',gap:5}}><span style={{width:14,height:8,background:'rgba(239,68,68,0.3)',display:'inline-block',borderRadius:2}}></span><span style={{color:C.sub}}>Drawdown</span></span>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={260}>
                    <ComposedChart data={equityData} margin={{top:4,right:8,bottom:0,left:0}}>
                      <defs>
                        <linearGradient id="ddGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#ef4444" stopOpacity={0.35}/>
                          <stop offset="100%" stopColor="#ef4444" stopOpacity={0.05}/>
                        </linearGradient>
                        <linearGradient id="eqGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={C.greenBright} stopOpacity={0.15}/>
                          <stop offset="100%" stopColor={C.greenBright} stopOpacity={0.01}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
                      <XAxis dataKey="name" stroke={C.sub} tick={{fontSize:10}} axisLine={false} tickLine={false}/>
                      <YAxis yAxisId="equity" stroke={C.sub} tick={{fontSize:10}} axisLine={false} tickLine={false} tickFormatter={v=>v>=1000?`$${(v/1000).toFixed(1)}k`:`$${v}`}/>
                      <YAxis yAxisId="dd" orientation="right" stroke={C.sub} tick={{fontSize:10}} axisLine={false} tickLine={false} tickFormatter={v=>`$${v}`}/>
                      <Tooltip
                        contentStyle={{background:C.card2,border:`1px solid ${C.border}`,borderRadius:'8px',fontSize:'12px',color:C.text}}
                        labelStyle={{color:C.sub,fontWeight:600}}
                        formatter={(v,name)=>name==='drawdown'?[`-$${Math.abs(v).toFixed(2)}`,'Drawdown']:[`$${v}`,'Equity']}
                      />
                      <ReferenceLine yAxisId="equity" y={0} stroke={C.border} strokeDasharray="4 2"/>
                      <Area yAxisId="dd" type="monotone" dataKey="drawdown" fill="url(#ddGrad)" stroke="#ef4444" strokeWidth={1} dot={false} activeDot={false}/>
                      <Area yAxisId="equity" type="monotone" dataKey="equity" fill="url(#eqGrad)" stroke={C.greenBright} strokeWidth={2} dot={false} activeDot={{r:4,fill:C.greenBright}}/>
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div style={{...cardStyle,padding:'20px'}}>
                <div style={{color:C.sub,fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'1px',marginBottom:'14px'}}>Performance Summary</div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))',gap:'12px'}}>
                  {[['Total Trades',accountTrades.length,C.text],['Win Rate',`${winRate.toFixed(1)}%`,C.blue],['Total P&L',`$${totalPnl.toFixed(2)}`,totalPnl>=0?C.greenBright:C.redBright],['Avg Win',`$${avgWin}`,C.greenBright],['Avg Loss',`$${avgLoss}`,C.redBright],['Best Trade',`$${bestTrade}`,C.greenBright],['Worst Trade',`$${worstTrade}`,C.redBright],['Max Drawdown',`$${maxDD.toFixed(2)}`,C.orange]].map(([l,v,c])=>(
                    <div key={l} style={{background:C.card2,padding:'14px',borderRadius:'8px',border:`1px solid ${C.border}`}}><div style={{color:C.sub,fontSize:'10px',marginBottom:'5px',fontWeight:'600',textTransform:'uppercase'}}>{l}</div><div style={{color:c,fontSize:'18px',fontWeight:'800'}}>{v}</div></div>
                  ))}
                </div>
              </div>
              <AnalyticsPanel trades={accountTrades} C={C} cardStyle={cardStyle} />
              <QuantLabPanel trades={accountTrades} themeMode={themeMode} />
              <SymbolBreakdown trades={accountTrades} />
              <RMultipleHistogram trades={accountTrades} />
              <VolumeProfileChart trades={accountTrades} />
            </>
          )}
          {activeTab==="trades"&&(
            <TradeTable
              accountTrades={accountTrades}
              filteredTrades={filteredTrades}
              filterAsset={filterAsset}
              setFilterAsset={setFilterAsset}
              filterStrategy={filterStrategy}
              setFilterStrategy={setFilterStrategy}
              setSelectedTradeDetail={setSelectedTradeDetail}
              openReplay={openReplay}
              deleteTrade={deleteTrade}
              editTrade={editTrade}
              bulkDeleteTrades={bulkDeleteTrades}
              formatTradeRr={formatTradeRr}
              resolveTvSymbol={resolveTvSymbol}
              isMobile={isMobile}
              C={C}
              cardStyle={cardStyle}
              inp={inp}
              PnLSummaryBar={PnLSummaryBar}
            />
          )}
          {activeTab==='replay'&&(
            <TradeReplayPanel
              trades={accountTrades}
              selectedTradeId={replayTradeId}
              themeMode={themeMode}
              isMobile={isMobile}
              resolveTvSymbol={resolveTvSymbol}
              parseTradeDate={parseTradeDate}
              C={C}
              cardStyle={cardStyle}
              inp={inp}
            />
          )}
          {activeTab==='live'&&(
            <LiveTradingPanel
              backendUrl={BACKEND_URL}
              liveToken={LIVE_VIEW_TOKEN}
              C={C}
              cardStyle={cardStyle}
              showShare
            />
          )}
          {activeTab==='import'&&<ImportTrades trades={trades} setTrades={setTrades} activeAccount={activeAccount} userId={user.id} onImportDone={handleImportDone}/>}
          {activeTab==='watchlist'&&<WatchlistPanel C={C} cardStyle={cardStyle} inp={inp}/>}
          {activeTab==='futures'&&<FuturesPanel C={C} cardStyle={cardStyle} inp={inp} userId={user.id}/>}
          {activeTab==='maalerts'&&<MAlertsWindow C={C} cardStyle={cardStyle} inp={inp} userId={user.id}/>}
          {activeTab==='briefing'&&<DailyBriefing C={C} cardStyle={cardStyle} inp={inp} userId={user.id}/>}
          {activeTab==='premarket'&&<PreMarketGainersPanel/>}
          {activeTab==='scanner'&&<GapScannerPanel/>}
          {activeTab==='news'&&<NewsPanel trades={accountTrades}/>}
          {activeTab==='ai'&&(
            <div style={{maxWidth:800,margin:'0 auto',width:'100%'}}>
              <div style={{...cardStyle,padding:32,textAlign:'center',marginBottom:20}}>
                <div style={{fontSize:48,marginBottom:12}}>🤖</div>
                <div style={{color:C.text,fontSize:20,fontWeight:800,marginBottom:8}}>AI Trading Assistant</div>
                <div style={{color:C.sub,fontSize:14,marginBottom:20}}>Your personal trading coach powered by Claude AI. Ask anything about your performance, psychology, or strategies.</div>
                <div style={{background:C.card2,border:`1px solid ${C.border}`,borderRadius:10,padding:16,textAlign:'left'}}>
                  <div style={{color:C.sub,fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'1px',marginBottom:8}}>Try asking:</div>
                  {['What are my best and worst performing symbols?','What time of day do I trade best?','Am I overtrading on losing days?','What patterns do you see in my losses?','How can I improve my risk management?'].map(q=>(
                    <div key={q} style={{color:C.blueBright,fontSize:13,padding:'4px 0',borderBottom:`1px solid ${C.border}`}}>· {q}</div>
                  ))}
                </div>
                <div style={{marginTop:20,color:C.sub,fontSize:12}}>Click the <span style={{background:'linear-gradient(135deg,#388bfd,#8957e5)',padding:'2px 8px',borderRadius:20,color:'#fff'}}>🤖</span> button at the bottom-right to open the chat.</div>
              </div>
            </div>
          )}
          {activeTab==='add'&&(
            isMobile ? (
              <MobileAddTradeForm form={form} setForm={setForm} handleChange={handleChange} toggleRule={toggleRule} addTrade={addTrade} fileRef={fileRef} handleScreenshot={handleScreenshot} calculateTradePnl={calculateTradePnl} C={C} cardStyle={cardStyle} inp={inp} />
            ) : (
            <div style={{maxWidth:'1200px',margin:'0 auto',width:'100%'}}>
              <AddTradeAdvancedChart
                asset={form.asset}
                market={form.market}
                themeMode={themeMode}
                interval={addChartInterval}
                setInterval={setAddChartInterval}
                resolveTvSymbol={resolveTvSymbol}
                isMobile={isMobile}
              />
              <div style={{...cardStyle,padding:'28px'}}>
                <h3 style={{margin:'0 0 22px',fontSize:'17px',fontWeight:'800',color:C.text}}>New Trade Entry</h3>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))',gap:'14px',marginBottom:'14px'}}>
                  {[['asset','Asset','text','AAPL, BTC...'],['entry','Entry Price','number','0.00'],['exit','Exit Price','number','0.00'],['size','Position Size','number','0'],['stopLoss','Stop Loss','number','0.00'],['target','Target','number','0.00'],['commission','Commission ($)','number','0.00']].map(([name,label,type,ph])=>(
                    <div key={name}><div style={{color:C.sub,fontSize:'10px',marginBottom:'5px',fontWeight:'700',textTransform:'uppercase'}}>{label}</div><input name={name} type={type} placeholder={ph} value={form[name]} onChange={handleChange} style={inp}/></div>
                  ))}
                </div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))',gap:'14px',marginBottom:'14px'}}>
                  <div><div style={{color:C.sub,fontSize:'10px',marginBottom:'5px',fontWeight:'700',textTransform:'uppercase'}}>Market</div><select name="market" value={form.market} onChange={handleChange} style={inp}><option>Stocks</option><option>Forex</option><option>Crypto</option><option>Options</option><option>Futures</option></select></div>
                  <div><div style={{color:C.sub,fontSize:'10px',marginBottom:'5px',fontWeight:'700',textTransform:'uppercase'}}>Direction</div><select name="type" value={form.type} onChange={handleChange} style={inp}><option>Buy</option><option>Sell</option></select></div>
                  <div><div style={{color:C.sub,fontSize:'10px',marginBottom:'5px',fontWeight:'700',textTransform:'uppercase'}}>Strategy</div><select name="strategy" value={form.strategy} onChange={handleChange} style={inp}><option value="">Select...</option><option>Breakout</option><option>Scalp</option><option>Swing</option><option>Trend Following</option><option>Reversal</option></select></div>
                </div>
                <div style={{marginBottom:'14px'}}><div style={{color:C.sub,fontSize:'10px',marginBottom:'5px',fontWeight:'700',textTransform:'uppercase'}}>Tags</div><input name="tags" placeholder="momentum, earnings..." value={form.tags} onChange={handleChange} style={inp}/></div>
                <div style={{background:C.card2,padding:'18px',borderRadius:'10px',marginBottom:'14px',border:`1px solid ${C.border}`}}>
                  <div style={{color:C.sub,fontSize:'10px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'1px',marginBottom:'12px'}}>🧠 Psychology</div>
                  <div style={{display:'flex',gap:'6px',flexWrap:'wrap',marginBottom:'12px'}}>
                    {EMOTIONS.map(e=>(<button key={e} onClick={()=>setForm(f=>({...f,emotion:f.emotion===e?'':e}))} style={{padding:'5px 11px',borderRadius:'20px',border:`1px solid ${form.emotion===e?C.blue:C.border}`,background:form.emotion===e?`${C.blue}22`:C.card,color:form.emotion===e?C.blueBright:C.sub,cursor:'pointer',fontSize:'12px'}}>{e}</button>))}
                  </div>
                  <div style={{color:C.sub,fontSize:'10px',marginBottom:'6px',fontWeight:'600'}}>Mental Score: <span style={{color:C.text,fontWeight:'800'}}>{form.psychScore}/10</span></div>
                  <input name="psychScore" type="range" min="1" max="10" value={form.psychScore} onChange={handleChange} style={{width:'100%',accentColor:C.blue}}/>
                </div>
                <div style={{background:C.card2,padding:'18px',borderRadius:'10px',marginBottom:'14px',border:`1px solid ${C.border}`}}>
                  <div style={{color:C.sub,fontSize:'10px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'1px',marginBottom:'12px'}}>📜 Rules <span style={{color:C.blue}}>({form.rulesChecked.length}/{RULES.length})</span></div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))',gap:'8px'}}>
                    {RULES.map(rule=>(<div key={rule} onClick={()=>toggleRule(rule)} style={{display:'flex',alignItems:'center',gap:'8px',padding:'9px 12px',background:form.rulesChecked.includes(rule)?'rgba(56,139,253,0.1)':C.card,borderRadius:'6px',border:`1px solid ${form.rulesChecked.includes(rule)?C.blue+'55':C.border}`,cursor:'pointer'}}><div style={{width:'16px',height:'16px',borderRadius:'4px',background:form.rulesChecked.includes(rule)?C.blue:'#30363d',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'10px',flexShrink:0,color:'white',fontWeight:'700'}}>{form.rulesChecked.includes(rule)?'✓':''}</div><span style={{fontSize:'11px',color:form.rulesChecked.includes(rule)?C.text:C.sub}}>{rule}</span></div>))}
                  </div>
                </div>
                <div style={{marginBottom:'14px'}}><div style={{color:C.sub,fontSize:'10px',marginBottom:'5px',fontWeight:'700',textTransform:'uppercase'}}>Notes</div><textarea name="notes" placeholder="Setup reasoning..." value={form.notes} onChange={handleChange} style={{...inp,height:'80px',resize:'vertical'}}/></div>
                <div style={{marginBottom:'16px'}}>
                  <div style={{color:C.sub,fontSize:'10px',marginBottom:'6px',fontWeight:'700',textTransform:'uppercase'}}>📸 Screenshot</div>
                  <input ref={fileRef} type="file" accept="image/*" onChange={handleScreenshot} style={{display:'none'}}/>
                  <button onClick={()=>fileRef.current.click()} style={{padding:'8px 16px',background:C.card2,color:C.text,border:`1px solid ${C.border}`,borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'600'}}>📎 Upload</button>
                  {form.screenshot&&<div style={{marginTop:'10px'}}><img src={form.screenshot} alt="preview" style={{width:'100%',maxHeight:'180px',objectFit:'contain',borderRadius:'8px',border:`1px solid ${C.border}`}}/><button onClick={()=>setForm(f=>({...f,screenshot:null}))} style={{marginTop:'6px',background:'none',border:`1px solid ${C.red}`,color:C.red,borderRadius:'4px',padding:'3px 8px',cursor:'pointer',fontSize:'11px'}}>Remove</button></div>}
                </div>
                {form.entry&&form.exit&&form.size&&(
                  <div style={{padding:'14px',background:calculateTradePnl(form.type, form.entry, form.exit, form.size)>=0?'rgba(46,160,67,0.1)':'rgba(218,54,51,0.1)',borderRadius:'8px',marginBottom:'16px',border:`1px solid ${calculateTradePnl(form.type, form.entry, form.exit, form.size)>=0?C.green+'55':C.red+'55'}`}}>
                    <div style={{color:C.sub,fontSize:'10px',fontWeight:'700',marginBottom:'4px',textTransform:'uppercase'}}>Estimated P&L</div>
                    <div style={{fontSize:'26px',fontWeight:'900',color:calculateTradePnl(form.type, form.entry, form.exit, form.size)>=0?C.greenBright:C.redBright}}>
                      {calculateTradePnl(form.type, form.entry, form.exit, form.size)>=0?'+':''}${calculateTradePnl(form.type, form.entry, form.exit, form.size).toFixed(2)}
                    </div>
                  </div>
                )}
                <button onClick={addTrade} style={{width:'100%',padding:'13px',background:C.blue,color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'14px',fontWeight:'800'}}>+ ADD TRADE</button>
              </div>
            </div>
            )
          )}
          {activeTab==='settings'&&(
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              {/* ── Appearance Card ── */}
              <div style={{ ...cardStyle, padding:'20px 22px' }}>
                <div style={{ color:C.text, fontWeight:800, fontSize:15, marginBottom:2, display:'flex', alignItems:'center', gap:8 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>
                  Appearance
                </div>
                <div style={{ color:C.sub, fontSize:12, marginBottom:16 }}>Customize the look and feel of your trading journal.</div>

                {/* Brightness Mode */}
                <div style={{ marginBottom:18 }}>
                  <div style={{ color:C.text, fontSize:12, fontWeight:700, marginBottom:8 }}>Brightness Mode</div>
                  <div style={{ display:'flex', gap:8 }}>
                    {[['night','🌙 Dark','Night mode'],['day','☀️ Light','Day mode'],['auto','⬡ Auto','Follow system']].map(([m, label, tip]) => (
                      <button key={m} title={tip}
                        onClick={() => {
                          if (m === 'auto') {
                            setAutoBrightness(true);
                            const sysMode = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'night' : 'day';
                            applyTheme(sysMode, accentColor);
                            setThemeMode(sysMode);
                          } else {
                            setAutoBrightness(false);
                            applyTheme(m, accentColor);
                            setThemeMode(m);
                          }
                        }}
                        style={{
                          flex:1, padding:'9px 6px', borderRadius:8, border:`1.5px solid ${(m==='auto'?autoBrightness:(!autoBrightness&&themeMode===m))?accentColor:C.border}`,
                          background:(m==='auto'?autoBrightness:(!autoBrightness&&themeMode===m))?`${accentColor}18`:C.card2,
                          color:(m==='auto'?autoBrightness:(!autoBrightness&&themeMode===m))?accentColor:C.sub,
                          fontWeight:700, fontSize:12, cursor:'pointer'
                        }}>{label}</button>
                    ))}
                  </div>
                </div>

                {/* Brightness Level Slider */}
                <div style={{ marginBottom:18 }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                    <div style={{ color:C.text, fontSize:12, fontWeight:700 }}>Brightness</div>
                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                      <span style={{ color:C.sub, fontSize:11 }}>🌑</span>
                      <span style={{ color:accentColor, fontSize:12, fontWeight:700, minWidth:36, textAlign:'center' }}>{brightnessLevel}%</span>
                      <span style={{ color:C.sub, fontSize:11 }}>☀️</span>
                    </div>
                  </div>
                  <div style={{ position:'relative' }}>
                    <input
                      type="range" min={20} max={130} step={1}
                      value={brightnessLevel}
                      onChange={e => setBrightnessLevel(Number(e.target.value))}
                      style={{ width:'100%', accentColor, cursor:'pointer', height:4 }}
                    />
                    <div style={{ display:'flex', justifyContent:'space-between', marginTop:4 }}>
                      {[20,50,100,115,130].map(v => (
                        <button key={v} onClick={() => setBrightnessLevel(v)}
                          style={{ padding:'2px 7px', borderRadius:4, border:`1px solid ${brightnessLevel===v?accentColor:C.border}`, background:brightnessLevel===v?`${accentColor}20`:'transparent', color:brightnessLevel===v?accentColor:C.sub, fontSize:10, fontWeight:700, cursor:'pointer' }}>
                          {v===100?'Default':v+'%'}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Accent Color Presets */}
                <div style={{ marginBottom:18 }}>
                  <div style={{ color:C.text, fontSize:12, fontWeight:700, marginBottom:8 }}>Accent Color</div>
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    {ACCENT_PRESETS.map(p => (
                      <button key={p.value} title={p.name}
                        onClick={() => setAccentColor(p.value)}
                        style={{
                          width:32, height:32, borderRadius:8, background:p.value, border: accentColor===p.value ? `2.5px solid ${C.text}` : '2.5px solid transparent',
                          cursor:'pointer', outline:'none', boxShadow: accentColor===p.value ? `0 0 0 2px ${p.value}66` : 'none',
                          transition:'all 0.15s', transform: accentColor===p.value ? 'scale(1.15)' : 'scale(1)',
                          display:'flex', alignItems:'center', justifyContent:'center'
                        }}>
                        {accentColor===p.value && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                      </button>
                    ))}
                    {/* Custom color input */}
                    <div style={{ position:'relative', width:32, height:32 }} title="Custom color">
                      <input type="color" value={accentColor} onChange={e => setAccentColor(e.target.value)}
                        style={{ position:'absolute', inset:0, width:'100%', height:'100%', opacity:0, cursor:'pointer', border:'none', padding:0 }}/>
                      <div style={{ width:32, height:32, borderRadius:8, background:`conic-gradient(red,yellow,lime,cyan,blue,magenta,red)`,
                        border: !ACCENT_PRESETS.find(p=>p.value===accentColor) ? `2.5px solid ${C.text}` : `2px solid ${C.border}`,
                        display:'flex', alignItems:'center', justifyContent:'center', pointerEvents:'none',
                        boxShadow: !ACCENT_PRESETS.find(p=>p.value===accentColor) ? `0 0 0 2px ${accentColor}66` : 'none' }}>
                        {!ACCENT_PRESETS.find(p=>p.value===accentColor) && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop:8, display:'flex', alignItems:'center', gap:8 }}>
                    <div style={{ width:16, height:16, borderRadius:4, background:accentColor }}/>
                    <span style={{ color:C.sub, fontSize:11, fontFamily:'monospace' }}>{accentColor.toUpperCase()}</span>
                    <button onClick={() => setAccentColor('#2f8cff')} style={{ marginLeft:'auto', padding:'3px 10px', borderRadius:5, border:`1px solid ${C.border}`, background:'transparent', color:C.sub, fontSize:11, cursor:'pointer', fontWeight:600 }}>Reset</button>
                  </div>
                </div>

                {/* Preview */}
                <div style={{ borderRadius:10, border:`1px solid ${C.border}`, overflow:'hidden' }}>
                  <div style={{ padding:'10px 14px', background:C.card2, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <span style={{ color:C.text, fontWeight:700, fontSize:12 }}>Preview</span>
                    <span style={{ color:C.sub, fontSize:11 }}>Tradournal</span>
                  </div>
                  <div style={{ padding:'14px', background:C.card, display:'flex', gap:10, flexWrap:'wrap' }}>
                    <button style={{ padding:'7px 16px', background:accentColor, color:'#fff', border:'none', borderRadius:7, fontWeight:700, fontSize:12, cursor:'default' }}>Add Trade</button>
                    <button style={{ padding:'7px 16px', background:'transparent', border:`1.5px solid ${accentColor}`, color:accentColor, borderRadius:7, fontWeight:700, fontSize:12, cursor:'default' }}>View Report</button>
                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                      <div style={{ width:8, height:8, borderRadius:'50%', background:accentColor }}/>
                      <span style={{ color:accentColor, fontSize:12, fontWeight:600 }}>+$1,240.00</span>
                    </div>
                    <div style={{ padding:'4px 10px', borderRadius:5, background:`${accentColor}20`, color:accentColor, fontSize:11, fontWeight:700 }}>Active</div>
                  </div>
                </div>
              </div>
              <WeeklyEmailSettings user={user} C={C} cardStyle={cardStyle} inp={inp} pushToast={pushToast} />
              {/* Push Notifications card */}
              <div style={{ ...cardStyle, padding:'18px 20px' }}>
                <div style={{ color:C.text, fontWeight:800, fontSize:14, marginBottom:4 }}>Push Notifications</div>
                <div style={{ color:C.sub, fontSize:12, marginBottom:14 }}>
                  Get alerts on your phone even when the app is closed. On iPhone, open this app from the Home Screen icon (not Safari).
                </div>
                {pushStatus === 'granted' && (
                  <div style={{ display:'flex', alignItems:'center', gap:8, color:'#0ecb81', fontWeight:700, fontSize:13 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    Push notifications enabled on this device
                  </div>
                )}
                {pushStatus === 'denied' && (
                  <div style={{ color:'#ef4444', fontSize:13, fontWeight:600 }}>
                    Notifications blocked. Go to your phone Settings → Safari / App → Notifications and allow them, then reload.
                  </div>
                )}
                {pushStatus === 'unsupported' && (
                  <div style={{ color:C.sub, fontSize:13 }}>
                    Push notifications not supported on this browser. On iPhone, add to Home Screen and open from there.
                  </div>
                )}
                {(pushStatus === 'unknown' || pushStatus === 'error') && (
                  <button onClick={registerPush}
                    style={{ padding:'10px 20px', background:'linear-gradient(135deg,#388bfd,#2563eb)', color:'#fff', border:'none', borderRadius:9, fontWeight:700, cursor:'pointer', fontSize:13, display:'flex', alignItems:'center', gap:8 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                    Enable Push Notifications
                  </button>
                )}
              </div>
            </div>
          )}
          {activeTab==='accounts'&&(
            <AccountsManager
              accounts={accounts}
              setAccounts={setAccounts}
              trades={statsTrades}
              userId={user.id}
              supabase={supabase}
              C={C}
              cardStyle={cardStyle}
              inp={inp}
            />
          )}
        </div>
        <TradeDetailDrawer trade={selectedTradeDetail} onClose={()=>setSelectedTradeDetail(null)} openReplay={(id)=>{setSelectedTradeDetail(null);openReplay(id);}} />
        <ToastStack toasts={toasts} removeToast={removeToast} />
        {!isMobile && <div style={{padding:'8px 24px 14px', borderTop:`1px solid ${C.border}`, color:C.sub, fontSize:'11px', display:'flex', justifyContent:'space-between', gap:'10px', flexWrap:'wrap'}}>
          <span>Need help? <a href="mailto:support@tradournal.com" style={{ color:C.sub }}>support@tradournal.com</a></span>
          <span style={{display:'flex', gap:'10px'}}>
            <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color:C.sub }}>Privacy Policy</a>
            <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ color:C.sub }}>Terms</a>
          </span>
        </div>}
        {isMobile && (
          <div style={{position:'fixed',bottom:0,left:0,right:0,background:C.sidebar,borderTop:`1px solid ${C.border}`,display:'flex',justifyContent:'space-around',alignItems:'center',padding:'6px 0 10px',zIndex:1000}}>
            {[
              {tab:'dashboard',icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="8" height="8"/><rect x="13" y="3" width="8" height="5"/><rect x="13" y="10" width="8" height="11"/><rect x="3" y="13" width="8" height="8"/></svg>,label:'Overview'},
              {tab:'trades',icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19h16"/><path d="M7 15l3-3 3 2 4-5"/></svg>,label:'Trades'},
              {tab:'add',icon:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>,label:'Add',isMain:true},
              {tab:'reports',icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 20V10"/><path d="M12 20V4"/><path d="M6 20v-6"/></svg>,label:'Reports'},
              {tab:'coach',icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="2"/><path d="M12 7v2"/><path d="M12 15v2"/><path d="M7 12h2"/><path d="M15 12h2"/></svg>,label:'AI'},
            ].map(({tab,icon,label,isMain})=>{
              const active = activeTab === tab;
              return (
                <button key={tab} onClick={()=>setActiveTab(tab)} style={{display:'flex',flexDirection:'column',alignItems:'center',gap:'2px',background:'none',border:'none',cursor:'pointer',color: isMain ? C.blue : active ? C.blue : C.sub,padding:'4px 10px',flex:1,minWidth:0}}>
                  {isMain ? (
                    <div style={{width:44,height:44,borderRadius:'50%',background:C.blue,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',marginTop:-16,boxShadow:'0 4px 16px rgba(47,140,255,0.4)'}}>
                      {icon}
                    </div>
                  ) : icon}
                  <span style={{fontSize:'10px',fontWeight:active||isMain?'700':'500'}}>{label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
      {!isMobile && <AIAssistant trades={accountTrades} />}
    </div>
  );
}
